import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getTableByIdBd, updateTableBd, insertTableBd } from '../../config/functions';
import { crearPlantillaCompletaDefault } from '../../config/functionsPlantillasDefault';
import { ModalCommentsOpt } from './ModalCommentsOpt';
import ModalVarSelectorOpt from './ModalVarSelectorOpt';
import ModalPlantillaOpt from './ModalPlantillaOpt';
import ModalCommentDetail from './ModalCommentDetail';
import ConfirmDialog from '../utils/ConfirmDialog';
import { GrillaIzqOpt } from './GrillaIzqOpt';
import RigTabs from './RigTabs';

import Highcharts from "highcharts/highstock";
import HighchartsReact from 'highcharts-react-official';
import NoDataToDisplay from "highcharts/modules/no-data-to-display";
import exporting from "highcharts/modules/exporting";
import offlineExporting from "highcharts/modules/offline-exporting";

import '../../css/liveGraphsOptimizedOpt.css';
import '../../css/modalTheme.css';

// 🎨 TEMA OSCURO - Colores actuales del sistema (perfectos)
const THEME_DARK = {
    bgColor: '#000000',
    fontColor: '#ffffff',
    chartBg: '#00000052',
    chartBorder: '#333333',
    chartFont: '#ffffff',
    gridColor: '#444444',
    gridMinorColor: '#2c2c2c',
    seriesColors: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
    ],
    // Colores específicos para medidas (mantener el azul claro brillante)
    measureValueColor: '#45B7D1'
};

// 🎨 TEMA CLARO - Colores para fondo claro
const THEME_LIGHT = {
    bgColor: '#ffffff',
    fontColor: '#2c2c2c',
    chartBg: '#ffffff',
    chartBorder: '#d0d0d0',
    chartFont: '#2c2c2c',
    gridColor: '#e0e0e0',
    gridMinorColor: '#f0f0f0',
    seriesColors: [
        '#E74C3C', '#16A085', '#2980B9', '#27AE60', '#F39C12',
        '#8E44AD', '#1ABC9C', '#D68910', '#6C3483', '#2471A3'
    ],
    // Colores específicos para medidas (más oscuros para legibilidad)
    measureValueColor: '#1a5490' // Azul oscuro más legible
};

const LiveGraphsOptimizedComponent = ({
    rig, setRig,
    mobile,
    variables,
    uid,
    user,
    horizontal,
    colors, tipoDispositivo,
    componente, setComponente,
    ventana,
    collapsed,
    broken,
    tipo,
    rigs, // 🗂️ Lista de rigs disponibles
    reportes,

    loading, setLoading,
    loadingMore, setLoadingMore,
    enVivo, setEnVivo,

    // 🗂️ Props para sistema de tabs (opcionales, vienen desde Menu.jsx)
    openTabs: propOpenTabs,
    setOpenTabs: propSetOpenTabs,
    activeTabId: propActiveTabId,
    setActiveTabId: propSetActiveTabId,
    showRigSelector: propShowRigSelector,
    setShowRigSelector: propSetShowRigSelector,

    // 🗄️ Props para sistema de caché (opcionales, vienen desde Menu.jsx)
    getCachedRigData,
    setCachedRigData,
    updateCachedRigData,
    clearCachedRigData
}) => {

    // 🔍 LOG DE RENDER DEL COMPONENTE
    /* console.log('🔴 LiveGraphsOptimized COMPONENT RENDER', new Date().toLocaleTimeString(), {
        rig, mobile, horizontal, loading, loadingMore, enVivo, componente, ventana, collapsed, broken
    }); */

    // 🕐 ESTADOS DEL SISTEMA DE NAVEGACIÓN TEMPORAL (Time Navigation System)
    const [viewMode, setViewMode] = useState('live'); // 'live' | 'exploring' | 'historical'
    const [rigTransmissionStartDate, setRigTransmissionStartDate] = useState(null); // Fecha inicio transmisión del rig
    const [historicalHours, setHistoricalHours] = useState(4); // Horas a cargar en modo histórico (4-24)
    const [isPollingPaused, setIsPollingPaused] = useState(false); // Flag para pausar polling
    const [showHistoricalModal, setShowHistoricalModal] = useState(false); // Modal de selección de fecha
    const [tempHistoricalDate, setTempHistoricalDate] = useState(null); // Fecha temporal en el modal
    const [historicalComments, setHistoricalComments] = useState([]); // Comentarios del rango completo
    const [commentSearchText, setCommentSearchText] = useState(''); // Texto de búsqueda en comentarios
    const [selectedHistoricalComment, setSelectedHistoricalComment] = useState(null); // Comentario seleccionado para centrar

    // 💾 BACKUP DE DATOS EN VIVO (para restaurar al volver de modo histórico)
    const liveDataBackupRef = useRef(null); // Backup de medidasGraficas en modo live
    const liveSeriesBackupRef = useRef(null); // Backup de finalSeries en modo live
    const liveOldestDateRef = useRef(null); // Backup de oldestDate en modo live
    const liveLatestDateRef = useRef(null); // Backup de latestDate en modo live
    const liveComentariosBackupRef = useRef(null); // Backup de comentarios en modo live

    // Estados principales
    const [varsDown, setVarsDown] = useState([]);
    const [medidasGraficas, setMedidasGraficas] = useState([]);
    const [datosInferiores, setDatosInferiores] = useState([]);

    // Estados para GrillaIzqOpt (manejados por el padre)
    const [variablesIzquierda, setVariablesIzquierda] = useState(Array(6).fill('0')); // IDs de variables
    const [datosIzquierda, setDatosIzquierda] = useState(Array(6).fill('')); // Datos completos de cada variable
    const [grillaIzqVisible, setGrillaIzqVisible] = useState(true); // Estado de visibilidad de grilla izquierda
    const [loadingMessage, setLoadingMessage] = useState('');
    const [dataLoaded, setDataLoaded] = useState(false); // 🔒 Flag para indicar si los datos ya fueron cargados
    const [seriesInfo, setSeriesInfo] = useState([]); // 📊 Información de las series para display
    const [finalSeries, setFinalSeries] = useState([]); // 📈 Series finales para HighchartsReact
    const [finalYAxis, setFinalYAxis] = useState([]); // 📐 Y-axes finales para HighchartsReact
    const [tooltipEnabled, setTooltipEnabled] = useState(false); // 💡 Activar/desactivar tooltip
    const [tooltipShared, setTooltipShared] = useState(true); // 💡 false = individual, true = compartido (cuando enabled=true)
    const [useFixedAxes, setUseFixedAxes] = useState(true); // 📏 Estado para usar ejes fijos (BD) o automáticos
    const [variablesMetadata, setVariablesMetadata] = useState(new Map()); // 🗃️ Metadata de variables (min, max, nombre, unidad)
    const [availableHours, setAvailableHours] = useState(4); // ? Horas de datos disponibles
    const [oldestDate, setOldestDate] = useState(null); // 📅 Fecha más antigua cargada
    const [latestDate, setLatestDate] = useState(null); // 📅 Fecha más reciente cargada (para comentarios)
    const [hoursToLoad, setHoursToLoad] = useState(4); // 🕐 Horas a cargar por defecto

    // 🔄 FASE 1-3: Estados para carga en segundo plano
    const [backgroundLoading, setBackgroundLoading] = useState(false); // 🔄 Indica si hay carga en background
    const [backgroundData, setBackgroundData] = useState(null); // 📦 Datos cargados en background (sin procesar)
    const [backgroundHoursToLoad, setBackgroundHoursToLoad] = useState(1); // 🕐 Horas a cargar en segundo plano
    const backgroundLoadedRef = useRef(false); // 🔑 Bandera para evitar cargas múltiples
    const [isFullscreen, setIsFullscreen] = useState(false); // 🖥️ Estado para detectar si el chart está en fullscreen

    // 🔑 CONVERTIDOS A REFS PARA EVITAR RE-RENDERS
    const canGoBackRef = useRef(false); // ⬅️ Puede navegar hacia atrás
    const canGoForwardRef = useRef(false); // ➡️ Puede navegar hacia adelante
    const canLoadMoreRef = useRef(true); // 📥 Puede cargar más datos (usuario está en el inicio)
    const isViewingLiveRef = useRef(true); // 👁️ Usuario viendo datos en vivo
    const [consecutiveErrors, setConsecutiveErrors] = useState(0); // ❌ Contador de errores consecutivos
    const rightClickTooltipRef = useRef(false); // 🖱️ Flag para controlar tooltip con botón derecho

    // 📱 Estados para vista móvil
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0); // Track actual visible en móvil vertical

    // 💬 Estados para comentarios
    const [showModalComments, setShowModalComments] = useState(false); // Modal de comentarios visible
    const [xComentario, setXComentario] = useState({}); // Datos del punto clickeado { x, y, varIdn }
    const [comentarios, setComentarios] = useState([]); // Lista de comentarios del período actual
    const [commentsData, setCommentsData] = useState([]); // 💬 Datos de comentarios para flags en track dedicado
    const [selectedComment, setSelectedComment] = useState(null); // 💬 Comentario seleccionado para ver detalle
    const [showCommentDetail, setShowCommentDetail] = useState(false); // 💬 Mostrar modal de detalle de comentario

    // 🔄 Estados para selector de variables
    const [showModalVarSelector, setShowModalVarSelector] = useState(false); // Modal selector visible
    const [selectedVarIndex, setSelectedVarIndex] = useState(null); // Índice de la variable a cambiar
    const [availableVariables, setAvailableVariables] = useState([]); // Variables disponibles para seleccionar
    const [currentVarMin, setCurrentVarMin] = useState(null); // Min actual de la variable seleccionada
    const [currentVarMax, setCurrentVarMax] = useState(null); // Max actual de la variable seleccionada
    const [modalInitialTab, setModalInitialTab] = useState('axes'); // Tab inicial del modal

    // 🚨 Estados para alarmas (integrados en ModalVarSelectorOpt)
    const [currentAlarmMin, setCurrentAlarmMin] = useState(null); // Valor mínimo de alarma
    const [currentAlarmMax, setCurrentAlarmMax] = useState(null); // Valor máximo de alarma
    const [currentAlarmId, setCurrentAlarmId] = useState(null); // ID de la alarma (null si no existe)

    // 📋 Estados para plantillas
    const [showModalPlantilla, setShowModalPlantilla] = useState(false); // Modal de plantillas visible
    const [isLoadingPlantilla, setIsLoadingPlantilla] = useState(false); // Loading de aplicación de plantilla
    const [internalLoading, setInternalLoading] = useState(false); // 🔑 Loading LOCAL (no depende del padre)
    const [showFooter, setShowFooter] = useState(false); // 🎯 Control de visibilidad del footer
    const [forceRenderKey, setForceRenderKey] = useState(0); // 🔄 Key para forzar re-render cuando sea necesario
    const [themeMode, setThemeMode] = useState(() => localStorage.getItem('theme') || 'dark'); // 🎨 Tema actual

    // 🎨 Estados para color picker
    const [showColorPicker, setShowColorPicker] = useState(false); // Mostrar color picker
    const [selectedSeriesIndex, setSelectedSeriesIndex] = useState(null); // Índice de serie seleccionada
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 }); // Posición del picker

    // 🗂️ SISTEMA DE TABS (solo desktop)
    // Si vienen props desde Menu.jsx, las usamos; sino, creamos estados locales (fallback)
    const [localOpenTabs, setLocalOpenTabs] = useState([]);
    const [localActiveTabId, setLocalActiveTabId] = useState(null);
    const [localShowRigSelector, setLocalShowRigSelector] = useState(false);

    // Usar props o estados locales según disponibilidad
    const finalOpenTabs = propOpenTabs !== undefined ? propOpenTabs : localOpenTabs;
    const finalSetOpenTabs = propSetOpenTabs || setLocalOpenTabs;
    const finalActiveTabId = propActiveTabId !== undefined ? propActiveTabId : localActiveTabId;
    const finalSetActiveTabId = propSetActiveTabId || setLocalActiveTabId;
    const finalShowRigSelector = propShowRigSelector !== undefined ? propShowRigSelector : localShowRigSelector;
    const finalSetShowRigSelector = propSetShowRigSelector || setLocalShowRigSelector;

    // 🎨 Selector de tema estable (sin Date.now() ni valores dinámicos)
    const theme = useMemo(() => {
        if (themeMode === 'light') {
            return THEME_LIGHT;
        } else if (themeMode === 'custom') {
            // 🔑 Leer colores DIRECTAMENTE desde localStorage para tema custom
            const storedColors = localStorage.getItem('colorsCustom');
            const customColors = storedColors ? JSON.parse(storedColors) : colors || {};

            return {
                bgColor: customColors.grColorBg || customColors.colorBg || '#000000',
                fontColor: customColors.grColorFont || customColors.colorFont || '#ffffff',
                chartBg: customColors.grColorBg || '#000000',
                chartBorder: customColors.grColorBorder || '#333333',
                gridColor: customColors.grColorBorder || '#444444',
                chartFont: customColors.grColorFont || '#ffffff',
                measureValueColor: customColors.grColorMeasure || '#45B7D1',
                alarmColor: customColors.grColorAlarm || '#ff6b6b',
                seriesColors: [
                    customColors.grColor1_1 || '#FF6B6B',
                    customColors.grColor1_2 || '#4ECDC4',
                    customColors.grColor2_1 || '#45B7D1',
                    customColors.grColor2_2 || '#96CEB4',
                    customColors.grColor3_1 || '#FFEAA7',
                    customColors.grColor3_2 || '#DDA0DD',
                    customColors.grColor4_1 || '#98D8C8',
                    customColors.grColor4_2 || '#F7DC6F',
                    customColors.grColor5_1 || '#BB8FCE',
                    customColors.grColor5_2 || '#85C1E9'
                ]
            };
        }
        // Por defecto siempre dark (incluye 'dark' y cualquier otro valor)
        return THEME_DARK;
    }, [themeMode, colors, forceRenderKey]);

    // Referencias para performance
    const intervalRef = useRef(null);
    const nbRef = useRef(null);
    const chartRef = useRef(null); // 📊 Referencia al chart de Highcharts
    const rigRef = useRef(rig); // 🔑 Ref para evitar stale closure
    const varsDownRef = useRef(varsDown); // 🔑 Ref para evitar stale closure
    const variablesIzquierdaRef = useRef(variablesIzquierda); // 🔑 Ref para evitar stale closure en grilla izquierda
    const finalSeriesRef = useRef(finalSeries); // 🔑 Ref para evitar stale closure
    const enVivoRef = useRef(enVivo); // 🔑 Ref para evitar re-renders innecesarios
    const latestDateRef = useRef(latestDate); // 🔑 Ref para evitar re-renders innecesarios
    const viewModeRef = useRef('live'); // 🔑 Ref para viewMode (evita stale closure en polling)
    const pendingTimeoutsRef = useRef([]); // 🔑 Ref para almacenar timeouts de addLivePointsGradually

    // ??? Referencias y estados para Page Visibility API (catch-up al volver a pesta�a)
    const lastVisibleTimeRef = useRef(Date.now());
    const isCatchingUpRef = useRef(false); // Flag para evitar polling durante catch-up

    // ?? Estado interno para detectar orientaci�n
    const [internalOrientation, setInternalOrientation] = useState({
        isMobile: mobile,
        isHorizontal: horizontal
    });

    // Sincronizar con props del padre cuando cambien
    useEffect(() => {
        setInternalOrientation({
            isMobile: mobile,
            isHorizontal: horizontal
        });
    }, [mobile, horizontal]);

    // 📱 Determinar modo de vista móvil (DEBE ESTAR ANTES DE getChartOptions)
    // Usar useMemo para que se recalculen cuando cambie internalOrientation
    const isMobileLandscape = useMemo(() => {
        const result = internalOrientation.isMobile && internalOrientation.isHorizontal;
        return result;
    }, [internalOrientation.isMobile, internalOrientation.isHorizontal]);

    const isMobilePortrait = useMemo(() => {
        const result = internalOrientation.isMobile && !internalOrientation.isHorizontal;
        return result;
    }, [internalOrientation.isMobile, internalOrientation.isHorizontal]);

    //--------------------------------------------------------------------------------

    // Configuración de tracks (5 columnas verticales + comentarios)
    // 🔑 Gaps de 2% entre tracks para separación visual clara
    // Track width: 16%, Gaps: 2% entre cada uno
    // Total: 2% inicial + (16% + 2%) * 5 tracks + 4% comentarios = 96%
    const TRACKS_CONFIG = [
        { id: 'track1', width: '18%', left: '0%', title: 'Track 1' },
        { id: 'track2', width: '18%', left: '20%', title: 'Track 2' },
        { id: 'track3', width: '18%', left: '40%', title: 'Track 3' },
        { id: 'track4', width: '18%', left: '60%', title: 'Track 4' },
        { id: 'track5', width: '18%', left: '80%', title: 'Track 5' },
        { id: 'track6', width: '4%', left: '95%', title: 'Comentarios', isCommentTrack: true }
    ];

    // 📊 Función para renderizar leyendas personalizadas por track
    const legendElementsRef = useRef([]); // Almacenar referencias a elementos de leyenda para cleanup
    const legendTextsRef = useRef([]); // Almacenar referencias a textos para actualización rápida
    const trackPositionsRef = useRef({}); // 🎯 Almacenar posiciones X reales de cada track desde yAxis

    const renderTrackLegends = useCallback((chart) => {
        // Limpiar leyendas anteriores
        legendElementsRef.current.forEach(element => {
            if (element && element.destroy) {
                element.destroy();
            }
        });
        legendElementsRef.current = [];
        legendTextsRef.current = []; // Limpiar referencias de textos también

        if (!chart || !chart.renderer) return;

        const renderer = chart.renderer;
        //const chartHeight = chart.chartHeight;
        const chartWidth = chart.chartWidth;

        // 📐 Calcular offset del navigator (está vertical a la izquierda en inverted chart)
        const navigatorHeight = chart.navigator && chart.navigator.height ? chart.navigator.height : 40;
        const navigatorOffset = navigatorHeight + 10; // Navigator + margin

        // Agrupar series por track (cada track tiene 2 variables)
        // Las series usan yAxis formato "yAxis-0", "yAxis-1", etc.
        // Necesitamos agrupar por trackIndex que está en custom
        const seriesByTrack = {};

        chart.series.forEach((serie, index) => {
            if (!serie.userOptions || !serie.userOptions.custom) return;

            const trackIndex = serie.userOptions.custom.trackIndex;
            if (trackIndex === undefined || trackIndex === null) return;

            // Ignorar track de comentarios (normalmente el último)
            if (trackIndex >= TRACKS_CONFIG.length - 1) return;

            if (!seriesByTrack[trackIndex]) {
                seriesByTrack[trackIndex] = [];
            }
            seriesByTrack[trackIndex].push(serie);
        });

        // Renderizar leyenda para cada track
        Object.keys(seriesByTrack).forEach(trackIndexStr => {
            const trackIndex = parseInt(trackIndexStr);
            const trackConfig = TRACKS_CONFIG[trackIndex];
            if (!trackConfig || trackConfig.isCommentTrack) return;

            const trackSeries = seriesByTrack[trackIndex];
            if (trackSeries.length === 0) return;

            // 🎯 OBTENER POSICIÓN REAL DEL YAXIS DEL TRACK
            // Buscar el primer yAxis que pertenece a este track
            const yAxisForTrack = chart.yAxis.find(axis => {
                const axisId = axis.userOptions.id;
                if (axisId && axisId.startsWith('yAxis-')) {
                    const seriesIndex = parseInt(axisId.split('-')[1]);
                    const seriesTrackIndex = Math.floor(seriesIndex / 2);
                    return seriesTrackIndex === trackIndex;
                }
                return false;
            });

            // Si encontramos el yAxis, usar su posición; sino, calcular manualmente
            let legendX, legendY;
            if (yAxisForTrack && yAxisForTrack.left !== undefined) {
                legendX = yAxisForTrack.left - 3;
                // Posicionar DEBAJO del área de ploteo (parte inferior de la gráfica)
                legendY = chart.plotTop - 30; /* + chart.plotHeight  + 35;*/ // Justo debajo del plot

                // 💾 GUARDAR POSICIÓN REAL DEL TRACK para usar en grilla inferior
                trackPositionsRef.current[trackIndex] = {
                    left: yAxisForTrack.left,
                    width: yAxisForTrack.width,
                    chartWidth: chartWidth
                };
            } else {
                const trackLeftPercent = parseFloat(trackConfig.left);
                const trackLeft = (trackLeftPercent / 100) * chartWidth;
                legendX = trackLeft + navigatorOffset + 5;
                legendY = chart.plotTop + chart.plotHeight + 35;
            }

            // Crear grupo para la leyenda del track
            const legendGroup = renderer.g('track-legend-' + trackIndex)
                .attr({
                    zIndex: 10
                })
                .add();

            let currentY = legendY;

            // Renderizar cada serie (máximo 2 por track)
            trackSeries.slice(0, 2).forEach((serie, serieIndex) => {
                const custom = serie.userOptions.custom || {};
                const color = serie.color || '#ffffff';
                const isVisible = serie.visible !== false;

                // 📊 OBTENER ÚLTIMO VALOR DIRECTAMENTE DE LA SERIE DE HIGHCHARTS
                let legendName = custom.legendName || serie.name;

                // Actualizar con el último valor si hay datos
                if (serie.data && serie.data.length > 0) {
                    const lastPoint = serie.data[serie.data.length - 1];
                    // Validar que el punto no sea null antes de acceder a 'y'
                    if (lastPoint && lastPoint.y !== undefined && lastPoint.y !== null) {
                        const lastValue = lastPoint.y;
                        const descriptiveName = custom.descriptiveName || serie.name;
                        const unit = custom.unit || '';
                        legendName = `${descriptiveName}: ${lastValue.toFixed(1)} ${unit}`.trim();
                    }
                }

                // Línea de color (indicador) - Reducida a la mitad
                const line = renderer.path(['M', legendX, currentY + 5, 'L', legendX + 7, currentY + 5])
                    .attr({
                        'stroke': color,
                        'stroke-width': 2,
                        'opacity': isVisible ? 1 : 0.3
                    })
                    .add(legendGroup);

                // Texto de la leyenda
                const text = renderer.text(legendName, legendX + 10, currentY + 10)
                    .css({
                        color: theme.chartFont,
                        fontSize: '8px',
                        fontWeight: 'normal',
                        cursor: 'pointer',
                        opacity: isVisible ? 1 : 0.3
                    })
                    .add(legendGroup);

                // 💾 Guardar referencia al texto para actualización rápida
                const textRefKey = `track${trackIndex}-serie${serieIndex}`;
                legendTextsRef.current[textRefKey] = text;

                // 🖱️ Agregar interactividad al click
                const legendBox = renderer.rect(legendX, currentY, 150, 16)
                    .attr({
                        fill: 'transparent',
                        'stroke': 'transparent',
                        'stroke-width': 0,
                        zIndex: 11
                    })
                    .css({
                        cursor: 'pointer'
                    })
                    .on('click', function () {
                        // Toggle visibilidad de la serie
                        const newVisibility = !serie.visible;
                        serie.setVisible(newVisibility, true);

                        // Actualizar leyendas después del click (solo mobile landscape)
                        if (isMobileLandscape) {
                            setTimeout(() => renderTrackLegends(chart), 50);
                        }
                    })
                    .add(legendGroup);

                // Almacenar referencias
                legendElementsRef.current.push(line, text, legendBox);

                // Incrementar Y para la siguiente variable (apiladas verticalmente)
                currentY += 18; // Espaciado vertical entre variables
            });

            legendElementsRef.current.push(legendGroup);
        });
    }, [theme.chartFont]);

    // ?? CONFIGURACIÓN OPTIMIZADA DE HIGHCHARTS
    // ?? GENERAR BOTONES DINÁMICOS DEL RANGE SELECTOR SEGÚN DATOS DISPONIBLES
    const getRangeSelectorButtons = () => {
        const buttons = [];

        // Siempre agregar botones de minutos
        buttons.push(
            {
                type: 'minute',
                count: 5,
                text: '5m',
                events: {
                    click: function () {
                        return true;
                    }
                }
            },
            {
                type: 'minute',
                count: 15,
                text: '15m',
                events: {
                    click: function () {
                        return true;
                    }
                }
            },
            {
                type: 'minute',
                count: 30,
                text: '30m',
                events: {
                    click: function () {
                        return true;
                    }
                }
            },
            {
                type: 'hour',
                count: 1,
                text: '1h',
                events: {
                    click: function () {
                        return true;
                    }
                }
            }
        );

        // Agregar botones de horas según disponibilidad
        if (availableHours >= 2) {
            buttons.push({
                type: 'hour',
                count: 2,
                text: '2h',
                events: {
                    click: function () {
                        //
                        return true;
                    }
                }
            });
        }

        if (availableHours >= 4) {
            buttons.push({
                type: 'hour',
                count: 4,
                text: '4h',
                events: {
                    click: function () {
                        //
                        return true;
                    }
                }
            });
        }

        if (availableHours >= 8) {
            buttons.push({
                type: 'hour',
                count: 8,
                text: '8h',
                events: {
                    click: function () {
                        //
                        return true;
                    }
                }
            });
        }

        if (availableHours >= 12) {
            buttons.push({
                type: 'hour',
                count: 12,
                text: '12h',
                events: {
                    click: function () {
                        //
                        return true;
                    }
                }
            });
        }

        // Siempre agregar el botón "All"
        buttons.push({
            type: 'all',
            text: 'All',
            events: {
                click: function () {
                    return true;
                }
            }
        });

        return buttons;
    };

    // ? CREAR OPCIONES DINÁMICAS DEL CHART BASADAS EN ESTADOS
    const getChartOptions = useCallback(() => {

        const options = {
            chart: {
                type: 'line',
                inverted: true, // Eje Y hacia abajo como en rigsenses
                height: null, // ? Altura dinámica (se controla por CSS)
                backgroundColor: theme.chartBg,
                animation: true, // Desactivar animaciones para mejor performance
                reflow: true, // Permitir que el gráfico recalcule su altura
                //marginLeft: isMobilePortrait ? 80 : (isMobileLandscape ? 80 : 100),
                //marginRight: isMobilePortrait ? 40 : (isMobileLandscape ? 50 : 50),
                marginTop: isMobilePortrait ? 30 : (isMobileLandscape ? 20 : 50), // Mantener espacio para range selector
                marginBottom: isMobilePortrait ? 40 : (isMobileLandscape ? 50 : 40), // Mínimo margen para que se vean los ejes
                //spacingTop: isMobilePortrait ? 2 : (isMobileLandscape ? 3 : 5),
                spacingBottom: isMobileLandscape && 40, // Sin espacio adicional
                zoomType: 'x',
                panning: {
                    enabled: true,
                    type: 'y',
                    /* opposite: true */
                },
                panKey: !mobile ? 'alt' : 'undefined', // En móvil no hay panKey

                events: {
                    load: function () {
                        const chart = this;
                        // ?? Inicializar botones de navegación cuando el chart se carga
                        setTimeout(() => {
                            updateCanGo();
                        }, 100);

                        // 📊 RENDERIZAR LEYENDAS PERSONALIZADAS POR TRACK (mobile landscape o fullscreen)
                        if (isMobileLandscape || isFullscreen) {
                            renderTrackLegends(chart);
                        }

                        // 🖥️ DETECTAR CAMBIOS DE FULLSCREEN
                        const fullscreenChangeHandler = () => {
                            const isNowFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
                            setIsFullscreen(isNowFullscreen);

                            // Renderizar o limpiar leyendas según estado de fullscreen
                            setTimeout(() => {
                                if (isNowFullscreen || isMobileLandscape) {
                                    renderTrackLegends(chart);
                                } else if (!isMobileLandscape) {
                                    legendElementsRef.current.forEach(element => {
                                        if (element && element.destroy) {
                                            element.destroy();
                                        }
                                    });
                                    legendElementsRef.current = [];
                                    legendTextsRef.current = [];
                                }
                            }, 100);
                        };

                        document.addEventListener('fullscreenchange', fullscreenChangeHandler);
                        document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
                        document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
                        document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);

                        // 🖱️ EVENTO DE BOTÓN DERECHO PARA TOOLTIP INDIVIDUAL (solo desktop)
                        if (!isMobilePortrait && !isMobileLandscape) {
                            const container = chart.container;

                            // Prevenir menú contextual del navegador
                            container.addEventListener('contextmenu', function (e) {
                                e.preventDefault();
                                return false;
                            });

                            // Función simplificada: dejar que Highcharts maneje todo
                            const handleMouseMoveRightClick = function (e) {
                                if (rightClickTooltipRef.current) {
                                    // Highcharts maneja automáticamente la detección del punto correcto
                                    chart.pointer.onContainerMouseMove(e);
                                }
                            };

                            // Activar tooltip individual al presionar botón derecho
                            container.addEventListener('mousedown', function (e) {
                                if (e.button === 2) { // Botón derecho
                                    e.preventDefault();
                                    e.stopPropagation();
                                    rightClickTooltipRef.current = true;

                                    // FORZAR activación temporal del tooltip en modo INDIVIDUAL
                                    chart.tooltip.update({
                                        enabled: true,
                                        shared: false
                                    });

                                    // Agregar listener de mousemove
                                    container.addEventListener('mousemove', handleMouseMoveRightClick);

                                    // Forzar actualización inicial del tooltip
                                    chart.pointer.onContainerMouseMove(e);
                                }
                            });

                            // Desactivar tooltip individual al soltar botón derecho
                            container.addEventListener('mouseup', function (e) {
                                if (e.button === 2) { // Botón derecho
                                    e.preventDefault();
                                    e.stopPropagation();
                                    rightClickTooltipRef.current = false;

                                    // Remover listener de mousemove
                                    container.removeEventListener('mousemove', handleMouseMoveRightClick);

                                    // FORZAR cierre del tooltip inmediatamente
                                    if (chart.tooltip) {
                                        chart.tooltip.hide(0); // 0 = sin delay

                                        // Restaurar estado original con update para asegurar el cambio
                                        setTimeout(() => {
                                            chart.tooltip.update({
                                                enabled: tooltipEnabled,
                                                shared: tooltipShared
                                            });
                                        }, 10);
                                    }
                                }
                            });

                            // También desactivar si el mouse sale del contenedor
                            container.addEventListener('mouseleave', function () {
                                if (rightClickTooltipRef.current) {
                                    rightClickTooltipRef.current = false;

                                    // Remover listener de mousemove
                                    container.removeEventListener('mousemove', handleMouseMoveRightClick);

                                    if (chart.tooltip) {
                                        chart.tooltip.hide(0); // 0 = sin delay

                                        // Restaurar estado original con update
                                        setTimeout(() => {
                                            chart.tooltip.update({
                                                enabled: tooltipEnabled,
                                                shared: tooltipShared
                                            });
                                        }, 10);
                                    }
                                }
                            });
                        }
                    },
                    redraw: function () {
                        // 🔄 ACTUALIZAR LEYENDAS AL REDIMENSIONAR O HACER ZOOM (mobile landscape o fullscreen)
                        const chart = this;
                        if (isMobileLandscape || isFullscreen) {
                            renderTrackLegends(chart);
                        }
                    },
                    beforePrint: function () {
                        // 🖨️ RENDERIZAR LEYENDAS ANTES DE IMPRIMIR/EXPORTAR
                        const chart = this;
                        renderTrackLegends(chart);
                    },
                    afterPrint: function () {
                        // 🧹 LIMPIAR LEYENDAS DESPUÉS DE IMPRIMIR (solo si no es mobile landscape ni fullscreen)
                        const chart = this;
                        if (!isMobileLandscape && !isFullscreen) {
                            legendElementsRef.current.forEach(element => {
                                if (element && element.destroy) {
                                    element.destroy();
                                }
                            });
                            legendElementsRef.current = [];
                            legendTextsRef.current = [];
                        }
                    }
                }
            },

            legend: {
                enabled: false // Desactivar leyenda global, usaremos leyendas por track
            },

            // ?? EJES Y DINÁMICOS BASADOS EN EL ESTADO
            // En móvil vertical: 2 ejes Y ocupando EL MISMO ESPACIO completo (como desktop pero con un solo track visible)
            yAxis:
                isMobilePortrait
                    ? (() => {
                        // Obtener las 2 series del track actual para aplicar sus configuraciones y colores
                        const trackSeries = seriesInfo.filter(s => s.trackIndex === currentTrackIndex);
                        const serie1 = trackSeries[0]; // Primera variable (izquierda)
                        const serie2 = trackSeries[1]; // Segunda variable (derecha)

                        // Índices reales de las variables en finalYAxis
                        const varIndex1 = currentTrackIndex * 2;
                        const varIndex2 = currentTrackIndex * 2 + 1;

                        // Obtener configuraciones de ejes desde finalYAxis si existen
                        const axis1Config = finalYAxis[varIndex1] || {};
                        const axis2Config = finalYAxis[varIndex2] || {};

                        return [
                            // Eje izquierdo - Ocupa TODO el ancho del gráfico
                            {
                                id: 'portrait-track-left',
                                width: '100%',
                                left: '0%',
                                top: '0%',
                                height: '100%',
                                offset: 0, // Pegado al borde del track
                                gridLineColor: theme.gridColor,
                                gridLineWidth: 1,
                                lineColor: serie1?.color || '#888888',
                                lineWidth: 2,
                                tickColor: serie1?.color || '#888888',
                                tickWidth: 1,
                                opposite: false, // Labels a la izquierda
                                reversed: false, // De abajo hacia arriba (normal)
                                startOnTick: true,
                                endOnTick: true,
                                tickAmount: 5, // 5 ticks = 4 líneas de grid
                                min: axis1Config.min !== undefined ? axis1Config.min : undefined,
                                max: axis1Config.max !== undefined ? axis1Config.max : undefined,
                                tickPositions: (useFixedAxes && axis1Config.min !== undefined && axis1Config.max !== undefined)
                                    ? (() => {
                                        const min = axis1Config.min;
                                        const max = axis1Config.max;
                                        const count = 5;
                                        const step = (max - min) / (count - 1);
                                        return Array.from({ length: count }, (_, i) => min + (step * i));
                                    })()
                                    : undefined,
                                minPadding: (useFixedAxes && axis1Config.min !== undefined && axis1Config.max !== undefined) ? 0 : 0.05,
                                maxPadding: (useFixedAxes && axis1Config.min !== undefined && axis1Config.max !== undefined) ? 0 : 0.05,

                                labels: {
                                    enabled: true,
                                    style: { color: serie1?.color || theme.chartFont, fontSize: '10px' },
                                    align: 'left',
                                    x: 5,
                                    y: 10
                                }
                            },
                            // Eje derecho - Ocupa TODO el ancho del gráfico (SUPERPUESTO con el izquierdo)
                            {
                                id: 'portrait-track-right',
                                width: '100%',
                                left: '0%',
                                top: '0%',
                                height: '100%',
                                offset: 15, // Pegado al borde del track
                                gridLineColor: 'transparent', // Sin líneas de grid para evitar duplicados
                                gridLineWidth: 0,
                                lineColor: serie2?.color || '#888888',
                                lineWidth: 2,
                                tickColor: serie2?.color || '#888888',
                                tickWidth: 1,
                                opposite: false, // Labels a la derecha
                                reversed: false, // Normal: de abajo hacia arriba (AMBOS EJES ABAJO)
                                startOnTick: true,
                                endOnTick: true,
                                tickAmount: 5, // 5 ticks = 4 líneas de grid (sin grid visible pero mantiene consistencia)
                                min: axis2Config.min !== undefined ? axis2Config.min : undefined,
                                max: axis2Config.max !== undefined ? axis2Config.max : undefined,
                                tickPositions: (useFixedAxes && axis2Config.min !== undefined && axis2Config.max !== undefined)
                                    ? (() => {
                                        const min = axis2Config.min;
                                        const max = axis2Config.max;
                                        const count = 5;
                                        const step = (max - min) / (count - 1);
                                        return Array.from({ length: count }, (_, i) => min + (step * i));
                                    })()
                                    : undefined,
                                minPadding: (useFixedAxes && axis2Config.min !== undefined && axis2Config.max !== undefined) ? 0 : 0.05,
                                maxPadding: (useFixedAxes && axis2Config.min !== undefined && axis2Config.max !== undefined) ? 0 : 0.05,

                                labels: {
                                    enabled: true,
                                    style: { color: serie2?.color || '#ffffff', fontSize: '10px' },
                                    align: 'left',
                                    x: 5,
                                    y: 10
                                }
                            },
                            // 💬 Eje dedicado para COMENTARIOS (invisible, independiente, siempre presente)
                            {
                                id: 'portrait-comments-track',
                                width: '100%',
                                left: '0%',
                                top: '0%',
                                height: '100%',
                                offset: 0,
                                // 🔒 Configuración invisible e independiente
                                visible: false,
                                labels: { enabled: false },
                                title: { text: null },
                                gridLineWidth: 0,
                                lineWidth: 0,
                                tickLength: 0,
                                tickWidth: 0,
                                minorTickLength: 0,
                                min: 0,
                                max: 1,
                                startOnTick: false,
                                endOnTick: false,
                                opposite: false,
                                reversed: false,
                                type: 'linear',
                                allowDecimals: false
                            }
                        ];
                    })()
                    : (finalYAxis.length > 0 ? finalYAxis : TRACKS_CONFIG.map((track, index) => {
                        // 🎨 TRACK ESPECIAL PARA COMENTARIOS - COMPLETAMENTE INDEPENDIENTE
                        if (track.isCommentTrack) {
                            return {
                                id: track.id,
                                width: '3%',
                                left: '94%',
                                top: '0%',
                                height: '100%',
                                offset: 0,
                                // ? CONFIGURACIÓN ULTRA-ESTRICTA PARA INDEPENDENCIA TOTAL
                                visible: false, // Ocultar eje visual completamente
                                labels: { enabled: false },
                                title: { text: null },
                                gridLineWidth: 0,
                                lineWidth: 0,
                                tickLength: 0,
                                tickWidth: 0,
                                minorTickLength: 0,
                                min: 0,
                                max: 1,
                                startOnTick: false,
                                endOnTick: false,
                                // ? EVITAR CUALQUIER INTERFERENCIA CON OTROS TRACKS
                                opposite: false,
                                reversed: false,
                                type: 'linear',
                                allowDecimals: false
                            };
                        }

                        // 🔍 Obtener configuración del eje desde finalYAxis si existe
                        const axisFromFinalYAxis = finalYAxis[index];

                        let axisConfig = {
                            id: track.id,
                            width: '16%',
                            left: `${TRACKS_CONFIG[index].left}`,
                            top: '0%', // Sin margen superior
                            height: '100%', // Ocupar todo el alto disponible
                            offset: 0, // Pegado al borde del track

                            // Estilo visual
                            gridLineColor: theme.gridColor,
                            gridLineWidth: 1,
                            lineColor: '#888888',
                            lineWidth: 1,
                            tickColor: '#888888',
                            opposite: false,

                            min: axisFromFinalYAxis?.min !== undefined ? axisFromFinalYAxis.min : undefined,
                            max: axisFromFinalYAxis?.max !== undefined ? axisFromFinalYAxis.max : undefined,

                            softMin: undefined,
                            softMax: undefined,

                            minPadding: 0,
                            maxPadding: 0,
                            startOnTick: axisFromFinalYAxis?.startOnTick !== undefined ? axisFromFinalYAxis.startOnTick : true,
                            endOnTick: axisFromFinalYAxis?.endOnTick !== undefined ? axisFromFinalYAxis.endOnTick : true
                        };

                        return axisConfig;
                    })),

            // X-Axis principal (tiempo compartido - vertical)
            xAxis: {
                type: 'datetime',
                // 🎯 Establecer rango inicial de 1 hora desde el último dato
                min: (() => {
                    // Calcular el máximo timestamp de todas las series
                    let maxTime = -Infinity;
                    finalSeries.forEach(serie => {
                        if (serie.data && serie.data.length > 0) {
                            const serieMax = Math.max(...serie.data.map(point => point[0]));
                            if (serieMax > maxTime) maxTime = serieMax;
                        }
                    });
                    // Retornar maxTime - 1 hora (en milisegundos)
                    return maxTime !== -Infinity ? maxTime - (1 * 60 * 60 * 1000) : undefined;
                })(),
                max: (() => {
                    // Calcular el máximo timestamp de todas las series
                    let maxTime = -Infinity;
                    finalSeries.forEach(serie => {
                        if (serie.data && serie.data.length > 0) {
                            const serieMax = Math.max(...serie.data.map(point => point[0]));
                            if (serieMax > maxTime) maxTime = serieMax;
                        }
                    });
                    return maxTime !== -Infinity ? maxTime : undefined;
                })(),
                labels: {
                    /* enabled: !isMobilePortrait, */
                    style: { color: theme.chartFont, fontSize: '10px' },
                    formatter: function () {
                        const value = this.value;
                        const currentDate = new Date(value);

                        // Obtener componentes de tiempo
                        const hours = currentDate.getUTCHours();
                        const minutes = currentDate.getUTCMinutes();
                        const seconds = currentDate.getUTCSeconds();

                        // Si es exactamente medianoche (00:00:00), mostrar fecha
                        if (hours === 0 && minutes === 0 && seconds === 0) {
                            return Highcharts.dateFormat('%d/%m/%Y', value);
                        }

                        // De lo contrario, solo mostrar hora
                        return Highcharts.dateFormat('%H:%M:%S', value);
                    }
                },
                // ?? Líneas de cuadrícula sutiles para identificar tiempo en todos los tracks
                gridLineWidth: 1,
                gridLineColor: '#414040ff', // Líneas sutiles (gris muy oscuro)
                gridLineDashStyle: 'Dot', // Líneas punteadas para que sean más sutiles
                minorGridLineWidth: 1,
                minorGridLineColor: '#2c2c2cff', // Líneas menores aún más sutiles
                minorGridLineDashStyle: 'Dot',
                minorTickInterval: 'auto', // Agregar ticks menores automáticamente
                lineColor: '#666666',
                tickColor: '#666666',
                crosshair: true,
                events: {
                    // ?? Detectar cuando cambian los extremos del eje (range selector, zoom, navegación)
                    afterSetExtremes: function (e) {
                        // ?? Actualizar estado de botones de navegación cada vez que cambian los extremos
                        // Usar setTimeout para asegurar que el chart está completamente actualizado
                        setTimeout(() => {
                            updateCanGo();
                        }, 50);
                    }
                }
            },

            // Navigator integrado
            navigator: {
                enabled: true, // Habilitado en todas las vistas
                height: isMobilePortrait ? 40 : 40, // Altura del navigator (se rotará en móvil)
                margin: isMobilePortrait ? 5 : 10,
                outlineWidth: 1,
                outlineColor: '#4ECDC4',
                maskFill: 'rgba(78, 205, 196, 0.15)',
                maskInside: true, // Resaltar el área seleccionada
                adaptToUpdatedData: true, // Adaptar cuando cambian los datos
                handles: {
                    backgroundColor: '#4ECDC4',
                    borderColor: '#ffffff',
                    width: isMobilePortrait ? 8 : 10,
                    height: isMobilePortrait ? 20 : 16
                },
                xAxis: {
                    labels: {
                        enabled: !isMobilePortrait, // Solo labels en desktop/landscape
                        style: { color: '#ffffff', fontSize: (isMobileLandscape || isMobilePortrait) ? '6px' : '8px' }
                    },
                    gridLineWidth: 0
                },
                series: {
                    type: 'line',
                    lineWidth: 1,
                    lineColor: '#4ECDC4',
                    color: '#4ECDC4',
                    fillOpacity: 0.1
                }
            },

            // Scrollbar
            scrollbar: {
                enabled: true,
                barBackgroundColor: '#666666',
                barBorderColor: '#333333',
                buttonBackgroundColor: '#888888',
                trackBackgroundColor: '#333333'
            },

            // Range selector
            rangeSelector: {
                enabled: true,
                inputEnabled: true, // Mantener habilitado para que se visualice
                inputDateFormat: '%d-%m-%Y %H:%M:%S',
                inputEditDateFormat: '%d-%m-%Y %H:%M:%S',
                y: isMobilePortrait ? -30 : (isMobileLandscape ? 0 : -50),
                x: isMobilePortrait ? -105 : 0,
                //height: isMobilePortrait ? 25 : 30,
                buttonSpacing: mobile ? 1 : 5,
                buttonMargin: (isMobilePortrait) ? 1 : 5,

                inputPosition: {
                    //align: isMobilePortrait ? 'center' : '',
                    x: (isMobilePortrait) ? 30 : (isMobileLandscape ? 0 : 0),
                    y: isMobileLandscape ? -10 : 0
                },

                buttonPosition: {
                    //align: isMobilePortrait ? 'center' : '',
                    x: (isMobilePortrait) ? -20 : 0,
                    y: isMobileLandscape ? -10 : 0
                },
                buttonTheme: {
                    fill: themeMode === 'light' ? '#e8e8e8' : '#666666',
                    stroke: themeMode === 'light' ? '#cccccc' : '#333333',
                    style: {
                        color: theme.fontColor,
                        fontSize: (mobile ? '10px' : '13px')
                    },
                    width: 30, // Botones pequeños en móviles
                    height: 18,
                    padding: 2,
                    r: 3 // Radio de borde
                },
                inputStyle: {
                    color: theme.fontColor,
                    backgroundColor: theme.cardBg,
                    border: `1px solid ${theme.gridColor}`,
                    fontSize: (isMobileLandscape || isMobilePortrait) ? '10px' : '12px',
                },
                inputBoxBorderColor: theme.gridColor,
                labelStyle: {
                    color: theme.fontColor,
                    fontSize: mobile ? '9px' : '11px'
                },
                buttons: getRangeSelectorButtons(),

                selected: 3, // Seleccionar por defecto el cuarto botón (usualmente "1h" o "All")
                allButtonsEnabled: true,
                dropdown: false // 🔑 ag
            },

            // ?? SERIES DINAMICAS BASADAS EN EL ESTADO
            series: finalSeries.length > 0 ? [
                // ? SOLO SERIES DE LÍNEAS PRINCIPALES (SIN FLAGS)
                ...finalSeries.map((serie, idx) => {
                    // 🔍 VALIDAR QUE EL YAXIS DE LA SERIE EXISTE
                    const serieYAxisId = serie.yAxis;
                    const yAxisExists = isMobilePortrait
                        ? true // En portrait usamos ejes fijos que siempre existen
                        : finalYAxis.some(axis => axis.id === serieYAxisId);

                    if (!yAxisExists) {
                        console.warn(`Serie ${serie.name} intenta usar yAxis ${serieYAxisId} que no existe. Omitiendo serie.`);
                        return null;
                    }

                    if (isMobilePortrait) {
                        // Solo mostrar series del track actual
                        if (serie.trackIndex !== currentTrackIndex) {
                            return null;
                        }
                        // Reasignar al track virtual fijo
                        // En cada track hay 2 variables: par (0,2,4...) = izquierda, impar (1,3,5...) = derecha
                        // Extraer el índice del yAxis string (ej: "yAxis-0" -> 0)
                        const yAxisIndex = typeof serie.yAxis === 'string'
                            ? parseInt(serie.yAxis.split('-')[1])
                            : (typeof serie.yAxis === 'number' ? serie.yAxis : idx);
                        const isLeftAxis = yAxisIndex % 2 === 0;

                        return {
                            ...serie,
                            yAxis: isLeftAxis
                                ? 'portrait-track-left'
                                : 'portrait-track-right',
                            type: 'line',
                            visible: true,
                            animation: true,
                            enableMouseTracking: tooltipEnabled, // CRÍTICO: OFF cuando panning activo
                            stickyTracking: false, // Evitar captura de eventos
                            turboThreshold: 5000000,
                            states: {
                                hover: {
                                    enabled: false, // Desactivado para evitar interferencia con panning
                                    //marker: { enabled: false }
                                },
                                inactive: {
                                    enabled: false
                                }
                            }
                        };
                    }
                    // Desktop/landscape normal
                    return {
                        ...serie,
                        type: 'line',
                        animation: false,
                        enableMouseTracking: true,
                        stickyTracking: false,
                        visible: true,
                        turboThreshold: 5000000,
                        states: {
                            hover: {
                                marker: { enabled: false } // NUNCA markers
                            }
                        }
                    };
                }).filter(s => s !== null),

                // ?? SERIE DE COMENTARIOS (SCATTER) - PUNTOS SUPERPUESTOS CON LAS GRÁFICAS
                ...(commentsData && commentsData.length > 0 ? [{
                    type: 'scatter',
                    name: 'Comentarios',
                    id: 'commentsPoints',
                    data: commentsData.map(comment => {
                        const timestamp = new Date(comment.fecha).getTime();
                        // Validar timestamp para evitar "Invalid time value"
                        if (isNaN(timestamp)) return null;

                        // 🎯 Posición Y fija para comentarios en todos los modos
                        // Mobile portrait: usa eje dedicado invisible (0-1)
                        // Desktop/landscape: usa track dedicado de comentarios (0-1)
                        const yValue = 0.75; // Posición fija en 75% del eje (visible en todos los tracks)

                        // 📌 Obtener nombre de variable desde metadata, datosInferiores o variables prop
                        const varId = comment.variable;
                        let varName = `Variable ${varId}`;

                        // Intentar obtener desde variablesMetadata primero
                        const varMetadata = variablesMetadata.get(parseInt(varId));
                        if (varMetadata?.name) {
                            varName = varMetadata.name;
                        } else {
                            // Si no está en metadata, buscar en datosInferiores
                            const varInfo = datosInferiores.find(v => v.nb_var_idn == varId);
                            if (varInfo?.vr_var_nom) {
                                varName = varInfo.vr_var_nom;
                            } else {
                                // Si no está en datosInferiores, buscar en variables prop (TODAS las variables)
                                const varFromProp = variables?.find(v => v.nb_var_idn == varId);
                                if (varFromProp?.vr_var_nom) {
                                    varName = varFromProp.vr_var_nom;
                                }
                            }
                        }

                        // Formatear fecha para tooltip (mantener hora UTC sin conversión)
                        const fecha = new Date(comment.fecha);
                        const fechaFormateada = fecha.toISOString().replace('T', ' ').substring(0, 19);

                        return {
                            x: timestamp,
                            y: yValue,
                            nb_com_idn: comment.nb_com_idn,
                            comentario: comment.comentario,
                            usuario: comment.usuario,
                            fecha: fechaFormateada,
                            varId: varId,
                            varName: varName
                        };
                    }).filter(point => point !== null),
                    // 🎯 Usar eje dedicado para comentarios en TODAS las vistas
                    // Mobile portrait: 'portrait-comments-track' (invisible, siempre presente)
                    // Desktop/landscape: 'commentTrack' (track dedicado visible en desktop)
                    yAxis: isMobilePortrait ? 'portrait-comments-track' : 'commentTrack',
                    lineWidth: 0, // ? SIN L�NEA conectando los puntos
                    marker: {
                        symbol: 'circle',
                        radius: isMobilePortrait ? 5 : 6, // Más pequeño en móvil
                        fillColor: '#FF6B35',
                        lineWidth: 2,
                        lineColor: '#FFF'
                    },
                    showInLegend: false,
                    enableMouseTracking: true, // ?? SIEMPRE ACTIVO para comentarios
                    stickyTracking: false, // ?? Permite tooltip individual
                    cursor: 'pointer', // 👆 Cursor de mano para indicar que es clickeable
                    point: {
                        events: {
                            click: function (event) {
                                // Evitar que se propague al evento click del chart
                                if (event && event.stopPropagation) {
                                    event.stopPropagation();
                                }
                                // 💬 Abrir modal con detalle del comentario
                                setSelectedComment({
                                    nb_com_idn: this.nb_com_idn,
                                    varName: this.varName,
                                    comentario: this.comentario,
                                    usuario: this.usuario,
                                    fecha: this.fecha
                                });
                                setShowCommentDetail(true);
                                return false; // Detener propagación
                            }
                        }
                    },
                    dataLabels: {
                        enabled: true, // ? LABELS SIEMPRE VISIBLES
                        useHTML: true, // ? Usar HTML para formateo multi-l�nea
                        formatter: function () {
                            const maxLength = isMobilePortrait ? 15 : 30; // Más compacto en portrait
                            const comentario = this.point.comentario || 'Sin comentario';
                            const varName = this.point.varName || 'Variable';
                            const comentarioTruncado = comentario.length > maxLength
                                ? comentario.substring(0, maxLength) + '...'
                                : comentario;

                            // ? HTML con variable y comentario - más compacto en portrait
                            return `<div style="text-align: left;">
                                <div style="font-size: ${isMobilePortrait ? '8px' : '9px'}; font-weight: bold; color: #FF8C69; margin-bottom: 2px;">${varName}</div>
                                <div style="font-size: ${isMobilePortrait ? '9px' : '10px'}; font-weight: bold; color: #FF6B35;">${comentarioTruncado}</div>
                            </div>`;
                        },
                        style: {
                            textOutline: 'none'
                        },
                        align: 'left',
                        verticalAlign: 'middle',
                        x: 10,
                        y: 0,
                        overflow: 'allow',
                        crop: false
                    },
                    // ?? TOOLTIP INDIVIDUAL Y SIEMPRE ACTIVO para comentarios
                    tooltip: {
                        enabled: true, // ?? SIEMPRE ACTIVO independiente del tooltip global
                        useHTML: true,
                        shared: false,
                        followPointer: false,
                        headerFormat: '',
                        pointFormatter: function () {
                            const comentario = this.comentario || 'Sin comentario';
                            const usuario = this.usuario || 'Usuario';
                            const fecha = this.fecha || '';
                            const varName = this.varName || 'Variable';

                            return `<div style="padding: 10px; max-width: 350px; background: white; color: #333;">
                                <div style="font-size: 13px; font-weight: bold; color: #FF6B35; margin-bottom: 8px; border-bottom: 2px solid #FF6B35; padding-bottom: 6px;">
                                    💬 ${varName}
                                </div>
                                <div style="font-size: 11px; color: #333; margin-bottom: 10px; line-height: 1.6; white-space: normal; word-wrap: break-word; word-break: break-word;">
                                    ${comentario}
                                </div>
                                <div style="border-top: 1px solid #ddd; padding-top: 6px; margin-top: 6px;">
                                    <div style="font-size: 9px; color: #666; margin-bottom: 3px;">
                                        <strong>Usuario:</strong> ${usuario}
                                    </div>
                                    <div style="font-size: 9px; color: #666;">
                                        <strong>Fecha:</strong> ${fecha}
                                    </div>
                                </div>
                            </div>`;
                        },
                        backgroundColor: '#FFFFFF',
                        borderColor: '#FF6B35',
                        borderRadius: 8,
                        borderWidth: 2,
                        shadow: true,
                        style: {
                            padding: 0,
                            pointerEvents: 'auto'
                        }
                    },
                    zIndex: 10
                }]
                    : []
                )

            ] : (isMobilePortrait ? [
                // Placeholders para móvil portrait (solo 2 ejes)
                { name: 'Placeholder 1', data: [], yAxis: 'portrait-track-left', visible: false },
                { name: 'Placeholder 2', data: [], yAxis: 'portrait-track-right', visible: false }
            ] : (finalYAxis.length > 0 ? [
                // Placeholders para desktop/landscape cuando HAY datos (usan yAxis dinámicos)
                { name: 'Placeholder 1', data: [], yAxis: 'yAxis-0', visible: false },
                { name: 'Placeholder 2', data: [], yAxis: 'yAxis-1', visible: false },
                { name: 'Placeholder 3', data: [], yAxis: 'yAxis-2', visible: false },
                { name: 'Placeholder 4', data: [], yAxis: 'yAxis-3', visible: false },
                { name: 'Placeholder 5', data: [], yAxis: 'yAxis-4', visible: false }
            ] : [
                // Placeholders para desktop/landscape cuando NO HAY datos (usan tracks default)
                { name: 'Placeholder 1', data: [], yAxis: 'track1', visible: false },
                { name: 'Placeholder 2', data: [], yAxis: 'track2', visible: false },
                { name: 'Placeholder 3', data: [], yAxis: 'track3', visible: false },
                { name: 'Placeholder 4', data: [], yAxis: 'track4', visible: false },
                { name: 'Placeholder 5', data: [], yAxis: 'track5', visible: false }
            ])),

            // Configuraciones de performance
            plotOptions: {
                // ?? CONFIGURACIÓN ESPECÍFICA PARA FLAGS DE COMENTARIOS
                flags: {
                    useHTML: true,
                    allowPointSelect: false,
                    enableMouseTracking: true,
                    animation: false,
                    dataGrouping: {
                        enabled: false
                    }, // ? Sin agrupación para flags
                    turboThreshold: 0, // ? Sin optimización turbo para flags
                    cropThreshold: 0, // ? Sin recorte para flags
                    boostThreshold: 0, // ? Sin boost para flags
                },
                series: {
                    animation: true,
                    turboThreshold: 10000000, // Aumentar threshold para mejor performance
                    cropThreshold: 10000000,
                    boostThreshold: 999999999,
                    dataGrouping: {
                        enabled: true, // ✅ Activado para optimización dinámica
                        forced: false, // Permitir que Highcharts decida según zoom
                        approximation: 'average',
                        // 🎯 Unidades adaptativas según rango visible
                        units: [
                            ['millisecond', [1, 2, 5, 10, 20, 50, 100, 200, 500]], // < 30 segundos
                            ['second', [1, 2, 5, 10, 15, 30]], // 30 seg - 1 min
                            ['minute', [1, 2, 5, 10, 15, 30]], // 1 min - 4 horas (DETALLE)
                            ['hour', [1, 2, 3, 6, 12]], // 4 - 12 horas (MEDIO)
                            ['day', [1]] // > 12 horas (GENERAL)
                        ],
                        // 🔑 Configuración de densidad adaptativa por dispositivo
                        // Móvil: más agrupación (menos píxeles disponibles)
                        // Desktop: menos agrupación (más píxeles disponibles)
                        groupPixelWidth: mobile ? 3 : 2 // Móvil: 3px, Desktop: 2px
                    },
                    // ?? MARKER EN HOVER - Punto de seguimiento sutil
                    marker: {
                        enabled: true, // Desactivado por defecto
                        radius: 4,
                    },
                    states: {
                        hover: {
                            enabled: tooltipEnabled, // Solo hover cuando tooltip activo
                            marker: {
                                enabled: false // NUNCA mostrar markers en hover
                            }
                        },
                        inactive: {
                            enabled: false // Desactivar opacidad de series no activas
                        }
                    },
                    //stickyTracking: false, // Evitar captura de eventos touch
                    //allowPointSelect: false, // Evitar selección de puntos
                    // ?? EVENTO CLICK PARA COMENTARIOS
                    events: {
                        click: function (event) {
                            const point = event.point;
                            const series = point.series;

                            // No activar modal si es una serie de flags o comentarios
                            if (series.type === 'flags' || series.options.id === 'commentsPoints') {
                                return false;
                            }

                            const x = point.x;
                            const y = point.y;
                            // Extraer variable ID de la serie (formato: "Var123 (T1.1)")
                            const serieId = series.options.id || series.options.name.match(/Var(\d+)/)?.[1];
                            chartClickComments(x, y, serieId);
                        }
                    }
                }
            },

            // ?? TOOLTIP CON FORMATO HTML - USAR FORMATTER PARA MOSTRAR SOLO UNA FECHA
            tooltip: {
                enabled: tooltipEnabled, // CRÍTICO: OFF = panning funciona, ON = tooltip funciona
                followTouchMove: tooltipEnabled, // Seguir dedo cuando tooltip activo
                shared: tooltipShared, // false = individual por track, true = compartido todos
                useHTML: true,
                backgroundColor: 'rgba(0,0,0,0.85)',
                borderColor: '#666666',
                borderRadius: 8,
                padding: 12,
                hideDelay: tooltipEnabled ? 500 : 0, // 500ms delay cuando activo
                //stickOnContact: false, // CRÍTICO: false para permitir panning y seguimiento
                split: false,
                // Seguir dedo cuando tooltip activo
                //followPointer: tooltipEnabled, // Seguir puntero para mejor tracking
                style: {
                    color: '#ffffff',
                    fontSize: '11px',
                    pointerEvents: 'auto'
                },
                formatter: function () {
                    // 💬 Los comentarios tienen su propio tooltip - ignorarlos aquí
                    // Manejar tooltip individual (this.point) vs compartido (this.points)
                    const points = this.points || [this.point];

                    // Filtrar comentarios del tooltip compartido (tienen su propio tooltip independiente)
                    const dataPoints = points.filter(p => p.series.options.id !== 'commentsPoints');

                    if (dataPoints.length === 0) return false; // No mostrar tooltip si solo hay comentarios

                    // Usar la fecha del primer punto
                    const fecha = new Date(dataPoints[0].x);
                    const fechaFormateada = fecha.toISOString().replace('T', ' ').substring(0, 19);

                    let html = '<div style="padding:8px;">';
                    html += `<div style="font-size:13px;font-weight:bold;margin-bottom:8px;border-bottom:1px solid #555;padding-bottom:5px;">${fechaFormateada}</div>`;

                    // Agregar cada punto (excepto comentarios)
                    dataPoints.forEach(point => {
                        const descriptiveName = point.series.options.custom?.descriptiveName || point.series.name;
                        const unit = point.series.options.custom?.unit || '';
                        html += `<div style="margin:4px 0;">`;
                        html += `<span style="color:${point.color};font-size:14px;">●</span> `;
                        html += `<span style="font-weight:500;">${descriptiveName}:</span> `;
                        html += `<span style="color:#4ECDC4;font-weight:bold;font-size:12px;">${point.y.toFixed(1)}</span> `;
                        html += `<span style="color:#aaa;font-size:10px;">${unit}</span>`;
                        html += `</div>`;
                    });

                    html += '</div>';
                    return html;
                }
            },

            // Configuración de exportación (deshabilitado en móviles)
            exporting: {
                enabled: !isMobilePortrait, // Ocultar botón de menú en móviles
                fallbackToExportServer: false, // No usar servidor externo - exportación del lado del cliente
                buttons: {
                    contextButton: {
                        enabled: !isMobilePortrait,
                        /* height: 24,
                        width: 24, */
                        symbolStroke: theme.chartFont,
                        symbolFill: theme.chartFont,
                        /* symbolSize: 12, */
                        symbolStrokeWidth: 2,
                        symbolStroke: theme.chartFont,
                        theme: { fill: theme.buttonFill }
                    }
                },
                theme: {
                    fill: '#666666'
                }
            },

            // Configuración de créditos
            credits: {
                enabled: false
            },

            // Desactivar accessibility para eliminar warnings
            accessibility: {
                enabled: false
            },

            // Performance boost
            boost: {
                enabled: true,
                useGPUTranslations: true
            }
        };

        return options;
    }, [finalSeries, finalYAxis, commentsData, tooltipEnabled, tooltipShared, availableHours, isMobilePortrait, isMobileLandscape, currentTrackIndex, seriesInfo, forceRenderKey, theme]);

    // 🎯 MEMORIZAR OPCIONES DEL CHART PARA EVITAR RE-RENDERS INNECESARIOS
    const chartOptions = useMemo(() => {
        return getChartOptions();
    }, [getChartOptions]);

    // ?? FUNCIÓN PARA OBTENER VARIABLES DE LA PLANTILLA
    const getDataGraphPlantilla = async (rigId) => {
        const dataSend = rigId + ':' + user;
        let data = await getTableByIdBd(dataSend, 'getvarsalarmgraph');

        // Si no hay datos, crear plantilla por defecto y reintentar
        if (data.length === 0) {
            await crearPlantillaCompletaDefault('graph', rig, user, uid, setRig, null);

            // Esperar un momento para que se inserte en BD
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Reintentar consulta después de crear plantilla
            data = await getTableByIdBd(dataSend, 'getvarsalarmgraph');

            if (data.length === 0) {
                //console.warn('⚠️ No se pudo obtener la plantilla después de crearla');
                return { variables: [], metadata: new Map() };
            }
        }

        let d = [...data];
        let metadata = new Map(); // ?? Definir metadata al inicio

        // ?? EXTRAER METADATA AQUÍ (donde están los datos completos)
        data.forEach(record => {
            const varId = record.nb_var_idn;
            if (varId && varId !== '0') {
                const min = record.nb_tldvrb_min ? parseFloat(record.nb_tldvrb_min) : null;
                const max = record.nb_tldvrb_max ? parseFloat(record.nb_tldvrb_max) : null;
                const alarmMin = record.nb_alarm_min ? parseFloat(record.nb_alarm_min) : null;
                const alarmMax = record.nb_alarm_max ? parseFloat(record.nb_alarm_max) : null;

                metadata.set(varId, {
                    varId: varId,
                    name: record.vr_var_nom || `Var ${varId}`,
                    unit: record.vr_var_umd || '',
                    min: min,
                    max: max,
                    alarmMin: alarmMin,
                    alarmMax: alarmMax
                });
            }
        });
        setVariablesMetadata(metadata);

        const ordenVariables = Array.from({ length: 12 }, (_, index) => {
            const foundItem = d.find(di => index === parseInt(di.nb_tldvrb_ord));
            return foundItem ? foundItem.nb_var_idn : '0';
        });

        const variablesDown = Array.from({ length: 12 }, (_, index) => {
            return d.find(di => index === parseInt(di.nb_tldvrb_ord)) || '';
        });

        setDatosInferiores(variablesDown);
        return { variables: ordenVariables, metadata: metadata };
    };

    // 🎨 ACTUALIZAR TODOS LOS COLORES DE LAS SERIES SEGÚN EL TEMA ACTUAL
    const updateAllSeriesColors = () => {
        if (!chartRef.current?.chart) return;

        const chart = chartRef.current.chart;

        // 🔑 Leer tema DIRECTAMENTE desde localStorage para obtener el valor más actualizado
        const currentTheme = localStorage.getItem('theme') || 'dark';
        // Obtener los colores del tema actual
        let currentSeriesColors;
        if (currentTheme === 'light') {
            currentSeriesColors = THEME_LIGHT.seriesColors;
        } else if (currentTheme === 'custom') {
            const storedColors = localStorage.getItem('colorsCustom');
            const customColors = storedColors ? JSON.parse(storedColors) : {};
            currentSeriesColors = [
                customColors.grColor1_1 || '#FF6B6B',
                customColors.grColor1_2 || '#4ECDC4',
                customColors.grColor2_1 || '#45B7D1',
                customColors.grColor2_2 || '#96CEB4',
                customColors.grColor3_1 || '#FFEAA7',
                customColors.grColor3_2 || '#DDA0DD',
                customColors.grColor4_1 || '#98D8C8',
                customColors.grColor4_2 || '#F7DC6F',
                customColors.grColor5_1 || '#BB8FCE',
                customColors.grColor5_2 || '#85C1E9'
            ];
        } else {
            // dark theme
            currentSeriesColors = [
                '#02f7c6', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
                '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
            ];
        }

        // Iterar sobre todas las series del chart
        chart.series.forEach((serie) => {
            // Ignorar series especiales (comentarios, navigator)
            if (!serie.options.custom?.varId) return;

            const varId = serie.options.custom.varId;
            const trackIndex = serie.options.trackIndex;

            // Encontrar el índice en varsDown para esta serie
            // Calcular varsDownIndex desde trackIndex (cada track tiene 2 posiciones)
            const baseIndex = trackIndex * 2;
            let varsDownIndex = -1;

            // Verificar cuál de las 2 posiciones del track tiene este varId
            if (varsDown[baseIndex] == varId) {
                varsDownIndex = baseIndex;
            } else if (varsDown[baseIndex + 1] == varId) {
                varsDownIndex = baseIndex + 1;
            }

            if (varsDownIndex !== -1) {
                // Obtener el color del tema actual
                const newColor = currentSeriesColors[varsDownIndex % currentSeriesColors.length];

                // Actualizar color de la serie
                serie.update({
                    color: newColor,
                    lineColor: newColor
                }, false); // false = no redraw todavía

            }
        });

        // Redraw una sola vez al final
        chart.redraw();

        // Actualizar seriesInfo con los nuevos colores
        setSeriesInfo(prev => prev.map((s) => {
            // Encontrar varsDownIndex para esta serie
            const baseIndex = s.trackIndex * 2;
            let varsDownIndex = -1;

            if (varsDown[baseIndex] == s.varId) {
                varsDownIndex = baseIndex;
            } else if (varsDown[baseIndex + 1] == s.varId) {
                varsDownIndex = baseIndex + 1;
            }

            if (varsDownIndex !== -1) {
                const newColor = currentSeriesColors[varsDownIndex % currentSeriesColors.length];
                return { ...s, color: newColor };
            }
            return s;
        }));

    };

    // 🗂️ FUNCIONES AUXILIARES PARA SISTEMA DE TABS (Desktop only)

    // Obtener nombre del rig desde el ID
    const getRigNameFromId = useCallback((rigId) => {
        if (!rigId || !rigs) return 'Sin nombre';
        const rigData = rigs.find(r => parseInt(r.nb_tldro_idn) === parseInt(rigId));

        if (rigData?.rig) {
            return rigData.rig;
        } else {
            // Extraer del nombre completo antes del guion
            const fullName = rigData?.vr_tldro_nom || `Rig ${rigId}`;
            const parts = fullName.split('-');
            return parts[0].trim();
        }
    }, [rigs]);

    // Obtener nombre del pozo desde el rig ID
    const getWellNameFromId = useCallback((rigId) => {
        if (!rigId || !rigs) return 'Sin pozo';
        const rigData = rigs.find(r => parseInt(r.nb_tldro_idn) === parseInt(rigId));

        if (rigData?.pozo) {
            return rigData.pozo;
        } else {
            // Extraer del nombre completo despues del guion
            const fullName = rigData?.vr_tldro_nom || rigData?.rig || '';
            const parts = fullName.split('-');
            if (parts.length >= 2) {
                return parts.slice(1).join('-').trim();
            }
            return 'Sin pozo';
        }
    }, [rigs]);

    // Verificar si un rig está transmitiendo
    const checkRigOnlineStatus = useCallback((rigId) => {
        if (!rigId || !rigs) return false;
        const rigData = rigs.find(r => parseInt(r.nb_tldro_idn) === parseInt(rigId));
        return rigData?.tx === true;
    }, [rigs]);

    // Handler para cambiar de tab
    const handleTabChange = useCallback((rigId) => {
        if (rigId !== rig) {
            setRig(rigId);
            finalSetActiveTabId(rigId);
        }
    }, [rig, setRig, finalSetActiveTabId]);

    // Handler para cerrar un tab
    const handleTabClose = useCallback((rigIdToClose) => {
        if (finalOpenTabs.length === 1) {
            return; // No cerrar si es el único tab
        }

        const newTabs = finalOpenTabs.filter(tab => tab.rigId !== rigIdToClose);
        finalSetOpenTabs(newTabs);

        // Si cerramos el tab activo, activar el primero disponible
        if (rigIdToClose === finalActiveTabId && newTabs.length > 0) {
            const newActiveRig = newTabs[0].rigId;
            finalSetActiveTabId(newActiveRig);
            setRig(newActiveRig);
        }
    }, [finalOpenTabs, finalActiveTabId, setRig, finalSetOpenTabs, finalSetActiveTabId]);

    // Handler para abrir modal de selección de rig
    const handleAddTab = useCallback(() => {
        finalSetShowRigSelector(true);
    }, [finalSetShowRigSelector]);

    // Handler para seleccionar un rig desde el modal
    const handleSelectRigFromModal = useCallback((selectedRigId) => {
        finalSetShowRigSelector(false);

        // Verificar si ya existe un tab para este rig
        const existingTab = finalOpenTabs.find(tab => tab.rigId === selectedRigId);

        if (existingTab) {
            // Solo cambiar al tab existente
            finalSetActiveTabId(selectedRigId);
            setRig(parseInt(selectedRigId));
        } else if (finalOpenTabs.length < 6) {
            // Crear nuevo tab
            const rigData = rigs.find(r => parseInt(r.nb_tldro_idn) === parseInt(selectedRigId));

            // Extraer nombre del rig y pozo
            let rigName, wellName;
            if (rigData?.pozo) {
                rigName = rigData.rig || rigData.vr_tldro_nom || `Rig ${selectedRigId}`;
                wellName = rigData.pozo;
            } else {
                const fullName = rigData?.vr_tldro_nom || rigData?.rig || `Rig ${selectedRigId}`;
                const parts = fullName.split('-');
                if (parts.length >= 2) {
                    rigName = parts[0].trim();
                    wellName = parts.slice(1).join('-').trim();
                } else {
                    rigName = fullName;
                    wellName = 'Sin pozo';
                }
            }

            const newTab = {
                rigId: selectedRigId,
                rigName: rigName,
                wellName: wellName,
                isOnline: rigData?.tx === true,
                createdAt: Date.now()
            };

            finalSetOpenTabs(prev => [...prev, newTab]);
            finalSetActiveTabId(selectedRigId);
            setRig(parseInt(selectedRigId));
        }
    }, [finalOpenTabs, rigs, finalSetOpenTabs, finalSetActiveTabId, finalSetShowRigSelector, setRig]);

    //--------------------------UseEffects--------------------------

    // 🔄 EFECTO PARA DETECTAR CAMBIO DE RIG Y CARGAR DESDE CACHE O BD
    useEffect(() => {
        // Si no hay varsDown o metadata aún, esperar
        if (!varsDown || varsDown.length === 0 || !variablesMetadata) {
            return;
        }

        // DETENER polling del rig anterior INMEDIATAMENTE
        if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        // CANCELAR todos los timeouts pendientes de addLivePointsGradually
        if (pendingTimeoutsRef.current.length > 0) {
            pendingTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
            pendingTimeoutsRef.current = [];
        }

        // LIMPIAR el chart completamente para evitar que se agreguen puntos del rig anterior
        if (chartRef.current?.chart) {
            const chart = chartRef.current.chart;
            // Remover todas las series
            while (chart.series.length > 0) {
                chart.series[0].remove(false);
            }
            // ✅ CRÍTICO: Resetear EJES inmediatamente después de remover series
            // Esto evita que el eje X mantenga extremos del rig anterior
            if (chart.xAxis && chart.xAxis[0]) {
                chart.xAxis[0].setExtremes(null, null, false, false);
            }
            // Resetear todos los Y-axes también
            if (chart.yAxis && chart.yAxis.length > 0) {
                for (let i = 0; i < chart.yAxis.length; i++) {
                    if (chart.yAxis[i]) {
                        chart.yAxis[i].setExtremes(null, null, false, false);
                    }
                }
            }
            chart.redraw();
        }

        // LIMPIAR datos de grilla izquierda SOLO si ya había datos cargados (no en la primera carga)
        if (dataLoaded) {
            setDatosIzquierda(Array(6).fill(''));
        }

        // Actualizar refs para evitar stale closures
        rigRef.current = rig;
        varsDownRef.current = varsDown;
        variablesIzquierdaRef.current = variablesIzquierda;

        // Siempre cargar cuando cambia el rig (cache o BD)
        // Cargar desde cache o BD (la función internamente limpiará datos si es necesario)
        loadInitialData(varsDown, variablesMetadata, variablesIzquierda);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rig, varsDown, variablesMetadata]); // NO incluir variablesIzquierda para evitar loop

    // 🧹 CLEANUP: Detener actualizaciones en vivo al desmontar componente
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearTimeout(intervalRef.current);
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setDataLoaded(false);// Resetear flag de datos cargados al desmontar
        };
    }, []);

    // 📱 Detectar cambios de orientación mediante resize/orientationchange
    useEffect(() => {
        const detectOrientation = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const isMobileDevice = width <= 768;
            const isHorizontalOrientation = width > height;

            const newOrientation = {
                isMobile: isMobileDevice,
                isHorizontal: isHorizontalOrientation
            };

            // Solo actualizar si cambió la orientación
            setInternalOrientation(prev => {
                if (prev.isMobile !== newOrientation.isMobile || prev.isHorizontal !== newOrientation.isHorizontal) {
                    setForceRenderKey(k => k + 1);// Forzar re-render incrementando key para destruir y recrear el chart
                    return newOrientation;
                }
                return prev;
            });
        };

        detectOrientation();// Detectar orientaci�n inicial

        // Escuchar cambios de orientación y resize
        window.addEventListener('resize', detectOrientation);
        window.addEventListener('orientationchange', detectOrientation);

        return () => {
            window.removeEventListener('resize', detectOrientation);
            window.removeEventListener('orientationchange', detectOrientation);
        };
    }, []);

    // 🗂️ INICIALIZAR TAB INICIAL (solo desktop)
    useEffect(() => {
        if (rig && !mobile && finalOpenTabs.length === 0 && rigs && rigs.length > 0) {
            const rigData = rigs.find(r => parseInt(r.nb_tldro_idn) === parseInt(rig));

            // Extraer nombre del rig y pozo
            let rigName, wellName;
            if (rigData?.pozo) {
                rigName = rigData.rig || rigData.vr_tldro_nom || `Rig ${rig}`;
                wellName = rigData.pozo;
            } else {
                const fullName = rigData?.vr_tldro_nom || rigData?.rig || `Rig ${rig}`;
                const parts = fullName.split('-');
                if (parts.length >= 2) {
                    rigName = parts[0].trim();
                    wellName = parts.slice(1).join('-').trim();
                } else {
                    rigName = fullName;
                    wellName = 'Sin pozo';
                }
            }

            const initialTab = {
                rigId: rig,
                rigName: rigName,
                wellName: wellName,
                isOnline: rigData?.tx === true,
                createdAt: Date.now()
            };

            finalSetOpenTabs([initialTab]);
            finalSetActiveTabId(rig);
        }
    }, [rig, mobile, rigs, finalOpenTabs.length, finalSetOpenTabs, finalSetActiveTabId]);

    // 🔄 ACTUALIZAR TAB ACTIVO cuando cambia el rig (para reflejar nombres correctos)
    useEffect(() => {
        if (!mobile && rig && finalOpenTabs.length > 0 && rigs && rigs.length > 0) {
            const existingTab = finalOpenTabs.find(tab => parseInt(tab.rigId) === parseInt(rig));

            // Obtener datos directamente del array rigs
            const rigData = rigs.find(r => parseInt(r.nb_tldro_idn) === parseInt(rig));

            if (!rigData) return;

            let newRigName, newWellName;
            if (rigData.pozo) {
                newRigName = rigData.rig || rigData.vr_tldro_nom || `Rig ${rig}`;
                newWellName = rigData.pozo;
            } else {
                const fullName = rigData.vr_tldro_nom || rigData.rig || `Rig ${rig}`;
                const parts = fullName.split('-');
                if (parts.length >= 2) {
                    newRigName = parts[0].trim();
                    newWellName = parts.slice(1).join('-').trim();
                } else {
                    newRigName = fullName;
                    newWellName = 'Sin pozo';
                }
            }
            const newOnlineStatus = rigData.tx === true;

            if (existingTab) {
                // El tab ya existe para este rig
                let needsUpdate = false;

                // Verificar si necesita actualizar información del tab
                if (existingTab.rigName !== newRigName || existingTab.wellName !== newWellName || existingTab.isOnline !== newOnlineStatus) {
                    const updatedTabs = finalOpenTabs.map(tab =>
                        parseInt(tab.rigId) === parseInt(rig)
                            ? { ...tab, rigName: newRigName, wellName: newWellName, isOnline: newOnlineStatus }
                            : tab
                    );
                    finalSetOpenTabs(updatedTabs);
                    needsUpdate = true;
                }

                // Siempre asegurar que el tab se active (incluso si ya está activo, forzar actualización)
                if (parseInt(finalActiveTabId) !== parseInt(rig)) {
                    finalSetActiveTabId(parseInt(rig));
                }
            } else {
                // El tab no existe, crearlo (viene de Home u otra navegación)
                if (finalOpenTabs.length < 6) {
                    const newTab = {
                        rigId: parseInt(rig),
                        rigName: newRigName,
                        wellName: newWellName,
                        isOnline: newOnlineStatus,
                        createdAt: Date.now()
                    };
                    finalSetOpenTabs(prev => [...prev, newTab]);
                    finalSetActiveTabId(parseInt(rig));
                } else {
                    // Si ya hay 6 tabs, reemplazar el último por el nuevo
                    const newTab = {
                        rigId: parseInt(rig),
                        rigName: newRigName,
                        wellName: newWellName,
                        isOnline: newOnlineStatus,
                        createdAt: Date.now()
                    };
                    const updatedTabs = [...finalOpenTabs.slice(0, -1), newTab];
                    finalSetOpenTabs(updatedTabs);
                    finalSetActiveTabId(parseInt(rig));
                }
            }
        }
    }, [rig, rigs, mobile, finalOpenTabs, finalSetOpenTabs, finalActiveTabId, finalSetActiveTabId]);

    // ??? DETECTAR CAMBIOS DE VISIBILIDAD DE PESTA�A (Page Visibility API)
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.hidden) {
                // ⏸️ Usuario salió de la pestaña - EL POLLING SE PAUSARÁ AUTOMÁTICAMENTE
                lastVisibleTimeRef.current = Date.now();
            } else {
                // ▶️ Usuario volvió a la pestaña - HACER CATCH-UP Y REANUDAR POLLING
                const timeAway = Date.now() - lastVisibleTimeRef.current;
                const secondsAway = Math.floor(timeAway / 1000);
                const minutesAway = Math.floor(secondsAway / 60);

                // 🔄 Siempre hacer catch-up al volver (consultar datos pendientes desde última fecha)
                if (secondsAway > 10 && nbRef.current && rigRef.current && varsDownRef.current?.length > 0) {
                    await performCatchUp(secondsAway);
                }
                // ▶️ El polling se reanudará automáticamente al detectar que document.hidden = false
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // ?? LIMPIAR CHART Y DATOS PROCESADOS CUANDO CAMBIE EL RIG (para retoma correcta)
    useEffect(() => {
        if (rig) {

            // ?? Resetear extremos del gráfico si existe
            if (chartRef.current) {
                const chart = chartRef.current.chart;
                if (chart && chart.xAxis && chart.xAxis[0]) {
                    // Resetear extremos a null para que se recalculen con los nuevos datos
                    chart.xAxis[0].setExtremes(null, null, false, false);
                }
            }

            // ?? Limpiar SOLO datos ya procesados (no afecta props del padre)
            setDataLoaded(false);
            // ✅ NO resetear varsDown ni variablesMetadata (vienen del padre y se sincronizan en el otro useEffect)
            setMedidasGraficas([]);
            setFinalSeries([]);
            setFinalYAxis([]);
            setSeriesInfo([]);
            setDatosInferiores([]);
            setEnVivo(false);
            setLoading(false);
            setLoadingMessage('');
            setOldestDate(null);
            setLatestDate(null);

            // ?? Limpiar referencias
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            nbRef.current = null;
        }
    }, [rig]);

    // 🔄 SINCRONIZAR REFS cuando cambien los estados (para evitar stale closures en polling)
    useEffect(() => {
        varsDownRef.current = varsDown;
    }, [varsDown]);

    useEffect(() => {
        variablesIzquierdaRef.current = variablesIzquierda;
    }, [variablesIzquierda]);

    useEffect(() => {
        finalSeriesRef.current = finalSeries;
    }, [finalSeries]);

    // ?? CARGAR DATOS INICIALES 
    useEffect(() => {
        if (rig && !dataLoaded) initializeChart();
    }, [rig, dataLoaded]);

    // 🔄 Grilla izquierda se actualiza directamente en el DOM desde addLivePointsOptimized
    // No necesita useEffect con medidasGraficas (contiene 4 horas de datos - demasiado pesado)

    // 🎯 CONTROLAR VISIBILIDAD DEL FOOTER (usa internalLoading, NO el prop del padre)
    useEffect(() => {
        const shouldShow = internalLoading || (loadingMessage && loadingMessage.trim() !== '');
        setShowFooter(shouldShow);
    }, [internalLoading, loadingMessage]);

    // 🎨 DETECTAR CAMBIOS DE TEMA Y COLORES CUSTOM
    useEffect(() => {
        let previousColorsCustom = localStorage.getItem('colorsCustom');

        const handleThemeChange = () => {
            const newTheme = localStorage.getItem('theme') || 'dark';
            const currentColorsCustom = localStorage.getItem('colorsCustom');

            // Detectar si cambió el tema o los colores custom
            const themeChanged = newTheme !== themeMode;
            const colorsChanged = currentColorsCustom !== previousColorsCustom;

            if (themeChanged) {
                setThemeMode(newTheme);
            }

            // Si cambió el tema o los colores custom
            if (themeChanged || colorsChanged) {
                previousColorsCustom = currentColorsCustom;

                // Aplicar atributo data-theme al documento para CSS
                document.documentElement.setAttribute('data-theme', newTheme);

                // Actualizar colores de las series dinámicamente sin re-render
                // Como updateAllSeriesColors lee directamente de localStorage, no necesita delay
                updateAllSeriesColors();
            }
        };

        // Aplicar tema inicial
        handleThemeChange();

        // Escuchar evento personalizado para cambios de tema en la misma pestaña
        window.addEventListener('themeChanged', handleThemeChange);

        // Storage event solo funciona entre pestañas, pero lo agregamos por si acaso
        window.addEventListener('storage', (e) => {
            if (e.key === 'theme' || e.key === 'colorsCustom') {
                handleThemeChange();
            }
        });

        return () => {
            window.removeEventListener('themeChanged', handleThemeChange);
            window.removeEventListener('storage', handleThemeChange);
        };
    }, [themeMode]); // updateAllSeriesColors se llama dentro del timeout, no necesita estar en deps

    useEffect(() => {
        enVivoRef.current = enVivo;
    }, [enVivo]);

    useEffect(() => {
        varsDownRef.current = varsDown;
    }, [varsDown]);

    useEffect(() => {
        variablesIzquierdaRef.current = variablesIzquierda;
    }, [variablesIzquierda]);

    // 🔄 FASE 1: Disparar carga en segundo plano cuando los datos iniciales estén listos
    useEffect(() => {
        // Verificar que tengamos todo lo necesario Y que no se haya ejecutado antes
        if (oldestDate && varsDown && varsDown.length > 0 && finalSeries.length > 0 &&
            !backgroundLoading && !backgroundData && !backgroundLoadedRef.current) {

            // Marcar como ejecutado para evitar ciclos
            backgroundLoadedRef.current = true;

            // Esperar 8 segundos después de que los datos estén listos
            const timer = setTimeout(() => {
                loadBackgroundData();
            }, 8000);

            return () => clearTimeout(timer);
        }
    }, [oldestDate, varsDown, finalSeries, backgroundLoading, backgroundData]);

    useEffect(() => {
        finalSeriesRef.current = finalSeries;
    }, [finalSeries]);

    // 🔄 REPROCESAR DATOS CUANDO CAMBIA EL MODO DE EJES
    useEffect(() => {
        // Solo reprocesar si ya hay datos cargados
        if (medidasGraficas.length > 0 && varsDown.length > 0 && dataLoaded) {
            processInitialData(medidasGraficas, varsDown, variablesMetadata);
        }
    }, [useFixedAxes]); // Solo escuchar cambios en useFixedAxes

    // 🔄 REGENERAR EJES CUANDO CAMBIA LA ORIENTACIÓN
    useEffect(() => {
        // Solo regenerar si ya hay datos cargados
        if (medidasGraficas.length > 0 && varsDown.length > 0 && dataLoaded) {
            processInitialData(medidasGraficas, varsDown, variablesMetadata);

            // 📊 RENDERIZAR/LIMPIAR LEYENDAS según orientación o fullscreen
            setTimeout(() => {
                if (chartRef.current && chartRef.current.chart) {
                    const chart = chartRef.current.chart;
                    if (isMobileLandscape || isFullscreen) {
                        renderTrackLegends(chart);
                    } else {
                        // Limpiar leyendas si no es mobile landscape ni fullscreen
                        legendElementsRef.current.forEach(element => {
                            if (element && element.destroy) {
                                element.destroy();
                            }
                        });
                        legendElementsRef.current = [];
                        legendTextsRef.current = [];
                    }
                }
            }, 300); // Delay para que el chart se redibuje primero
        }
    }, [isMobilePortrait, isMobileLandscape, isFullscreen]); // Escuchar cambios en orientación y fullscreen

    // 🔄 FORZAR REFLOW DEL CHART CUANDO CAMBIA LA VISIBILIDAD DE GRILLA IZQUIERDA
    useEffect(() => {
        if (chartRef.current && chartRef.current.chart) {
            // Dar un pequeño delay para que el contenedor ajuste su ancho primero
            setTimeout(() => {
                chartRef.current?.chart.reflow();
            }, 100);
        }
    }, [grillaIzqVisible]);

    // 📅 OBTENER FECHA DE INICIO DE TRANSMISIÓN DEL RIG
    useEffect(() => {
        const fetchRigTransmissionStartDate = async () => {
            if (!rig) return;

            try {
                const data = await getTableByIdBd(rig, 'getrigbyid');
                if (data && data.length > 0) {
                    const rigData = data[0];
                    const startDate = new Date(rigData.dt_adt_fchreg);
                    setRigTransmissionStartDate(startDate);
                }
            } catch (error) {
                console.error('❌ Error obteniendo fecha inicio transmisión:', error);
            }
        };

        fetchRigTransmissionStartDate();
    }, [rig]);

    // 🔄 SINCRONIZAR viewModeRef CON viewMode
    useEffect(() => {
        viewModeRef.current = viewMode;
    }, [viewMode]);

    // ?? USEEFFECT PARA PROCESAR COMENTARIOS EN FLAGS
    useEffect(() => {
        if (comentarios && comentarios.length > 0) {

            const flagsData = comentarios.map(com => ({
                nb_com_idn: com.nb_com_idn,
                fecha: com.dt_com_fch,
                comentario: com.nb_com_val || 'Sin comentario',
                usuario: com.vr_adt_usureg || 'Usuario desconocido',
                variable: com.nb_var_idn || '',
                profundidad: com.nb_com_prof || ''
            }));

            setCommentsData(flagsData);

            // 💾 Actualizar cache con los comentarios actualizados
            if (updateCachedRigData && rig) {
                updateCachedRigData(rig, {
                    comentarios: comentarios,
                    commentsData: flagsData
                });
            }
        } else {
            setCommentsData([]);

            // 💾 Actualizar cache con comentarios vacíos
            if (updateCachedRigData && rig) {
                updateCachedRigData(rig, {
                    comentarios: [],
                    commentsData: []
                });
            }
        }
    }, [comentarios, rig, updateCachedRigData]);

    //--------------------------------------------------------------------------------

    // 📋 CARGAR PLANTILLA DE GRILLA IZQUIERDA
    const loadPlantillaIzquierda = async () => {
        try {
            const data = await getTableByIdBd(rig + ':' + user, 'getvarsalarmgraphizq');

            if (data && data.length > 0) {
                const ordenVariables = Array(6).fill('0');
                const datosOrdenados = Array(6).fill('');

                data.forEach((item) => {
                    const orden = item.nb_datos_grafica_orden;
                    if (orden >= 0 && orden < 6) {
                        ordenVariables[orden] = item.nb_var_idn;
                        datosOrdenados[orden] = item;
                    }
                });

                setVariablesIzquierda(ordenVariables);
                setDatosIzquierda(datosOrdenados);

                // Retornar las variables para uso inmediato
                return ordenVariables;
            }

            return Array(6).fill('0');
        } catch (error) {
            console.error('❌ Error cargando plantilla grilla izquierda:', error);
            return Array(6).fill('0');
        }
    };

    // Handler para recargar plantilla después de cambios CRUD
    const handleReloadPlantillaIzquierda = async () => {
        await loadPlantillaIzquierda();
    };

    // ??? FUNCIÓN ORIGINAL
    const initializeChart = async () => {
        try {
            const result = await getDataGraphPlantilla(rig);
            if (result && result.variables && result.variables.length > 0) {
                setVarsDown(result.variables);

                // Cargar plantilla de grilla izquierda ANTES de la carga inicial
                const varsIzq = await loadPlantillaIzquierda();

                // Pasar variables izquierda a loadInitialData
                await loadInitialData(result.variables, result.metadata, varsIzq);
                setDataLoaded(true);// ? Marcar datos como cargados para evitar cargas duplicadas
            }

        } catch (error) {
            console.error('? Error inicializando gr?ficas:', error);
            setLoadingMessage('? Error cargando datos iniciales');
            setTimeout(() => setLoadingMessage(''), 3000);
        }
    };

    // ?? CARGAR DATOS INICIALES DE 4 HORAS
    const loadInitialData = async (variables, metadata = null, varsIzq = []) => {
        try {
            // PASO 0: Verificar si hay datos en cache
            if (getCachedRigData) {
                const cachedData = getCachedRigData(rig);
                if (cachedData && cachedData.finalSeries && cachedData.finalSeries.length > 0) {
                    setLoadingMessage('📦 Cargando desde cache...');

                    // LIMPIAR datos de grillas ANTES de restaurar para evitar parpadeos
                    setMedidasGraficas([]);
                    setDatosIzquierda(Array(6).fill(''));

                    // Restaurar TODOS los estados necesarios para mostrar el gráfico
                    if (cachedData.oldestDate) setOldestDate(cachedData.oldestDate);
                    if (cachedData.latestDate) setLatestDate(cachedData.latestDate);
                    if (cachedData.ultimaFecha) {
                        nbRef.current = new Date(cachedData.ultimaFecha).getTime();
                    }

                    // Restaurar datos del gráfico
                    if (cachedData.finalSeries) {
                        setFinalSeries(cachedData.finalSeries);
                        finalSeriesRef.current = cachedData.finalSeries;
                    }
                    if (cachedData.finalYAxis) setFinalYAxis(cachedData.finalYAxis);
                    if (cachedData.seriesInfo) setSeriesInfo(cachedData.seriesInfo);
                    if (cachedData.medidasGraficas) setMedidasGraficas(cachedData.medidasGraficas);

                    // 💬 Restaurar comentarios desde cache
                    if (cachedData.comentarios) setComentarios(cachedData.comentarios);
                    if (cachedData.commentsData) setCommentsData(cachedData.commentsData);


                    // 💬 Si no hay comentarios en cache, cargarlos desde BD
                    if ((!cachedData.comentarios || cachedData.comentarios.length === 0) && cachedData.oldestDate && cachedData.latestDate) {
                        await cargarComentarios(cachedData.oldestDate, cachedData.latestDate);
                    }

                    // Cargar plantilla de grilla izquierda para el nuevo rig
                    // ESPERAR a que termine antes de marcar como cargado
                    if (dataLoaded) await loadPlantillaIzquierda();

                    // Marcar datos como cargados DESPUÉS de cargar plantilla
                    setDataLoaded(true);
                    setInternalLoading(false);
                    setLoading(false);
                    setLoadingMessage('');

                    // Actualizar timestamp de ultimo acceso
                    if (updateCachedRigData) {
                        updateCachedRigData(rig, { lastAccessed: Date.now() });
                    }

                    // Iniciar polling después de restaurar desde caché
                    setTimeout(() => {
                        startLiveUpdates();
                    }, 1000);

                    return; // Salir sin cargar desde BD
                }
            }

            setInternalLoading(true);
            setLoading(true); // Sincronizar con padre para otros componentes
            setLoadingMessage('📅 Consultando última fecha de datos...');

            // ?? PASO 1: Obtener la última fecha real de datos transmitidos
            const dataSend1 = `${rig}:${uid}`;
            const dataLastDate = await getTableByIdBd(dataSend1, 'getvarsalarmgraphact');

            if (!dataLastDate || dataLastDate.length === 0) {
                setLoadingMessage('?? No hay datos disponibles');
                // ?? LIMPIAR LOADING Y MENSAJE DESPU�S DE 3 SEGUNDOS
                setInternalLoading(false);
                setLoading(false);
                setTimeout(() => setLoadingMessage(''), 3000);
                return;
            }

            const ultimaFecha = new Date(dataLastDate[0].dt_mda_fch);
            setLatestDate(ultimaFecha); // ? Guardar fecha m�s reciente para comentarios

            // ?? PASO 2: Calcular 4 horas hacia atrás desde la última fecha real
            setLoadingMessage('📅 Cargando últimas 4 horas de datos...');

            const fechaF = ultimaFecha.toISOString().slice(0, 19); // formato: yyyy-MM-DDTHH:mm:ss
            const fechaInicio = new Date(ultimaFecha.getTime() - (4 * 60 * 60 * 1000)); // 4 horas atrás
            setOldestDate(fechaInicio); // ? Guardar fecha más antigua inicial
            const fechaI = fechaInicio.toISOString().slice(0, 19);

            // Construir string de variables (SOLO grilla inferior - 12 posiciones)
            // La grilla izquierda NO necesita datos históricos, solo datos en vivo del polling
            const varsString = variables.join(',');
            const dataSend = `${rig};${varsString};${fechaF};${fechaI}`;

            setLoadingMessage('📅 Consultando base de datos...');

            // Llamar a BD con el método correcto
            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            if (data && data.length > 0) {
                setLoadingMessage(`? Procesando ${data.length} registros...`);
                processInitialData(data, variables, metadata);// ?? HighchartsReact: Procesar datos directamente con metadata
                cargarComentarios(fechaInicio, ultimaFecha);// ?? Cargar comentarios del mismo per�odo
                // Loading se limpia en processInitialData
            } else {
                setLoadingMessage(' No hay datos recientes'); setTimeout(() => setLoadingMessage(''), 3000);
                // ?? LIMPIAR LOADING Y MENSAJE INMEDIATAMENTE
                setInternalLoading(false);
                setLoading(false);
                setLoadingMessage('');
            }

        } catch (error) {
            //console.error('? Error cargando datos iniciales:', error);
            setLoadingMessage('? Error cargando datos');
            setInternalLoading(false);
            setLoading(false);
            setLoadingMessage('');
            setLoadingMessage(' Error cargando datos'); setTimeout(() => setLoadingMessage(''), 3000);
        }
        // 🔑 NO hay finally - processInitialData se encarga de limpiar loading
    };

    // ? CARGAR COMENTARIOS DEL PER�ODO
    const cargarComentarios = useCallback(async (fechaInicio, fechaFin) => {
        try {
            // ⚠️ AJUSTE DE ZONA HORARIA: +5 horas (Colombia UTC-5)
            // Los datos del chart están en UTC pero los comentarios en BD están en hora local Colombia
            // Por lo tanto, sumamos 5 horas a las fechas del chart para consultarlos correctamente
            const toLocalISOString = (date) => {
                const adjustedDate = new Date(date.getTime() + (5 * 60 * 60 * 1000));

                const year = adjustedDate.getFullYear();
                const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
                const day = String(adjustedDate.getDate()).padStart(2, '0');
                const hour = String(adjustedDate.getHours()).padStart(2, '0');
                const minute = String(adjustedDate.getMinutes()).padStart(2, '0');
                const second = String(adjustedDate.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
            };

            const fechaI = toLocalISOString(fechaInicio);
            const fechaF = toLocalISOString(fechaFin);

            const dataSend = `${rig};${fechaF};${fechaI}`;
            const comentariosData = await getTableByIdBd(dataSend, 'getcommentsperiod');

            if (comentariosData && comentariosData.length > 0) {
                setComentarios(comentariosData);
            } else {
                setComentarios([]);
            }

        } catch (error) {
            console.error('❌ Error cargando comentarios:', error);
            setComentarios([]);
        }
    }, [rig]);

    // 💬 CALLBACK PARA RECARGAR COMENTARIOS DESPUÉS DE EDITAR/ELIMINAR
    const handleCommentUpdated = useCallback(async () => {
        // Recargar comentarios del período actual
        if (oldestDate && latestDate) {
            await cargarComentarios(oldestDate, latestDate);
        }
    }, [oldestDate, latestDate, rig, cargarComentarios]);

    // 🔄 FASE 1: CARGAR HORAS ADICIONALES EN SEGUNDO PLANO (SOLO CONSULTA)
    const loadBackgroundData = async () => {
        if (!oldestDate || !varsDown || varsDown.length === 0 || backgroundLoading) return;

        try {
            setBackgroundLoading(true);

            // Calcular horas adicionales desde oldestDate hacia atrás
            const fechaFin = new Date(oldestDate);
            const fechaInicio = new Date(fechaFin.getTime() - (backgroundHoursToLoad * 60 * 60 * 1000));

            const fechaF = fechaFin.toISOString().slice(0, 19);
            const fechaI = fechaInicio.toISOString().slice(0, 19);

            const varsString = varsDown.join(',');
            const dataSend = `${rig};${varsString};${fechaF};${fechaI}`;
            const startTime = performance.now();

            // CONSULTA EN SEGUNDO PLANO - No bloquea UI
            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            const endTime = performance.now();
            const duration = ((endTime - startTime) / 1000).toFixed(2);

            if (data && data.length > 0) {
                // Guardar datos sin procesar
                setBackgroundData(data);

                // 🔄 FASE 2: Procesar datos en segundo plano
                processBackgroundData(data, fechaInicio);
            } else {
                setBackgroundData(null);
                setBackgroundLoading(false);
            }

        } catch (error) {
            console.error('❌ FASE 1: Error en carga background:', error);
            setBackgroundData(null);
            setBackgroundLoading(false);
        }
    };

    // 🔄 FASE 2: PROCESAR DATOS EN SEGUNDO PLANO (SIN BLOQUEAR UI)
    const processBackgroundData = (data, newOldestDate) => {
        const startTime = performance.now();

        // Usar requestIdleCallback para procesar cuando el navegador esté inactivo
        const processInChunks = () => {
            return new Promise((resolve) => {
                const CHUNK_SIZE = 500; // Procesar 500 registros a la vez
                let currentIndex = 0;
                const newPoints = new Map();

                // Inicializar Map para cada variable
                const validVariables = varsDown.filter(varId => varId !== '0' && varId !== 0);
                validVariables.forEach(varId => {
                    newPoints.set(parseInt(varId), []);
                });

                const processChunk = (deadline) => {
                    // Procesar mientras haya tiempo disponible
                    while (currentIndex < data.length && (deadline.timeRemaining() > 0 || deadline.didTimeout)) {
                        const endIndex = Math.min(currentIndex + CHUNK_SIZE, data.length);

                        for (let i = currentIndex; i < endIndex; i++) {
                            const record = data[i];
                            const timestamp = new Date(record.dt_mda_fch).getTime();
                            const varId = parseInt(record.nb_var_idn);
                            const value = parseFloat(record.nb_mda_val) || 0;

                            if (newPoints.has(varId)) {
                                newPoints.get(varId).push([timestamp, value]);
                            }
                        }

                        currentIndex = endIndex;

                    }

                    if (currentIndex < data.length) {
                        // Aún hay más datos, programar siguiente chunk
                        requestIdleCallback(processChunk, { timeout: 1000 });
                    } else {
                        // Procesamiento completo
                        resolve(newPoints);
                    }
                };

                // Iniciar procesamiento
                requestIdleCallback(processChunk, { timeout: 1000 });
            });
        };

        // Ejecutar procesamiento en chunks
        processInChunks().then((newPoints) => {
            // Actualizar oldestDate
            setOldestDate(newOldestDate);
            // 🔄 FASE 3: Agregar datos al chart sin afectar navegación
            addBackgroundDataToChart(newPoints, newOldestDate);
        });
    };

    // 🔄 FASE 3: AGREGAR DATOS AL CHART SIN AFECTAR NAVEGACIÓN
    const addBackgroundDataToChart = (newPoints, newOldestDate) => {

        if (!chartRef.current?.chart) {
            console.error('❌ FASE 3: Chart no disponible');
            setBackgroundLoading(false);
            return;
        }

        const chart = chartRef.current.chart;
        const xAxis = chart.xAxis[0];

        // 🔑 GUARDAR EXTREMOS ACTUALES DEL USUARIO
        const currentExtremes = xAxis.getExtremes();
        const userMin = currentExtremes.min;
        const userMax = currentExtremes.max;

        // Agregar nuevos puntos a las series existentes
        const updatedSeries = finalSeries.map(serie => {
            const varId = parseInt(serie.name.match(/Var(\d+)/)?.[1]);
            const additionalPoints = newPoints.get(varId) || [];

            if (additionalPoints.length > 0) {
                // Combinar datos existentes con nuevos y ordenar
                const combinedData = serie.data.concat(additionalPoints);
                combinedData.sort((a, b) => a[0] - b[0]);

                return {
                    ...serie,
                    data: combinedData
                };
            }

            return serie;
        });

        // Actualizar las series (esto NO cambia los extremos visuales)
        setFinalSeries(updatedSeries);

        // Recalcular horas disponibles
        const allTimestamps = [];
        updatedSeries.forEach(serie => {
            serie.data.forEach(point => allTimestamps.push(point[0]));
        });

        if (allTimestamps.length > 0) {
            let minTime = Infinity;
            let maxTime = -Infinity;

            allTimestamps.forEach(timestamp => {
                if (timestamp < minTime) minTime = timestamp;
                if (timestamp > maxTime) maxTime = timestamp;
            });

            const timeDiffMs = maxTime - minTime;
            const hoursAvailable = Math.ceil(timeDiffMs / (1000 * 60 * 60));

            setAvailableHours(hoursAvailable);
        }

        // Actualizar oldestDate
        setOldestDate(newOldestDate);

        // 💬 CARGAR COMENTARIOS DEL RANGO COMPLETO (newOldestDate hasta latestDate)
        if (latestDate) cargarComentarios(newOldestDate, latestDate);

        // 🔑 RESTAURAR EXTREMOS DEL USUARIO (sin animación)
        // Usar setTimeout para asegurar que las series se actualizaron primero
        setTimeout(() => {
            if (chartRef.current?.chart) {
                const xAxis = chartRef.current.chart.xAxis[0];
                // Restaurar extremos SIN animación para que sea imperceptible
                xAxis.setExtremes(userMin, userMax, true, false);
            }
            setBackgroundLoading(false);
            setBackgroundData(null);
        }, 100);
    };

    // ??? CARGAR M�S DATOS HACIA ATR�S
    const loadMoreData = async () => {
        if (!oldestDate || !varsDown || varsDown.length === 0 || loadingMore) {
            return;
        }

        try {
            setLoadingMore(true);
            setLoadingMessage(`?? Cargando ${hoursToLoad} horas adicionales...`);

            // Calcular rango desde la fecha m�s antigua hacia atr�s
            const fechaFin = new Date(oldestDate);
            const fechaInicio = new Date(fechaFin.getTime() - (hoursToLoad * 60 * 60 * 1000));

            const fechaF = fechaFin.toISOString().slice(0, 19);
            const fechaI = fechaInicio.toISOString().slice(0, 19);



            const varsString = varsDown.join(',');
            const dataSend = `${rig};${varsString};${fechaF};${fechaI}`;

            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            if (data && data.length > 0) {


                // ? Combinar con datos existentes - Optimizado para grandes vol�menes
                const combinedData = data.concat(medidasGraficas);


                // ? Actualizar fecha m�s antigua - Optimizado para grandes vol�menes
                let minTime = Infinity;
                data.forEach(d => {
                    const timestamp = new Date(d.dt_mda_fch).getTime();
                    if (timestamp < minTime) minTime = timestamp;
                });
                const newOldestDate = new Date(minTime);
                setOldestDate(newOldestDate);

                // 🔄 CARGAR COMENTARIOS DE TODO EL RANGO VISIBLE (oldestDate hasta latestDate)
                if (latestDate) {
                    cargarComentarios(newOldestDate, latestDate);
                }

                // 💾 Actualizar el estado con los datos combinados
                setMedidasGraficas(combinedData);

                // ? Procesar SOLO los datos nuevos (modo incremental)
                processAdditionalData(data, varsDown, variablesMetadata);
            } else {
                setLoadingMessage('⚠️ No hay más datos históricos');
                setTimeout(() => setLoadingMessage(''), 3000);
            }

        } catch (error) {
            console.error('? Error cargando m?s datos:', error);
            setLoadingMessage(' Error cargando datos adicionales'); setTimeout(() => setLoadingMessage(''), 3000);
        } finally {
            setLoadingMore(false);
            setLoadingMessage('');
        }
    };

    // ?? NAVEGAR HACIA ATR�S EN EL TIEMPO (manteniendo el rango actual)
    const shiftTimeBackward = async () => {
        if (!chartRef.current || !chartRef.current.chart) return;

        const chart = chartRef.current.chart;
        const xAxis = chart.xAxis[0];
        const extremes = xAxis.getExtremes();

        // Calcular el rango actual en horas
        const currentRange = extremes.max - extremes.min;
        const currentRangeHours = currentRange / (1000 * 60 * 60);

        // Mover 50% del rango hacia atr�s
        const shift = currentRange * 0.5;
        const newMin = extremes.min - shift;
        const newMax = extremes.max - shift;

        // ?? VERIFICAR SI NECESITAMOS CARGAR M�S DATOS
        const reachingLimit = newMin <= extremes.dataMin;

        if (reachingLimit && !loadingMore && oldestDate) {


            // 📏 Calcular cuántas horas cargar (entre 1 y 4 según el rango actual)
            let hoursToLoadAuto;
            if (currentRangeHours <= 2) {
                hoursToLoadAuto = 1; // Rango pequeño: cargar 1 hora
            } else if (currentRangeHours <= 4) {
                hoursToLoadAuto = 2; // Rango medio: cargar 2 horas
            } else {
                hoursToLoadAuto = 4; // Rango grande: cargar 4 horas
            }

            try {
                setLoadingMore(true);
                setLoadingMessage(`🔄 Cargando ${hoursToLoadAuto} horas adicionales para navegación...`);

                // Calcular rango desde la fecha m�s antigua hacia atr�s
                const fechaFin = new Date(oldestDate);
                const fechaInicio = new Date(fechaFin.getTime() - (hoursToLoadAuto * 60 * 60 * 1000));

                const fechaF = fechaFin.toISOString().slice(0, 19);
                const fechaI = fechaInicio.toISOString().slice(0, 19);

                const varsString = varsDown.join(',');
                const dataSend = `${rig};${varsString};${fechaF};${fechaI}`;

                const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

                if (data && data.length > 0) {
                    // Combinar con datos existentes
                    const combinedData = data.concat(medidasGraficas);

                    // Actualizar fecha m�s antigua
                    let minTime = Infinity;
                    data.forEach(d => {
                        const timestamp = new Date(d.dt_mda_fch).getTime();
                        if (timestamp < minTime) minTime = timestamp;
                    });
                    const newOldestDate = new Date(minTime);
                    setOldestDate(newOldestDate);

                    // 🔄 CARGAR COMENTARIOS DE TODO EL RANGO VISIBLE (oldestDate hasta latestDate)
                    if (latestDate) {
                        cargarComentarios(newOldestDate, latestDate);
                    }

                    // Actualizar el estado con los datos combinados
                    setMedidasGraficas(combinedData);

                    // Procesar SOLO los datos nuevos (modo incremental)
                    processAdditionalData(data, varsDown, variablesMetadata);

                    // ?? Esperar un momento para que los datos se procesen
                    setTimeout(() => {
                        // Ahora s� navegar hacia atr�s con los nuevos datos
                        const updatedExtremes = xAxis.getExtremes();
                        const updatedNewMin = updatedExtremes.min - shift;
                        const updatedNewMax = updatedExtremes.max - shift;


                        xAxis.setExtremes(updatedNewMin, updatedNewMax, true, false);
                        updateCanGo();
                    }, 300);

                    setLoadingMessage('✅ Datos cargados correctamente');
                    setTimeout(() => setLoadingMessage(''), 2000);
                } else {
                    setLoadingMessage('⚠️ No hay más datos históricos');
                    setTimeout(() => setLoadingMessage(''), 3000);
                }

            } catch (error) {
                console.error('? Error cargando datos autom?ticos:', error);
                setLoadingMessage(' Error cargando datos adicionales'); setTimeout(() => setLoadingMessage(''), 3000);
            } finally {
                setLoadingMore(false);
                setLoadingMessage('');
            }

            return; // Salir aqu? para no hacer la navegaci?n inmediata
        }

        // Aplicar nuevos extremos manteniendo el rango
        xAxis.setExtremes(newMin, newMax, true, false);

        // Actualizar estado de botones
        updateCanGo();
    };

    // ?? NAVEGAR HACIA ADELANTE EN EL TIEMPO (manteniendo el rango actual)
    const shiftTimeForward = () => {
        if (!chartRef.current || !chartRef.current.chart) return;

        const chart = chartRef.current.chart;
        const xAxis = chart.xAxis[0];
        const extremes = xAxis.getExtremes();

        // Calcular el rango actual
        const currentRange = extremes.max - extremes.min;

        // Mover 50% del rango hacia adelante
        const shift = currentRange * 0.5;
        const newMin = extremes.min + shift;
        const newMax = extremes.max + shift;

        // Verificar que no nos pasemos del l�mite de datos disponibles
        const dataMax = extremes.dataMax;
        if (newMax > dataMax) {
            setLoadingMessage(' Ya est�s en el rango m�s reciente'); setTimeout(() => setLoadingMessage(''), 2000);
            return;
        }


        // Aplicar nuevos extremos manteniendo el rango
        xAxis.setExtremes(newMin, newMax, true, false);

        // Actualizar estado de botones
        updateCanGo();
    };

    // 🔘 ACTUALIZAR ESTADO DE BOTONES DE NAVEGACIÓN
    // 🔘 MEMOIZADO PARA EVITAR RECREACIONES
    const updateCanGo = useCallback(() => {
        if (!chartRef.current || !chartRef.current.chart) return;

        const chart = chartRef.current.chart;
        const xAxis = chart.xAxis[0];
        const extremes = xAxis.getExtremes();

        const canBack = true;// 🔘 SIEMPRE permitir ir hacia atrás (cargará datos automáticamente si es necesario)
        const canForward = extremes.max < extremes.dataMax - 1;// 🔘 Solo permitir adelante si hay datos disponibles

        // 👁️ DETECTAR SI USUARIO ESTÁ VIENDO DATOS EN VIVO
        // Usuario está "en vivo" si está dentro del 5% del rango más reciente
        const range = extremes.dataMax - extremes.dataMin;
        const threshold = extremes.dataMax - (range * 0.05);
        const viewingLive = extremes.max >= threshold;

        // 📥 DETECTAR SI PUEDE CARGAR MÁS DATOS
        // Solo permitir si está viendo cerca del inicio (dentro del 10% más antiguo)
        const thresholdOldest = extremes.dataMin + (range * 0.10);
        const canLoad = extremes.min <= thresholdOldest;

        // 🔑 USAR REFS EN LUGAR DE setState (evita re-renders)
        isViewingLiveRef.current = viewingLive;
        canGoBackRef.current = canBack;
        canGoForwardRef.current = canForward;
        canLoadMoreRef.current = canLoad;
    }, []); // Sin dependencias - solo usa refs

    // ?? PROCESAR SOLO DATOS ADICIONALES (sin reprocesar todo)
    const processAdditionalData = (newData, variables, metadata = variablesMetadata) => {
        // Filtrar variables v�lidas
        const validVariables = variables.filter(varId => varId !== '0' && varId !== 0);

        if (validVariables.length === 0 || !finalSeries || finalSeries.length === 0) {

            processInitialData(newData, variables, metadata);
            return;
        }

        // Procesar nuevos datos y agregarlos a las series existentes
        const newPoints = new Map();
        let pointsAdded = 0;

        // Inicializar Map para cada variable
        validVariables.forEach(varId => {
            newPoints.set(parseInt(varId), []);
        });

        // Agrupar nuevos datos por variable
        newData.forEach(record => {
            const timestamp = new Date(record.dt_mda_fch).getTime();
            const varId = parseInt(record.nb_var_idn);
            const value = parseFloat(record.nb_mda_val) || 0;

            if (newPoints.has(varId)) {
                newPoints.get(varId).push([timestamp, value]);
                pointsAdded++;
            }
        });

        // Agregar nuevos puntos a las series existentes y ordenar
        const updatedSeries = finalSeries.map(serie => {
            const varId = parseInt(serie.name.match(/Var(\d+)/)?.[1]);
            const additionalPoints = newPoints.get(varId) || [];

            if (additionalPoints.length > 0) {
                // Combinar datos existentes con nuevos y ordenar
                const combinedData = serie.data.concat(additionalPoints);
                combinedData.sort((a, b) => a[0] - b[0]);



                return {
                    ...serie,
                    data: combinedData
                };
            }

            return serie;
        });

        // Actualizar las series
        setFinalSeries(updatedSeries);

        // Recalcular horas disponibles con TODOS los datos
        const allTimestamps = [];
        updatedSeries.forEach(serie => {
            serie.data.forEach(point => allTimestamps.push(point[0]));
        });

        if (allTimestamps.length > 0) {
            let minTime = Infinity;
            let maxTime = -Infinity;

            allTimestamps.forEach(timestamp => {
                if (timestamp < minTime) minTime = timestamp;
                if (timestamp > maxTime) maxTime = timestamp;
            });

            const timeDiffMs = maxTime - minTime;
            const hoursAvailable = Math.ceil(timeDiffMs / (1000 * 60 * 60));

            setAvailableHours(hoursAvailable);
        }
    };

    // ?? PROCESAR DATOS INICIALES PARA MÚLTIPLES SERIES
    const processInitialData = (data, variables, metadata = variablesMetadata) => {
        setLoadingMessage(`?? Procesando ${data.length} registros...`);

        // Filtrar variables válidas (no '0')
        const validVariables = variables.filter(varId => varId !== '0' && varId !== 0);

        if (validVariables.length === 0) {
            setLoadingMessage('⚠️ No hay variables válidas configuradas');
            return;
        }

        // 🔑 CAMBIO CRÍTICO: Agrupar datos por POSICIÓN, no por varId
        // Esto permite variables duplicadas en diferentes posiciones
        const seriesData = new Map();
        let totalPointsProcessed = 0;

        // Inicializar series para cada POSICION valida (no por varId)
        validVariables.forEach((varId, index) => {
            const trackIndex = Math.floor(index / 2); // 2 variables por track
            const variableInTrack = index % 2; // 0 o 1 dentro del track

            // OBTENER METADATA DE LA VARIABLE
            const varMetadata = metadata.get(varId) || metadata.get(parseInt(varId));
            const varInfo = datosInferiores.find(v => v.nb_var_idn == varId);
            const descriptiveName = varMetadata?.name || varInfo?.vr_var_nom || varInfo?.tx_var_nme || `Var ${varId}`;
            const unit = varMetadata?.unit || varInfo?.vr_var_umd || varInfo?.tx_und_mda || '';

            // MANTENER nombre con codigo para identificacion interna (grilla inferior lo usa)
            const seriesName = `Var${varId} (T${trackIndex + 1}.${variableInTrack + 1})`;

            // Asegurar que no excedamos el numero de tracks disponibles
            const finalTrackIndex = Math.min(trackIndex, TRACKS_CONFIG.length - 1);

            // 🔑 USAR INDEX COMO KEY, no varId
            seriesData.set(index, {
                name: seriesName,
                yAxis: `yAxis-${index}`, // Usar ID string para coincidir con dynamicYAxes
                trackIndex: finalTrackIndex,
                varId: parseInt(varId), // Guardar varId para filtrar datos
                originalIndex: index, // 🔑 GUARDAR ÍNDICE ORIGINAL en varsDown
                data: [],
                color: getVariableColor(index),
                lineWidth: 2,
                shadow: false,
                step: false, // ? Líneas continuas (no escalonadas)
                connectNulls: true, // ? Conectar puntos aunque haya nulos
                dashStyle: 'Solid', // ? Siempre líneas sólidas
                marker: {
                    enabled: false, // ? Desactivar marcadores para líneas más limpias
                    radius: 2
                },
                // ?? AGREGAR CUSTOM PROPERTIES PARA EL TOOLTIP Y LEYENDAS
                custom: {
                    descriptiveName: descriptiveName,
                    unit: unit,
                    varId: varId,
                    trackIndex: finalTrackIndex // 📊 Para agrupar leyendas por track
                }
            });
        });

        // Procesar cada registro y asignarlo a TODAS las posiciones que tengan esa variable
        data.forEach(record => {
            const timestamp = new Date(record.dt_mda_fch).getTime();
            const recordVarId = parseInt(record.nb_var_idn);
            const value = parseFloat(record.nb_mda_val) || 0;

            // 🔑 BUSCAR TODAS LAS POSICIONES que tienen esta variable
            seriesData.forEach((series, positionIndex) => {
                if (series.varId === recordVarId) {
                    series.data.push([timestamp, value]);
                    totalPointsProcessed++;
                }
            });
        });

        // Convertir a array y ordenar datos por tiempo
        const processedSeries = Array.from(seriesData.values()).map(series => {
            const sortedData = series.data.sort((a, b) => a[0] - b[0]);
            const lastValue = sortedData.length > 0 ? sortedData[sortedData.length - 1][1] : null;

            // Crear nombre legible para la leyenda: "NombreVariable: valor unidad"
            const legendName = lastValue !== null
                ? `${series.custom.descriptiveName}: ${lastValue.toFixed(1)} ${series.custom.unit}`.trim()
                : `${series.custom.descriptiveName} ${series.custom.unit}`.trim();

            return {
                ...series,
                data: sortedData,
                custom: {
                    ...series.custom,
                    legendName: legendName // Nombre para mostrar en leyenda
                }
            };
        });

        // ?? Calcular horas de datos disponibles y fecha más antigua
        let hoursAvailable = 4; // Default
        if (data && data.length > 0) {
            // ? Optimizado para grandes vol�menes de datos - usar reduce en lugar de spread operator
            let minTime = Infinity;
            let maxTime = -Infinity;

            data.forEach(d => {
                const timestamp = new Date(d.dt_mda_fch).getTime();
                if (timestamp < minTime) minTime = timestamp;
                if (timestamp > maxTime) maxTime = timestamp;
            });

            const timeDiffMs = maxTime - minTime;
            hoursAvailable = Math.ceil(timeDiffMs / (1000 * 60 * 60)); // Convertir a horas
            setAvailableHours(hoursAvailable);

            // ?? Establecer la fecha m�s antigua para el bot�n "Cargar M�s"
            const oldestTimestamp = new Date(minTime);
            setOldestDate(oldestTimestamp);
        }

        // Estad�sticas de procesamiento
        const seriesStats = finalSeries.map(series => ({
            name: series.name,
            pointCount: series.data.length,
            yAxis: series.yAxis,
            color: series.color,
            firstPoint: series.data.length > 0 ? series.data[0] : null,
            lastPoint: series.data.length > 0 ? series.data[series.data.length - 1] : null
        }));

        //

        // ?? GENERAR EJES Y DINÁMICAMENTE - Uno por cada serie con metadata de BD
        const dynamicYAxes = processedSeries.map((serie, index) => {
            const trackIndex = serie.trackIndex || 0;
            const serieInTrack = index % 2; // 0 para primera serie del track, 1 para segunda

            // ?? Extraer variable ID de la serie
            const varId = serie.name.match(/Var(\d+)/)?.[1];
            const varMetadata = metadata.get(varId) || metadata.get(parseInt(varId));

            // ?? Determinar min/max basado en el estado useFixedAxes
            let axisMin, axisMax;
            if (useFixedAxes && varMetadata && varMetadata.min !== null && varMetadata.max !== null) {
                // Usar valores EXACTOS de la BD sin padding
                axisMin = varMetadata.min;
                axisMax = varMetadata.max;
            } else {
                // Usar valores autom�ticos
                axisMin = undefined;
                axisMax = undefined;
            }

            return {
                id: `yAxis-${index}`,
                // Posicionar en el track correspondiente
                width: '16%',
                left: `${TRACKS_CONFIG[trackIndex].left}`,
                top: '0%',
                height: '100%',
                offset: serieInTrack === 0 ? 0 : 20, // Primer eje pegado, segundo separado

                labels: {
                    style: {
                        color: serie.color || theme.chartFont,
                        fontSize: isMobileLandscape ? '7px' : '10px'
                    },
                    align: 'center',
                    x: 0,
                    y: 12, // Debajo del eje
                    verticalAlign: 'top'
                },

                // Estilo visual
                gridLineColor: theme.gridColor,
                gridLineWidth: 1,
                lineColor: serie.color || '#888888',
                lineWidth: 2,
                tickColor: serie.color || '#888888',
                tickWidth: 1,
                tickLength: 8,
                tickPosition: 'outside',

                // Configuraci�n
                opposite: false,
                reversed: false, // ? Corregido: no invertir ejes Y (valores mayores arriba)
                // ?? Si usamos ejes fijos de BD: NO ajustar a ticks para respetar min/max exactos
                startOnTick: useFixedAxes && varMetadata ? false : true,
                endOnTick: useFixedAxes && varMetadata ? false : true,
                alignTicks: false,
                showFirstLabel: true,
                showLastLabel: true,
                tickAmount: 5, // 5 ticks = 4 espacios/líneas de grid (ej: 0, 25, 50, 75, 100)

                // ?? Usar min/max fijos de BD o autom�ticos seg�n el estado
                min: axisMin,
                max: axisMax,
                softMin: undefined,
                softMax: undefined,
                minPadding: useFixedAxes && varMetadata ? 0 : 0.05, // Sin padding extra si usamos valores fijos
                maxPadding: useFixedAxes && varMetadata ? 0 : 0.05,

                /* // ?? L�nea de plot resaltada en el m�ximo para marcar l�mite del track
                plotLines: axisMax !== undefined ? [{
                    value: axisMax,
                    color: serie.color || '#ffffffff',
                    width: 2,
                    zIndex: 5,
                    dashStyle: 'Solid'
                }] : [] */
            };
        });

        //

        // ?? Extraer información de las series para display usando metadata
        const seriesDisplayInfo = processedSeries.map((serie, index) => {
            const varId = serie.name.match(/Var(\d+)/)?.[1];
            const varMetadata = metadata.get(varId) || metadata.get(parseInt(varId));
            const varInfo = datosInferiores.find(v => v.nb_var_idn == varId);
            const lastPoint = serie.data && serie.data.length > 0 ? serie.data[serie.data.length - 1][1] : null;

            // ?? Calcular min/max de los datos reales
            let dataMin = null, dataMax = null;
            if (serie.data && serie.data.length > 0) {
                const values = serie.data.map(point => point[1]);
                dataMin = Math.min(...values);
                dataMax = Math.max(...values);
            }

            return {
                name: serie.name,
                varId: varId, // 🔑 AGREGAR varId para identificar la serie
                variable: varMetadata?.name || varInfo?.tx_var_nme || `Var ${varId}`,
                unit: varMetadata?.unit || varInfo?.tx_und_mda || '',
                lastValue: lastPoint,
                color: serie.color,
                trackIndex: serie.trackIndex,
                // ?? Informaci�n de rangos
                configMin: varMetadata?.min || null,
                configMax: varMetadata?.max || null,
                dataMin: dataMin,
                dataMax: dataMax,
                alarmMin: varMetadata?.alarmMin || null,
                alarmMax: varMetadata?.alarmMax || null
            };
        });

        // ?? AGREGAR TRACK DEDICADO PARA COMENTARIOS AL FINAL DE dynamicYAxes
        // ?? NOTA: Las anotaciones de comentarios se agregan en la configuraci�n del chart
        const commentTrackYAxis = {
            id: 'commentTrack',
            width: '8%',  // Ancho para posicionar los comentarios
            left: '90%',  // Posición a la derecha
            top: '0%',
            height: '100%',
            offset: 0,
            // Completamente oculto
            visible: false,
            labels: { enabled: false },
            title: { text: null },
            gridLineWidth: 0,
            gridLineColor: 'transparent',
            lineWidth: 0, // Sin línea visible
            lineColor: 'transparent',
            tickLength: 0,
            tickWidth: 0,
            minorTickLength: 0,
            min: 0,
            max: 1,
            startOnTick: false,
            endOnTick: false,
            // ? EVITAR CUALQUIER INTERFERENCIA CON OTROS TRACKS
            opposite: false,
            reversed: false,
            type: 'linear',
            allowDecimals: false
        };

        // ? AGREGAR EL TRACK DE COMENTARIOS A dynamicYAxes
        const finalYAxesWithComments = [...dynamicYAxes, commentTrackYAxis];

        // ? ACTUALIZAR ESTADOS PARA QUE HighchartsReact RENDERICE AUTOMÁTICAMENTE
        setFinalSeries(processedSeries);
        setFinalYAxis(finalYAxesWithComments);
        setSeriesInfo(seriesDisplayInfo);
        setMedidasGraficas(data);

        // 🧹 LIMPIAR REFERENCIAS DE POSICIONES Y LEYENDAS AL CARGAR NUEVOS DATOS
        trackPositionsRef.current = {};
        legendTextsRef.current = [];

        // 🔑 LIMPIAR LOADING Y MENSAJE INMEDIATAMENTE
        setInternalLoading(false);
        setLoading(false);
        setLoadingMessage('');

        // 💾 GUARDAR EN CACHE después de procesar exitosamente (incluir TODOS los datos del gráfico)
        if (setCachedRigData && nbRef.current && rig) {
            setCachedRigData(rig, {
                ultimaFecha: new Date(nbRef.current),
                oldestDate: oldestDate,
                latestDate: latestDate,
                finalSeries: processedSeries,
                finalYAxis: finalYAxesWithComments,
                seriesInfo: seriesDisplayInfo,
                medidasGraficas: data,
                comentarios: comentarios, // 💬 Guardar comentarios en cache
                commentsData: commentsData, // 💬 Guardar commentsData en cache
                configGrillaIzq: null // Se puede agregar después si es necesario
            });
        }
        //

        // 🚀 INICIAR ACTUALIZACIONES EN VIVO DESPUÉS DE 5 SEGUNDOS
        // Dar tiempo para que el chart se renderice completamente
        setTimeout(() => {
            // Verificar que el chart esté listo
            if (processedSeries && processedSeries.length > 0) {
                startLiveUpdates();
            }
        }, 5000);
    };

    // ?? OBTENER COLOR PARA VARIABLE
    const getVariableColor = (index) => {
        return theme.seriesColors[index % theme.seriesColors.length];
    };

    // 🎨 CAMBIAR COLOR DE SERIE DINÁMICAMENTE
    const handleColorChange = (varsDownIndex, newColor) => {

        if (!chartRef.current?.chart) {
            console.error('❌ [ERROR] chartRef.current.chart no existe');
            return;
        }

        const chart = chartRef.current.chart;

        // 🔑 Buscar la serie que corresponde a este varsDownIndex
        const targetVarId = varsDown[varsDownIndex]; // Mantener como STRING

        // 🎯 Calcular trackIndex desde varsDownIndex (cada track tiene 2 posiciones)
        // varsDown: [T1.1, T1.2, T2.1, T2.2, T3.1, T3.2, T4.1, T4.2, T5.1, T5.2, -, -]
        //           [  0,    1,    2,    3,    4,    5,    6,    7,    8,    9, 10, 11]
        const targetTrackIndex = Math.floor(varsDownIndex / 2);

        // Comparar por varId Y trackIndex para evitar conflictos con series repetidas
        const serie = chart.series.find(s =>
            s.options.custom?.varId == targetVarId &&
            s.options.trackIndex === targetTrackIndex
        );

        if (serie) {

            // Probar con redraw=true para actualización inmediata
            serie.update({
                color: newColor,
                lineColor: newColor // Asegurar que lineColor también cambie
            }, true); // true = redraw inmediato

            // Actualizar seriesInfo para reflejar el cambio en la UI
            setSeriesInfo(prev => {
                const updated = prev.map((s) => {
                    // Comparar por varId Y trackIndex para actualizar solo la serie correcta
                    const match = String(s.varId) === String(targetVarId) && s.trackIndex === targetTrackIndex;
                    return match ? { ...s, color: newColor } : s;
                });
                return updated;
            });

            // 💾 GUARDAR EN LOCALSTORAGE si es tema custom
            if (themeMode === 'custom') {
                try {
                    const customColors = JSON.parse(localStorage.getItem('colorsCustom') || '{}');

                    // Mapear varsDownIndex a la propiedad correcta (grColor1_1, grColor1_2, etc.)
                    const colorPropertyMap = [
                        'grColor1_1', // varsDown[0] - Track 1, Variable 1
                        'grColor1_2', // varsDown[1] - Track 1, Variable 2
                        'grColor2_1', // varsDown[2] - Track 2, Variable 1
                        'grColor2_2', // varsDown[3] - Track 2, Variable 2
                        'grColor3_1', // varsDown[4] - Track 3, Variable 1
                        'grColor3_2', // varsDown[5] - Track 3, Variable 2
                        'grColor4_1', // varsDown[6] - Track 4, Variable 1
                        'grColor4_2', // varsDown[7] - Track 4, Variable 2
                        'grColor5_1', // varsDown[8] - Track 5, Variable 1
                        'grColor5_2', // varsDown[9] - Track 5, Variable 2
                        null,         // varsDown[10] - sin uso
                        null          // varsDown[11] - sin uso
                    ];

                    const colorProperty = colorPropertyMap[varsDownIndex];

                    if (colorProperty) {
                        customColors[colorProperty] = newColor;
                        localStorage.setItem('colorsCustom', JSON.stringify(customColors));

                        // NO disparar evento para evitar re-render que sobrescribe el cambio
                        // window.dispatchEvent(new Event('themeChanged'));
                    }
                } catch (error) {
                    console.error('❌ [ERROR] No se pudo guardar en localStorage:', error);
                }
            }
            // No es necesario redraw manual, serie.update(options, true) ya lo hace
        } else {
            console.error('❌ [ERROR] Serie no encontrada:', { varsDownIndex, targetVarId });
        }
    };

    // 🎨 ABRIR COLOR PICKER AL HACER CLIC EN EL BORDE (solo en tema custom)
    const handleBorderClick = (event, varsDownIndex) => {
        event.stopPropagation();

        // Solo permitir en tema custom
        if (themeMode !== 'custom') return;

        const rect = event.currentTarget.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const pickerHeight = 150; // Altura aproximada del picker

        // Calcular posición para que siempre sea visible
        let top = rect.top;
        let left = rect.right + 10; // A la derecha del borde

        // Si no cabe abajo, abrir hacia arriba
        if (top + pickerHeight > windowHeight) {
            top = Math.max(10, rect.bottom - pickerHeight);
        }

        // Si no cabe a la derecha, abrir a la izquierda
        if (left + 250 > window.innerWidth) {
            left = rect.left - 260;
        }

        setPickerPosition({ top, left });
        setSelectedSeriesIndex(varsDownIndex);
        setShowColorPicker(true);
    };

    // ?? MANEJAR CLICK EN EL GRAFICO PARA COMENTARIOS
    // ?? MEMOIZADO PARA EVITAR RECREACIONES
    const chartClickComments = useCallback((x, y, varIdn) => {

        // Guardar los datos del punto clickeado
        setXComentario({
            x: x,
            y: y,
            varIdn: varIdn
        });

        setShowModalComments(true);// Abrir el modal de comentarios
    }, []); // Sin dependencias - solo usa setters que no cambian

    // 🔄 MANEJAR CLICK EN NOMBRE DE VARIABLE PARA SELECTOR
    // 🚨 CALLBACK PARA ACTUALIZAR ALARMAS
    const handleAlarmUpdated = useCallback(async () => {
        // Recargar datos para actualizar las alarmas
        if (rig && varsDown && varsDown.length > 0) {
            try {
                // Consultar datos actualizados con alarmas (mismo formato que carga inicial)
                const dataSend = `${rig}:${user}`;
                const updatedData = await getTableByIdBd(dataSend, 'getvarsalarmgraph');

                if (updatedData && updatedData.length > 0) {
                    // Actualizar datosInferiores con las nuevas alarmas
                    const updatedDatosInferiores = [...datosInferiores];
                    updatedData.forEach(record => {
                        const index = varsDown.findIndex(v => v == record.nb_var_idn);
                        if (index !== -1) {
                            updatedDatosInferiores[index] = {
                                ...updatedDatosInferiores[index],
                                nb_alarm_min: record.nb_alarm_min,
                                nb_alarm_max: record.nb_alarm_max,
                                nb_alarm_idn: record.nb_alarm_idn
                            };
                        }
                    });
                    setDatosInferiores(updatedDatosInferiores);

                    // Actualizar seriesInfo con las nuevas alarmas
                    setSeriesInfo(prevInfo => {
                        return prevInfo.map((info, index) => {
                            const varId = varsDown[index];
                            const updatedRecord = updatedData.find(r => r.nb_var_idn == varId);
                            if (updatedRecord) {
                                return {
                                    ...info,
                                    alarmMin: updatedRecord.nb_alarm_min || null,
                                    alarmMax: updatedRecord.nb_alarm_max || null
                                };
                            }
                            return info;
                        });
                    });

                    // 🔄 ACTUALIZAR data-alarm-min y data-alarm-max en el DOM
                    setTimeout(() => {
                        updatedData.forEach(record => {
                            const varId = record.nb_var_idn;
                            const valueElements = document.querySelectorAll(`[data-var-id="${varId}"]`);
                            valueElements.forEach(element => {
                                element.setAttribute('data-alarm-min', record.nb_alarm_min || 'null');
                                element.setAttribute('data-alarm-max', record.nb_alarm_max || 'null');

                                // Actualizar color inmediatamente basado en las nuevas alarmas
                                const currentValue = parseFloat(element.textContent);
                                if (!isNaN(currentValue) && record.nb_alarm_min !== null && record.nb_alarm_max !== null) {
                                    if (currentValue < record.nb_alarm_min || currentValue > record.nb_alarm_max) {
                                        element.style.color = '#ff6b6b';
                                    } else {
                                        element.style.color = '#4ECDC4';
                                    }
                                }
                            });
                        });
                    }, 100);

                    // 🔄 FORZAR RE-RENDER para actualizar la grilla con nuevas alarmas
                    setForceRenderKey(prev => prev + 1);
                }
            } catch (error) {
                console.error('Error recargando alarmas:', error);
                setLoadingMessage(' Error al refrescar alarmas'); setTimeout(() => setLoadingMessage(''), 3000);
            }
        }
    }, [rig, user, varsDown, datosInferiores]);

    const handleOpenVarSelector = useCallback(async (varIndex, currentVarId, initialTab = 'variable') => {
        setSelectedVarIndex(varIndex);

        // Cargar variables disponibles desde props
        if (variables && variables.length > 0) {
            setAvailableVariables(variables);
        } else {
            console.warn('⚠️ No hay variables disponibles');
            setAvailableVariables([]);
        }

        // Cargar min/max actuales de datosInferiores y datos de alarma
        if (datosInferiores[varIndex]) {
            setCurrentVarMin(datosInferiores[varIndex].nb_tldvrb_min || 0);
            setCurrentVarMax(datosInferiores[varIndex].nb_tldvrb_max || 100);

            // 🚨 DATOS DE ALARMA
            setCurrentAlarmMin(datosInferiores[varIndex].nb_alarm_min || null);
            setCurrentAlarmMax(datosInferiores[varIndex].nb_alarm_max || null);
            setCurrentAlarmId(datosInferiores[varIndex].nb_alarm_idn || null);
        }

        setModalInitialTab(initialTab); // Tab personalizado
        setShowModalVarSelector(true);
    }, [variables, datosInferiores]);

    // 🚨 MANEJAR CLICK EN VALOR PARA CONFIGURAR ALARMA
    // 🚨 ABRIR MODAL DE VARIABLE SELECTOR EN TAB DE ALARMA (click en valor)
    const handleOpenAlarmModal = useCallback((varIndex) => {
        const varInfo = datosInferiores[varIndex];
        const varId = varsDown[varIndex];

        if (!varInfo || !varId || varId === '0') {
            setLoadingMessage(' No hay variable configurada'); setTimeout(() => setLoadingMessage(''), 2000);
            return;
        }

        // Abrir variable selector en el tab de alarma
        setSelectedVarIndex(varIndex);

        // Cargar variables disponibles desde props
        if (variables && variables.length > 0) {
            setAvailableVariables(variables);
        }

        // Cargar min/max actuales de datosInferiores y datos de alarma
        if (datosInferiores[varIndex]) {
            setCurrentVarMin(datosInferiores[varIndex].nb_tldvrb_min || 0);
            setCurrentVarMax(datosInferiores[varIndex].nb_tldvrb_max || 100);

            // 🚨 DATOS DE ALARMA
            setCurrentAlarmMin(datosInferiores[varIndex].nb_alarm_min || null);
            setCurrentAlarmMax(datosInferiores[varIndex].nb_alarm_max || null);
            setCurrentAlarmId(datosInferiores[varIndex].nb_alarm_idn || null);
        }

        setModalInitialTab('alarm'); // 🚨 Abrir en tab de alarma
        setShowModalVarSelector(true);
    }, [datosInferiores, varsDown, variables]);

    // 📋 HANDLERS PARA MODAL DE PLANTILLAS
    const handleOpenModalPlantilla = useCallback(() => {
        setShowModalPlantilla(true);
    }, []);

    const handleCloseModalPlantilla = useCallback(() => {
        setShowModalPlantilla(false);
    }, []);

    const handlePlantillaAplicada = useCallback(async () => {
        // Después de aplicar plantilla, recargar datos completamente
        try {
            setLoading(true);
            setLoadingMessage('🔄 Recargando datos con nueva plantilla...');

            // Limpiar estados de gráficas
            setVarsDown([]);
            setFinalSeries([]);
            setFinalYAxis([]);
            setSeriesInfo([]);
            setMedidasGraficas([]);
            setDataLoaded(false);

            // Limpiar estados de grilla izquierda
            setVariablesIzquierda(Array(6).fill('0'));
            setDatosIzquierda(Array(6).fill(''));

            // Esperar un momento para que la BD se actualice
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Reinicializar (esto carga tanto gráficas como grilla izquierda)
            await initializeChart();

            setLoadingMessage('✅ Plantilla aplicada correctamente');
            setTimeout(() => setLoadingMessage(''), 2000);
        } catch (error) {
            console.error('Error recargando después de aplicar plantilla:', error);
            setLoadingMessage('❌ Error recargando datos');
            setTimeout(() => setLoadingMessage(''), 3000);
        } finally {
            setLoading(false);
            setLoadingMessage('');
            setIsLoadingPlantilla(false); // Resetear estado del botón de plantillas
        }
    }, [initializeChart]);

    // 🔄 ACTUALIZAR VARIABLE EN BD Y REEMPLAZAR SERIE
    const updateVariableInMatriz = async (varIndex, newVarId, newMin, newMax) => {
        try {
            // Validar que tenemos los datos necesarios
            if (!datosInferiores[varIndex] || !datosInferiores[varIndex].nb_tldvrb_idn) {
                setLoadingMessage(' Error: configuraci�n no encontrada'); setTimeout(() => setLoadingMessage(''), 3000);
                return { success: false };
            }

            // Obtener metadata de la nueva variable
            const newVarMetadata = variablesMetadata.get(newVarId.toString()) || variablesMetadata.get(parseInt(newVarId));
            const newVarInfo = variables?.find(v => v.nb_var_idn == newVarId);
            const fecha = new Date();

            // Preparar datos para actualizar en BD usando min/max proporcionados
            const dataSend = {
                nb_tldvrb_idn: parseInt(datosInferiores[varIndex].nb_tldvrb_idn),
                nb_var_idn: parseInt(newVarId),
                nb_tldvrb_min: parseFloat(newMin),
                nb_tldvrb_max: parseFloat(newMax),
                vr_adt_usureg: user,
                dt_adt_fchreg: fecha.toISOString()
            };

            // Actualizar en BD
            const result = await updateTableBd('updatevarmatrizgraph', dataSend);

            if (result) {
                // Actualizar la metadata global con la nueva variable y sus min/max
                const updatedMetadata = new Map(variablesMetadata);
                updatedMetadata.set(newVarId.toString(), {
                    ...newVarMetadata,
                    min: parseFloat(newMin),
                    max: parseFloat(newMax),
                    name: newVarInfo?.vr_var_nom || newVarInfo?.tx_var_nme || `Variable ${newVarId}`,
                    unit: newVarInfo?.vr_var_umd || newVarInfo?.tx_und_mda || ''
                });
                setVariablesMetadata(updatedMetadata);

                return { success: true, updatedData: result };
            } else {
                setLoadingMessage(' Error al actualizar en base de datos'); setTimeout(() => setLoadingMessage(''), 3000);
                return { success: false };
            }

        } catch (error) {
            console.error('Error actualizando variable:', error);
            setLoadingMessage(' Error al actualizar variable'); setTimeout(() => setLoadingMessage(''), 3000);
            return { success: false };
        }
    };

    // 📊 OBTENER DATOS DE UNA VARIABLE EN EL RANGO ACTUAL
    const getDataForVariable = async (varId) => {
        try {
            // Usar el rango de fechas actual (oldestDate hasta latestDate)
            if (!oldestDate || !latestDate) {
                setLoadingMessage(' No hay rango de fechas'); setTimeout(() => setLoadingMessage(''), 2000);
                return [];
            }

            const fechaI = oldestDate.toISOString().slice(0, 19);
            const fechaF = latestDate.toISOString().slice(0, 19);

            const dataSend = `${rig};${varId};${fechaF};${fechaI}`;

            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            return data || [];

        } catch (error) {
            console.error('Error obteniendo datos de variable:', error);
            return [];
        }
    };

    // 🔄 REEMPLAZAR SOLO UNA SERIE SIN AFECTAR LAS DEMÁS
    const replaceSerieData = (varIndex, newVarId, newVarData, updatedDatosInferiores, newMin, newMax) => {
        // Obtener la serie actual que vamos a reemplazar
        const currentSeries = [...finalSeries];
        const currentYAxes = [...finalYAxis];
        const currentSeriesInfo = [...seriesInfo];

        // Encontrar el índice de la serie en finalSeries por yAxis
        // yAxis tiene formato "yAxis-N" donde N es el índice
        const expectedYAxisId = `yAxis-${varIndex}`;
        const seriesIndex = currentSeries.findIndex(s => s.yAxis === expectedYAxisId);

        if (seriesIndex === -1) {
            //console.error('No se encontró la serie a reemplazar. VarIndex:', varIndex, 'Buscando yAxis:', expectedYAxisId);
            return;
        }

        // Obtener metadata de la nueva variable con los nuevos min/max
        const varMetadata = variablesMetadata.get(newVarId.toString()) || variablesMetadata.get(parseInt(newVarId));
        const varInfo = updatedDatosInferiores[varIndex];
        const descriptiveName = varMetadata?.name || varInfo?.vr_var_nom || varInfo?.tx_var_nme || `Var ${newVarId}`;
        const unit = varMetadata?.unit || varInfo?.vr_var_umd || varInfo?.tx_und_mda || '';

        // Usar los nuevos min/max proporcionados
        const configMin = newMin !== null && newMin !== undefined ? parseFloat(newMin) : (varMetadata?.min || 0);
        const configMax = newMax !== null && newMax !== undefined ? parseFloat(newMax) : (varMetadata?.max || 100);

        // Procesar los nuevos datos
        const processedData = newVarData
            .map(record => [
                new Date(record.dt_mda_fch).getTime(),
                parseFloat(record.nb_mda_val) || 0
            ])
            .sort((a, b) => a[0] - b[0]);

        // Calcular trackIndex
        const trackIndex = Math.floor(varIndex / 2);
        const variableInTrack = varIndex % 2;

        // Actualizar la serie
        const seriesName = `Var${newVarId} (T${trackIndex + 1}.${variableInTrack + 1})`;
        currentSeries[seriesIndex] = {
            ...currentSeries[seriesIndex],
            name: seriesName,
            data: processedData,
            custom: {
                descriptiveName: descriptiveName,
                unit: unit,
                varId: newVarId
            }
        };

        // Actualizar el eje Y correspondiente si usamos ejes fijos
        if (useFixedAxes && configMin !== null && configMax !== null) {
            const range = configMax - configMin;
            const padding = range * 0.05;
            currentYAxes[varIndex] = {
                ...currentYAxes[varIndex],
                min: configMin - padding,
                max: configMax + padding
            };
        } else {
            // Ejes automáticos
            currentYAxes[varIndex] = {
                ...currentYAxes[varIndex],
                min: undefined,
                max: undefined
            };
        }

        // Calcular estadísticas de los nuevos datos
        let dataMin = null, dataMax = null;
        if (processedData.length > 0) {
            const values = processedData.map(point => point[1]);
            dataMin = Math.min(...values);
            dataMax = Math.max(...values);
        }

        const lastValue = processedData.length > 0 ? processedData[processedData.length - 1][1] : null;

        // Actualizar seriesInfo
        currentSeriesInfo[seriesIndex] = {
            name: seriesName,
            variable: descriptiveName,
            unit: unit,
            lastValue: lastValue,
            color: currentSeries[seriesIndex].color,
            trackIndex: trackIndex,
            configMin: configMin,
            configMax: configMax,
            dataMin: dataMin,
            dataMax: dataMax,
            alarmMin: varMetadata?.alarmMin || null,
            alarmMax: varMetadata?.alarmMax || null
        };

        // Actualizar estados
        setFinalSeries(currentSeries);
        setFinalYAxis(currentYAxes);
        setSeriesInfo(currentSeriesInfo);

        // 🎯 ACTUALIZAR GRÁFICO DE HIGHCHARTS DIRECTAMENTE (sin esperar re-render)
        if (chartRef.current && chartRef.current.chart) {
            const chart = chartRef.current.chart;
            const highchartsSerie = chart.series[seriesIndex];

            if (highchartsSerie) {
                // Actualizar datos de la serie
                highchartsSerie.setData(processedData, false);

                // Actualizar nombre de la serie
                highchartsSerie.update({
                    name: seriesName,
                    custom: {
                        descriptiveName: descriptiveName,
                        unit: unit,
                        varId: newVarId
                    }
                }, false);

                // Actualizar eje Y si usamos ejes fijos
                // Buscar el yAxis por su ID (puede no coincidir con varIndex)
                const yAxisId = expectedYAxisId;
                const yAxisIndex = chart.yAxis.findIndex(axis => axis.options.id === yAxisId);

                if (yAxisIndex !== -1) {
                    if (useFixedAxes && configMin !== null && configMax !== null) {
                        const range = configMax - configMin;
                        const padding = range * 0.05;
                        chart.yAxis[yAxisIndex].update({
                            min: configMin - padding,
                            max: configMax + padding
                        }, false);
                    } else {
                        chart.yAxis[yAxisIndex].update({
                            min: undefined,
                            max: undefined
                        }, false);
                    }
                }

                // Hacer un solo redraw al final
                chart.redraw();
            }
        }

        // 🔄 FORZAR RE-RENDER para actualizar la grilla
        setForceRenderKey(prev => prev + 1);

        // 🔄 ACTUALIZAR DOM de la grilla
        setTimeout(() => {
            // Actualizar todos los elementos con el nuevo varId
            const valueElements = document.querySelectorAll(`[data-var-id="${newVarId}"]`);
            valueElements.forEach(element => {
                if (lastValue !== null && lastValue !== undefined) {
                    element.textContent = lastValue.toFixed(1);

                    // Aplicar color de alarma si corresponde
                    const alarmMin = element.getAttribute('data-alarm-min');
                    const alarmMax = element.getAttribute('data-alarm-max');
                    if (alarmMin !== 'null' && alarmMax !== 'null' && alarmMin && alarmMax) {
                        const min = parseFloat(alarmMin);
                        const max = parseFloat(alarmMax);
                        if (!isNaN(min) && !isNaN(max)) {
                            if (lastValue < min || lastValue > max) {
                                element.style.color = '#ff6b6b';
                            } else {
                                element.style.color = '#4ECDC4';
                            }
                        }
                    } else {
                        element.style.color = '#4ECDC4';
                    }
                }
            });
        }, 150);
    };

    // ? ACTUALIZACIONES EN VIVO DESACTIVADAS (MODO DESARROLLO)
    // 🚀 FASE 1: POLLING INTELIGENTE CON ACTUALIZACIONES EN BACKGROUND
    const startLiveUpdates = () => {
        if (intervalRef.current) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
        }

        const smartPoll = async () => {

            // ⏸️ PAUSAR POLLING si usuario no está viendo la pestaña
            if (document.hidden) {
                intervalRef.current = setTimeout(smartPoll, 10000); // Revisar cada 10s (no cada 1s)
                return;
            }

            // ⏸️ PAUSAR POLLING si este rig NO es el tab activo (usar rigRef para evitar stale closure)
            if (finalActiveTabId && parseInt(rigRef.current) !== parseInt(finalActiveTabId)) {
                intervalRef.current = setTimeout(smartPoll, 10000); // Revisar cada 10s (no cada 1s)
                return;
            }

            if (isCatchingUpRef.current) {
                intervalRef.current = setTimeout(smartPoll, 1000); // Reintentar en 1s
                return;
            }

            try {
                const hadNewData = await updateLiveData();

                // 🔄 Intervalo fijo de 10 segundos (optimizado para servidor)
                // Los datos se agregan gradualmente cada 2s para animación fluida
                const pollingInterval = 10000; // 10 segundos

                if (hadNewData) {
                    setConsecutiveErrors(0); // Reset errores
                }

                // Programar siguiente actualización
                intervalRef.current = setTimeout(smartPoll, pollingInterval);

            } catch (error) {
                console.error('❌ Error en polling:', error);

                // Incrementar contador de errores
                const newErrorCount = consecutiveErrors + 1;
                setConsecutiveErrors(newErrorCount);

                // Backoff exponencial en caso de errores (hasta 60 segundos)
                const backoffInterval = Math.min(60000, 10000 * Math.pow(2, newErrorCount));

                intervalRef.current = setTimeout(smartPoll, backoffInterval);
            }
        };

        // Iniciar primer poll inmediatamente
        smartPoll();
    };

    // 🕐 SISTEMA DE CONTROL DE POLLING (Time Navigation System - Fase 1)

    /**
     * Pausar VISUALMENTE el polling de datos en vivo (pero continúa en segundo plano)
     * Se usa al entrar en modo 'exploring' o 'historical'
     */
    const pausePolling = useCallback(() => {
        // 💾 HACER BACKUP DE LOS DATOS EN VIVO ANTES DE CAMBIAR VISTA
        liveDataBackupRef.current = [...medidasGraficas]; // Deep copy del array
        liveSeriesBackupRef.current = JSON.parse(JSON.stringify(finalSeries)); // Deep copy de series
        liveOldestDateRef.current = oldestDate;
        liveLatestDateRef.current = latestDate;
        liveComentariosBackupRef.current = [...comentarios]; // Backup de comentarios
        // 🔑 NO PAUSAR EL POLLING - solo marcar que estamos en modo histórico
        // El polling continuará actualizando medidasGraficas y finalSeries en segundo plano
        setIsPollingPaused(true); // Esto evita que se renderice en el chart mientras estamos en histórico

    }, [medidasGraficas, finalSeries, oldestDate, latestDate, comentarios]);

    /**
     * Reanudar el polling de datos en vivo
     * Se usa al volver a modo 'live'
     */
    const resumePolling = useCallback(() => {
        // 🔑 IMPORTANTE: Actualizar PRIMERO los estados y refs para que el polling funcione
        setViewMode('live');
        viewModeRef.current = 'live'; // Actualizar ref inmediatamente

        setIsPollingPaused(false);

        setEnVivo(true);
        enVivoRef.current = true;

        // 🧹 LIMPIAR COMENTARIOS HISTÓRICOS Y RESTAURAR LOS DE VIVO
        if (liveComentariosBackupRef.current) {
            setComentarios(liveComentariosBackupRef.current);
        } else {
            setComentarios([]);
        }

        // 🔄 RESTAURAR DATOS EN VIVO DESDE BACKUP
        if (liveDataBackupRef.current && liveSeriesBackupRef.current) {
            // Restaurar estados
            setMedidasGraficas(liveDataBackupRef.current);
            setFinalSeries(liveSeriesBackupRef.current);
            setOldestDate(liveOldestDateRef.current);
            setLatestDate(liveLatestDateRef.current);

            // Forzar actualización del chart con los datos restaurados
            setTimeout(() => {
                if (chartRef.current?.chart) {
                    // Actualizar todas las series del chart
                    const chart = chartRef.current.chart;
                    liveSeriesBackupRef.current.forEach((serieData, index) => {
                        if (chart.series[index]) {
                            chart.series[index].setData(serieData.data, false); // false = no redraw yet
                        }
                    });

                    chart.redraw(); // Redraw una sola vez al final

                    // 🎯 AJUSTAR NAVIGATOR AL FINAL (datos más recientes) en modo EN VIVO
                    setTimeout(() => {
                        if (chart.xAxis?.[0]) {
                            const xAxis = chart.xAxis[0];
                            const dataMin = xAxis.dataMin;
                            const dataMax = xAxis.dataMax;

                            // Mostrar últimas 2 horas de datos en vivo
                            const twoHoursMs = 2 * 60 * 60 * 1000;
                            const viewStart = Math.max(dataMin, dataMax - twoHoursMs);

                            xAxis.setExtremes(viewStart, dataMax, true, false);
                        }
                    }, 300);
                }
            }, 100);

            // ℹ️ Los comentarios ya se restauraron del backup al inicio de resumePolling
            // No es necesario cargarlos de nuevo

            setLoadingMessage('✅ Datos en vivo restaurados');
            setTimeout(() => setLoadingMessage(''), 2000);
        } else {
            console.warn('⚠️ No hay backup disponible, los datos permanecen como están');
        }

        // Reiniciar polling para continuar recibiendo actualizaciones
        setEnVivo(true);
        enVivoRef.current = true;

        // 🔑 IMPORTANTE: Actualizar viewModeRef para que el polling funcione
        viewModeRef.current = 'live';

        if (varsDown.length > 0 && rig) {
            setTimeout(() => {
                startLiveUpdates();
            }, 1000);
        } else {
            console.warn('⚠️ No se puede reiniciar actualizaciones: varsDown o rig no válidos', { varsDown: varsDown.length, rig });
        }
    }, [varsDown, rig, cargarComentarios, startLiveUpdates]);

    /**
     * 🔒 Cerrar modal histórico y limpiar estados
     */
    const closeHistoricalModal = useCallback(() => {
        setShowHistoricalModal(false);
        setCommentSearchText('');
        setSelectedHistoricalComment(null);
    }, []);

    /**
     * 💬 Cargar comentarios del rango completo del rig (para búsqueda histórica)
     */
    const loadHistoricalComments = useCallback(async () => {
        if (!rigTransmissionStartDate) {
            console.warn('⚠️ No hay fecha de inicio de transmisión');
            return;
        }

        try {
            const fechaI = rigTransmissionStartDate.toISOString().slice(0, 19);
            const fechaF = new Date().toISOString().slice(0, 19);

            const dataSend = `${rig};${fechaF};${fechaI}`;
            const comentariosData = await getTableByIdBd(dataSend, 'getcommentsperiod');

            if (comentariosData && comentariosData.length > 0) {
                setHistoricalComments(comentariosData);
            } else {
                setHistoricalComments([]);
            }

        } catch (error) {
            console.error('Error cargando comentarios históricos:', error);
            setHistoricalComments([]);
        }
    }, [rig, rigTransmissionStartDate]);

    /**
     * 📍 Centrar búsqueda histórica en un comentario específico
     */
    const centerOnComment = useCallback((comment) => {
        // ⚠️ AJUSTE: La BD guarda hora local como UTC, necesitamos sumar 5 horas
        const commentDateUtc = new Date(comment.dt_com_fch);
        const commentDate = new Date(commentDateUtc.getTime() + (5 * 60 * 60 * 1000));
        const hours = historicalHours;

        // Calcular mitad del rango (horas antes y después)
        const hoursOffset = hours / 2;
        const centeredStartDate = new Date(commentDate.getTime() - (hoursOffset * 60 * 60 * 1000));

        // ⚠️ IMPORTANTE: datetime-local espera formato local sin zona horaria
        // Necesitamos formatear como YYYY-MM-DDTHH:mm usando la hora LOCAL (no UTC)
        const year = centeredStartDate.getFullYear();
        const month = String(centeredStartDate.getMonth() + 1).padStart(2, '0');
        const day = String(centeredStartDate.getDate()).padStart(2, '0');
        const hour = String(centeredStartDate.getHours()).padStart(2, '0');
        const minute = String(centeredStartDate.getMinutes()).padStart(2, '0');

        const localDateString = `${year}-${month}-${day}T${hour}:${minute}`;

        setTempHistoricalDate(localDateString);
        setSelectedHistoricalComment(comment);

    }, [historicalHours]);

    /**
     * Cargar rango histórico de datos
     * @param {Date} startDate - Fecha de inicio del periodo
     * @param {number} hours - Número de horas a cargar (4-24)
     */
    const loadHistoricalRange = useCallback(async (startDate, hours) => {
        // Validar que tengamos variables cargadas
        if (!varsDown || varsDown.length === 0 || varsDown.every(v => v === '0')) {
            console.error('❌ No hay variables configuradas para cargar');
            setLoadingMessage('⚠️ No hay variables configuradas. Por favor espera a que carguen los datos.');
            setTimeout(() => setLoadingMessage(''), 3000);
            return false;
        }

        // Validar fecha contra fecha de inicio de transmisión del rig
        if (rigTransmissionStartDate && startDate < rigTransmissionStartDate) {
            console.warn('⚠️ Fecha solicitada anterior a inicio de transmisión del rig');
            setLoadingMessage(`⚠️ No hay datos antes del ${rigTransmissionStartDate.toLocaleString()}`);
            setTimeout(() => setLoadingMessage(''), 3000);
            return false;
        }

        // Pausar polling
        pausePolling();

        // Cambiar a modo historical
        setViewMode('historical');

        const endDate = new Date(startDate.getTime() + (hours * 60 * 60 * 1000));
        // Mostrar loading
        setLoading(true);

        try {
            // ⚠️ IMPORTANTE: La BD guarda hora local como UTC
            // NO usar toISOString() porque convierte a UTC (suma 5 horas)
            // Necesitamos enviar la hora local en formato ISO

            // Función para convertir Date a string ISO local (sin convertir a UTC)
            const toLocalISOString = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hour = String(date.getHours()).padStart(2, '0');
                const minute = String(date.getMinutes()).padStart(2, '0');
                const second = String(date.getSeconds()).padStart(2, '0');
                return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
            };

            // Preparar query - usar hora LOCAL (no UTC)
            const fechaI = toLocalISOString(startDate);
            const fechaF = toLocalISOString(endDate);

            // Construir string de variables - varsDown YA es un array de IDs (strings)
            // igual que en loadMoreData (línea 2100)
            const varsString = varsDown.join(',');

            // ORDEN CORRECTO: rig;variables;fechaFinal;fechaInicial
            const dataSend = `${rig};${varsString};${fechaF};${fechaI}`;

            // Consultar datos históricos
            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            if (data && data.length > 0) {
                // 🧹 LIMPIAR DATOS Y SERIES EXISTENTES antes de cargar datos históricos
                // Limpiar estados de datos
                setMedidasGraficas([]);
                setFinalSeries([]);

                // Limpiar series del chart
                if (chartRef.current?.chart) {
                    const chart = chartRef.current.chart;

                    // Remover todas las series (incluyendo comentarios, que se recargarán)
                    while (chart.series.length > 0) {
                        chart.series[0].remove(false); // false = no redraw todavía
                    }
                }

                // 🔑 OBTENER PRIMER Y ÚLTIMO TIMESTAMP DE LOS DATOS REALES
                const timestamps = data.map(record => {
                    const timestamp = new Date(record.dt_mda_fch).getTime();
                    return timestamp;
                }).sort((a, b) => a - b);

                const firstDataTimestamp = timestamps[0];
                const lastDataTimestamp = timestamps[timestamps.length - 1];

                // Procesar datos históricos - varsDown YA es un array de IDs (strings)
                processInitialData(data, varsDown, variablesMetadata);

                // Actualizar fechas oldest/latest para el Navigator
                setOldestDate(new Date(firstDataTimestamp));
                setLatestDate(new Date(lastDataTimestamp));

                // 🎯 ESTABLECER EXTREMOS PARA MOSTRAR TODO EL RANGO CONSULTADO
                setTimeout(() => {
                    if (chartRef.current?.chart?.xAxis?.[0]) {
                        const xAxis = chartRef.current.chart.xAxis[0];
                        // Mostrar TODO el rango de datos históricos consultado
                        // Establecer extremos para mostrar TODO el rango completo
                        xAxis.setExtremes(firstDataTimestamp, lastDataTimestamp, true, false);
                    }
                }, 200);

                // Cargar comentarios del periodo (usar fechas SIN ajustar)
                await cargarComentarios(startDate, endDate);

                setLoadingMessage(`✅ Cargadas ${hours}h de datos históricos`);
                setTimeout(() => setLoadingMessage(''), 3000);
                return true;
            } else {
                console.warn('⚠️ No se encontraron datos en el rango solicitado');
                setLoadingMessage('⚠️ No hay datos disponibles para el periodo seleccionado');
                setTimeout(() => setLoadingMessage(''), 3000);
                return false;
            }

        } catch (error) {
            console.error('❌ Error cargando datos históricos:', error);
            setLoadingMessage('❌ Error al cargar datos históricos');
            setTimeout(() => setLoadingMessage(''), 3000);
            return false;
        } finally {
            setLoading(false);
        }
    }, [rig, varsDown, variablesMetadata, rigTransmissionStartDate, pausePolling, cargarComentarios]);

    // 📡 ACTUALIZAR DATOS EN VIVO (FASE 1 - INTELIGENTE)
    // 🔄 NOTA: Esta función se adapta automáticamente a cambios de variables porque:
    // 1. varsDownRef.current se sincroniza automáticamente con varsDown (useEffect línea 809)
    // 2. Cuando onSelectVariable actualiza varsDown, el ref se actualiza inmediatamente
    // 3. La siguiente consulta ya usará las nuevas variables sin necesidad de reiniciar polling
    const updateLiveData = async () => {
        try {
            // 🕐 VERIFICAR SI ESTAMOS EN MODO HISTÓRICO
            // Si estamos en modo histórico, CONTINUAR actualizando datos en segundo plano
            // pero NO renderizar en el chart
            const isHistoricalMode = viewModeRef.current === 'historical';

            if (viewModeRef.current === 'exploring') return false;

            // 🔑 Usar refs para evitar stale closures
            const currentRig = rigRef.current;
            const currentVarsDown = varsDownRef.current; // ✅ Siempre tiene las variables actuales

            if (!currentRig || currentVarsDown.length === 0) return false;

            // 🔑 CAMBIO IMPORTANTE: Consultar TODOS los datos desde el último timestamp conocido
            if (!nbRef.current) {
                // Primera consulta - usar getlivedatagraph
                const data = await getTableByIdBd(currentRig, 'getlivedatagraph');

                if (data && data.length > 0) {
                    const nuevaFecha = new Date(data[0].dt_mda_fch);
                    nbRef.current = nuevaFecha.getTime();

                    // 🔑 ACTUALIZAR REF Y ESTADO DEL PADRE
                    enVivoRef.current = true;
                    setEnVivo(true); // 🔑 SINCRONIZAR CON PADRE para que UI muestre "EN VIVO"

                    // ⚠️ SOLO RENDERIZAR SI NO ESTAMOS EN MODO HISTÓRICO
                    if (!isHistoricalMode) {
                        addLivePointsOptimized(data);
                    }

                    // 🔑 USAR REF en lugar de estado para latestDate (no causa re-render)
                    latestDateRef.current = nuevaFecha;

                    return true;
                }
                return false;
            }

            // 📅 Consultar datos desde el último timestamp hasta ahora
            const fechaF = new Date().toISOString().slice(0, 19); // Ahora
            const fechaI = new Date(nbRef.current + 1).toISOString().slice(0, 19); // Último timestamp + 1ms (evitar duplicados)

            // 🔑 COMBINAR variables de grilla inferior + variables de grilla izquierda
            const todasLasVariables = [...currentVarsDown];
            const currentVariablesIzquierda = variablesIzquierdaRef.current; // 🔑 Usar ref para evitar stale closure

            // Agregar variables de grilla izquierda que no sean '0' y no estén duplicadas
            if (currentVariablesIzquierda && currentVariablesIzquierda.length > 0) {
                currentVariablesIzquierda.forEach(varIzq => {
                    if (varIzq && varIzq !== '0' && !todasLasVariables.includes(varIzq)) {
                        todasLasVariables.push(varIzq);
                    }
                });
            }

            // Construir string de variables (incluye grilla inferior + grilla izquierda)
            const varsString = todasLasVariables.join(',');
            const dataSend = `${currentRig};${varsString};${fechaF};${fechaI}`;

            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            if (data && data.length > 0) {
                // 🔍 FILTRAR solo datos NUEVOS (timestamp > último conocido)
                const newData = data.filter(record => {
                    const recordTimestamp = new Date(record.dt_mda_fch).getTime();
                    return recordTimestamp > nbRef.current;
                });

                if (newData.length === 0) {
                    // No hay datos nuevos - verificar si taladro dejó de transmitir
                    enVivoRef.current = false;
                    setEnVivo(false); // 🔑 SINCRONIZAR CON PADRE para que UI muestre "STANDBY"
                    return false;
                }

                // 🔑 ACTUALIZAR REF Y ESTADO DEL PADRE
                enVivoRef.current = true;
                setEnVivo(true); // 🔑 SINCRONIZAR CON PADRE para que UI muestre "EN VIVO"

                // ⚠️ SOLO RENDERIZAR SI NO ESTAMOS EN MODO HISTÓRICO
                if (!isHistoricalMode) {
                    // 🔑 BLINDAJE INTERAPLICACIÓN: Actualizar cursor temporal inmediatamente con el máximo timestamp recibido
                    // Esto previene que consultas subsiguientes soliciten datos solapados mientras los puntos se agregan gradualmente.
                    const latestTimestampInBatch = Math.max(...newData.map(r => new Date(r.dt_mda_fch).getTime()));
                    if (!isNaN(latestTimestampInBatch) && latestTimestampInBatch > (nbRef.current || 0)) {
                        nbRef.current = latestTimestampInBatch;
                    }

                    // 🎯 AGREGAR PUNTOS GRADUALMENTE (distribución suave)
                    addLivePointsGradually(newData);

                    // 💾 ACTUALIZAR CACHE con los nuevos datos
                    if (updateCachedRigData) {
                        const nuevaLatestDate = new Date(latestTimestampInBatch);

                        // 📅 Actualizar latestDate estado para que recargarComentarios tenga la fecha correcta
                        setLatestDate(nuevaLatestDate);

                        updateCachedRigData(currentRig, {
                            ultimaFecha: nuevaLatestDate,
                            latestDate: nuevaLatestDate,
                            lastAccessed: Date.now()
                        });
                    }
                }

                return true;
            }

            // No hay datos - taladro en standby
            enVivoRef.current = false;
            setEnVivo(false); // 🔑 SINCRONIZAR CON PADRE para que UI muestre "STANDBY"
            return false;

        } catch (error) {
            console.error('❌ Error actualizando datos en vivo:', error);
            throw error; // Propagar para manejo en smartPoll
        }
    };

    // 🎯 AGREGAR PUNTOS GRADUALMENTE - Simular actualización fluida con blindaje de concurrencia
    const addLivePointsGradually = (allData) => {
        if (!allData || allData.length === 0) return;

        // 🛡️ Limpiar timeouts previos para evitar solapamientos concurrentes o ráfagas fuera de orden
        if (pendingTimeoutsRef.current && pendingTimeoutsRef.current.length > 0) {
            pendingTimeoutsRef.current.forEach(tId => clearTimeout(tId));
            pendingTimeoutsRef.current = [];
        }

        // Ordenar datos por timestamp (ascendente - más antiguos primero)
        const sortedData = [...allData].sort((a, b) => {
            return new Date(a.dt_mda_fch).getTime() - new Date(b.dt_mda_fch).getTime();
        });

        // Agrupar por timestamp único
        const dataByTimestamp = new Map();
        sortedData.forEach(record => {
            const timestamp = new Date(record.dt_mda_fch).getTime();
            if (!isNaN(timestamp)) {
                if (!dataByTimestamp.has(timestamp)) {
                    dataByTimestamp.set(timestamp, []);
                }
                dataByTimestamp.get(timestamp).push(record);
            }
        });

        const timestamps = Array.from(dataByTimestamp.keys()).sort((a, b) => a - b); // Ordenar cronológicamente

        // Adaptar delay: si hay muchos timestamps acumulados, compactar para que el lote termine antes del próximo poll (10s)
        const stepDelay = Math.min(2000, Math.floor(7000 / Math.max(1, timestamps.length)));

        // Agregar cada grupo de datos con delay
        timestamps.forEach((timestamp, index) => {
            const timeoutId = setTimeout(() => {
                // Verificar que seguimos en el mismo rig antes de agregar puntos
                if (rigRef.current !== rig) return;

                const dataGroup = dataByTimestamp.get(timestamp);
                addLivePointsOptimized(dataGroup);

                // Mantener cursor en el timestamp más alto procesado
                if (timestamp > (nbRef.current || 0)) {
                    nbRef.current = timestamp;
                }
                latestDateRef.current = new Date(nbRef.current);

                // 📅 Actualizar latestDate solo en el último grupo para sincronización
                if (index === timestamps.length - 1) {
                    setLatestDate(new Date(nbRef.current));
                }
            }, index * stepDelay);

            // Guardar el timeoutId para poder cancelarlo si cambia el rig o entra nuevo lote
            pendingTimeoutsRef.current.push(timeoutId);
        });
    };

    // � BULK UPDATE: Agregar grandes cantidades de datos de una sola vez (CATCH-UP)
    const addLivePointsBulk = (liveData) => {
        if (!liveData || liveData.length === 0) return;
        if (!chartRef.current?.chart) return;

        const chart = chartRef.current.chart;
        const currentSeries = finalSeriesRef.current;

        if (!currentSeries || currentSeries.length === 0) return;

        let totalPointsAdded = 0;

        currentSeries.forEach((serieInfo, serieIndex) => {
            const varId = serieInfo.custom?.varId || serieInfo.name.match(/Var(\d+)/)?.[1];
            if (!varId) return;

            const highchartsSerie = chart.series[serieIndex];
            if (!highchartsSerie) return;

            // Obtener datos existentes
            const existingData = highchartsSerie.data.map(point => [point.x, point.y]);

            // Filtrar y mapear nuevos puntos para esta variable
            const newPoints = liveData
                .filter(record => parseInt(record.nb_var_idn) === parseInt(varId))
                .map(record => [
                    new Date(record.dt_mda_fch).getTime(),
                    parseFloat(record.nb_mda_val) || 0
                ]);

            if (newPoints.length === 0) return;

            totalPointsAdded += newPoints.length;

            // 🔑 USAR addPoint() para insertar los puntos en orden
            // Ordenar los nuevos puntos por timestamp
            const sortedNewPoints = newPoints.sort((a, b) => a[0] - b[0]);

            // Agregar cada punto individualmente (garantiza inserción correcta en el timeline)
            sortedNewPoints.forEach((point, idx) => {
                highchartsSerie.addPoint(point, false, false, false);
            });
        });

        // Actualizar leyendas
        currentSeries.forEach((serieInfo, serieIndex) => {
            const varId = serieInfo.custom?.varId || serieInfo.name.match(/Var(\d+)/)?.[1];
            if (!varId) return;

            const highchartsSerie = chart.series[serieIndex];
            if (!highchartsSerie || highchartsSerie.data.length === 0) return;

            const lastPoint = highchartsSerie.data[highchartsSerie.data.length - 1];
            const lastValue = lastPoint?.y;
            if (lastValue === undefined) return;

            const descriptiveName = serieInfo.custom?.descriptiveName || `Var ${varId}`;
            const unit = serieInfo.custom?.unit || '';
            const newLegendName = `${descriptiveName}: ${lastValue.toFixed(1)} ${unit}`.trim();

            highchartsSerie.update({
                custom: { ...serieInfo.custom, legendName: newLegendName }
            }, false);
        });

        // UN SOLO REDRAW al final
        chart.redraw(false);

        // Actualizar grilla izquierda
        //updateGrillaIzquierda(liveData);

    };

    // �📈 AGREGAR PUNTOS EN VIVO - VERSIÓN OPTIMIZADA SIN RE-RENDER
    // 🔄 NOTA: Esta función usa addPoint() de Highcharts para evitar re-renders completos
    const addLivePointsOptimized = (liveData) => {

        if (!liveData || liveData.length === 0) return;

        // 🔑 USAR CHART REF PARA AGREGAR PUNTOS DIRECTAMENTE SIN RE-RENDER
        if (!chartRef.current || !chartRef.current.chart) return;

        const chart = chartRef.current.chart;
        const currentSeries = finalSeriesRef.current;

        // ⚠️ VALIDACIÓN: Asegurar que finalSeries exista
        if (!currentSeries || currentSeries.length === 0) return;

        // 🎯 AGREGAR PUNTOS DIRECTAMENTE A LAS SERIES DE HIGHCHARTS (SIN RE-RENDER)
        currentSeries.forEach((serieInfo, serieIndex) => {
            // 🔑 MEJORA: Usar custom.varId directamente (más robusto que regex)
            const varId = serieInfo.custom?.varId || serieInfo.name.match(/Var(\d+)/)?.[1];
            if (!varId) return;

            const newPoints = liveData
                .filter(record => parseInt(record.nb_var_idn) === parseInt(varId))
                .map(record => [
                    new Date(record.dt_mda_fch).getTime(),
                    parseFloat(record.nb_mda_val) || 0
                ]);

            if (newPoints.length > 0 && chart.series[serieIndex]) {
                const highchartsSerie = chart.series[serieIndex];

                // 🛡️ BLINDAJE HIGHCHARTS: Obtener el timestamp del último punto existente en la serie
                const existingData = highchartsSerie.data;
                const lastPoint = existingData && existingData.length > 0 ? existingData[existingData.length - 1] : null;
                const lastTimestamp = lastPoint ? lastPoint.x : -Infinity;

                // Filtrar solo puntos con timestamp válido y estrictamente posterior al último de la serie
                // Esto previene que la curva retroceda en el tiempo o genere lazos cruzados
                const validPoints = newPoints
                    .filter(point => !isNaN(point[0]) && point[0] > lastTimestamp)
                    .sort((a, b) => a[0] - b[0]);

                // Agregar cada punto usando el método addPoint de Highcharts
                validPoints.forEach(point => {
                    // addPoint(point, redraw=false, shift=false, animation=false)
                    // shift=true cuando hay más de 10000 puntos para mantener performance
                    const shouldShift = highchartsSerie.data.length >= 10000;
                    highchartsSerie.addPoint(point, false, shouldShift, false);
                });
            }
        });

        // 📝 ACTUALIZAR LEYENDA CON ÚLTIMOS VALORES (ANTES DEL REDRAW)
        currentSeries.forEach((serieInfo, serieIndex) => {
            const varId = serieInfo.custom?.varId || serieInfo.name.match(/Var(\d+)/)?.[1];
            if (!varId) return;

            // Obtener la serie de Highcharts
            const highchartsSerie = chart.series[serieIndex];
            if (!highchartsSerie || highchartsSerie.data.length === 0) return;

            // Obtener el último punto de la serie
            const lastPoint = highchartsSerie.data[highchartsSerie.data.length - 1];
            if (!lastPoint) return;

            const lastValue = lastPoint.y;
            const descriptiveName = serieInfo.custom?.descriptiveName || `Var ${varId}`;
            const unit = serieInfo.custom?.unit || '';

            // Crear nuevo nombre para la leyenda
            const newLegendName = `${descriptiveName}: ${lastValue.toFixed(1)} ${unit}`.trim();

            // Actualizar el nombre de la serie en la leyenda
            highchartsSerie.update({
                custom: {
                    ...serieInfo.custom,
                    legendName: newLegendName
                }
            }, false); // false = no redraw aún
        });

        // 🔄 REDRAW FINAL - Esto disparará el evento 'redraw' que actualizará las leyendas automáticamente
        chart.redraw();

        // 📈 AUTO-SCROLL: Mover el gráfico si el usuario está viendo el final de los datos
        const xAxis = chart.xAxis[0];
        const extremes = xAxis.getExtremes();
        const range = extremes.dataMax - extremes.dataMin;
        const threshold = extremes.dataMax - (range * 0.05); // 5% del rango
        const viewingLive = extremes.max >= threshold;

        if (viewingLive && extremes.dataMax > extremes.max) {
            // Usuario está viendo el final, mover el gráfico para mostrar nuevos datos
            const currentRange = extremes.max - extremes.min;
            const newMax = extremes.dataMax;
            const newMin = newMax - currentRange;
            xAxis.setExtremes(newMin, newMax, true, false);
        }

        // 📊 ACTUALIZAR GRILLA Y LEYENDAS SIMULTÁNEAMENTE CON LOS MISMOS VALORES
        currentSeries.forEach((serieInfo, serieIndex) => {
            const varId = serieInfo.custom?.varId || serieInfo.name.match(/Var(\d+)/)?.[1];
            if (!varId) return;

            // Buscar el último valor de esta variable en liveData
            const latestRecord = liveData
                .filter(record => parseInt(record.nb_var_idn) === parseInt(varId))
                .sort((a, b) => new Date(b.dt_mda_fch).getTime() - new Date(a.dt_mda_fch).getTime())[0];

            if (latestRecord) {
                const newValue = parseFloat(latestRecord.nb_mda_val);

                // 🎨 ACTUALIZAR LEYENDA CON EL MISMO VALOR QUE LA GRILLA (solo mobile landscape)
                if (isMobileLandscape) {
                    const trackIndex = serieInfo.trackIndex;
                    const seriesInTrack = currentSeries.filter(s => s.trackIndex === trackIndex);
                    const serieIndexInTrack = seriesInTrack.findIndex(s => s.custom?.varId === varId);

                    if (serieIndexInTrack !== -1) {
                        const textRefKey = `track${trackIndex}-serie${serieIndexInTrack}`;
                        const textElement = legendTextsRef.current[textRefKey];

                        if (textElement) {
                            const descriptiveName = serieInfo.custom?.descriptiveName || `Var ${varId}`;
                            const unit = serieInfo.custom?.unit || '';
                            const legendName = `${descriptiveName}: ${newValue.toFixed(1)} ${unit}`.trim();
                            textElement.attr({ text: legendName });
                        }
                    }
                }

                // Actualizar todos los elementos con data-var-id={varId}
                const valueElements = document.querySelectorAll(`[data-var-id="${varId}"]`);
                valueElements.forEach(element => {
                    const formattedValue = newValue.toFixed(1);
                    if (element.textContent !== formattedValue) {
                        element.textContent = formattedValue;

                        // Verificar alarmas y cambiar color si es necesario
                        const alarmMin = element.getAttribute('data-alarm-min');
                        const alarmMax = element.getAttribute('data-alarm-max');
                        if (alarmMin !== 'null' && alarmMax !== 'null' && alarmMin && alarmMax) {
                            const min = parseFloat(alarmMin);
                            const max = parseFloat(alarmMax);
                            if (!isNaN(min) && !isNaN(max)) {
                                if (newValue < min || newValue > max) {
                                    element.style.color = '#ff6b6b';
                                } else {
                                    element.style.color = '#4ECDC4';
                                }
                            }
                        } else {
                            // Sin alarmas, usar color por defecto
                            element.style.color = '#4ECDC4';
                        }
                    }
                });
            }
        });

        // 📊 ACTUALIZAR GRILLA IZQUIERDA - Mismo proceso que grilla inferior
        const currentVariablesIzquierda = variablesIzquierdaRef.current; // 🔑 Usar ref para evitar stale closure

        currentVariablesIzquierda.forEach((varId) => {
            if (!varId || varId === '0') return;

            // Buscar el último valor de esta variable en liveData
            const latestRecord = liveData
                .filter(record => parseInt(record.nb_var_idn) === parseInt(varId))
                .sort((a, b) => new Date(b.dt_mda_fch).getTime() - new Date(a.dt_mda_fch).getTime())[0];

            if (latestRecord) {
                const newValue = parseFloat(latestRecord.nb_mda_val);

                // Actualizar elementos de grilla izquierda con data-var-izq-id={varId}
                const valueElements = document.querySelectorAll(`[data-var-izq-id="${varId}"]`);

                valueElements.forEach((element, index) => {
                    const formattedValue = newValue.toFixed(2); // 2 decimales para grilla izquierda

                    if (element.textContent !== formattedValue) {
                        element.textContent = formattedValue;
                    }
                });
            } /* else {
                console.warn('⚠️ No se encontró registro para variable izquierda:', varId);
            } */
        });

    };

    // ?? CATCH-UP: Recuperar datos faltantes cuando usuario vuelve a la pesta�a
    const performCatchUp = async (secondsAway) => {
        try {
            isCatchingUpRef.current = true;
            const minutesAway = Math.floor(secondsAway / 60);

            const currentRig = rigRef.current;
            const currentVarsDown = varsDownRef.current;

            if (!currentRig || currentVarsDown.length === 0 || !nbRef.current) {
                isCatchingUpRef.current = false;
                return;
            }

            // 📊 GUARDAR RANGO DE VISUALIZACIÓN ACTUAL (ancho de ventana que tenía el usuario)
            let userViewRangeMs = null;
            let xAxis = null;
            if (chartRef.current?.chart?.xAxis?.[0]) {
                xAxis = chartRef.current.chart.xAxis[0];
                if (xAxis.min !== undefined && xAxis.max !== undefined) {
                    userViewRangeMs = xAxis.max - xAxis.min; // Guardar ancho de ventana
                }
            }
            // Mostrar mensaje de actualización
            setLoadingMessage(`🔄 Sincronizando ${minutesAway > 0 ? `${minutesAway} min` : `${secondsAway} seg`} de datos...`);

            // Calcular rango completo de datos faltantes
            const now = Date.now();
            const fechaF = new Date(now).toISOString().slice(0, 19); // Ahora
            const fechaI = new Date(nbRef.current + 1).toISOString().slice(0, 19); // Último dato + 1ms (igual que updateLiveData)

            // Construir query
            const varsString = currentVarsDown.join(',');
            const dataSend = `${currentRig};${varsString};${fechaF};${fechaI}`;

            // Consultar TODOS los datos faltantes
            const data = await getTableByIdBd(dataSend, 'getvarstldrofecha');

            if (data && data.length > 0) {
                // 🔑 USAR LA MISMA METODOLOGÍA QUE CARGA INICIAL
                // No combinar datos - simplemente procesar los nuevos y dejar que Highcharts maneje el merge

                // 1️⃣ Guardar vista actual
                const chart = chartRef.current?.chart;
                const xAxis = chart?.xAxis?.[0];
                let savedViewMin = null;
                let savedViewMax = null;

                if (xAxis && xAxis.min !== undefined && xAxis.max !== undefined) {
                    savedViewMin = xAxis.min;
                    savedViewMax = xAxis.max;
                } else if (userViewRangeMs) {
                    const latestTimestamp = Math.max(...data.map(r => new Date(r.dt_mda_fch).getTime()));
                    savedViewMax = latestTimestamp;
                    savedViewMin = latestTimestamp - userViewRangeMs;
                }

                // 2️⃣ Actualizar timestamp
                const latestTimestamp = Math.max(...data.map(r => new Date(r.dt_mda_fch).getTime()));
                nbRef.current = latestTimestamp;
                latestDateRef.current = new Date(latestTimestamp);
                setLatestDate(new Date(latestTimestamp));

                // 3️⃣ Agregar datos usando la función que SÍ funciona
                addLivePointsBulk(data);

                // 4️⃣ Restaurar vista extendida hasta el presente para incluir datos nuevos
                setTimeout(() => {
                    if (chart && xAxis && savedViewMin && savedViewMax) {
                        const ahora = Date.now();
                        // Extender hasta ahora para incluir todos los datos de catch-up
                        xAxis.setExtremes(savedViewMin, ahora, true, false);
                    }
                }, 300);

                setLoadingMessage(`✅ Sincronizado - ${data.length} puntos`);
                setTimeout(() => setLoadingMessage(''), 2000);

            } else {
                setLoadingMessage('ℹ️ Ya estás al día');
                setTimeout(() => setLoadingMessage(''), 1500);
            }

        } catch (error) {
            setLoadingMessage('❌ Error al sincronizar datos');
            setTimeout(() => setLoadingMessage(''), 3000);
        } finally {
            isCatchingUpRef.current = false;
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: !mobile && grillaIzqVisible && dataLoaded ? '8px' : '0',
            width: '98%',
            height: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {/* Left Sidebar Grid - GrillaIzqOpt - Solo renderizar cuando dataLoaded es true Y NO es mobile */}
            {dataLoaded && !mobile && (
                <GrillaIzqOpt
                    key={`grilla-izq-${rig}`}
                    rig={rig}
                    user={user}
                    uid={uid}
                    colors={colors}
                    mobile={mobile}
                    datosIzquierda={datosIzquierda}
                    variablesIzquierda={variablesIzquierda}
                    isVisible={grillaIzqVisible}
                    onToggleVisibility={() => setGrillaIzqVisible(!grillaIzqVisible)}
                    variables={variables}
                    onReloadPlantilla={handleReloadPlantillaIzquierda}
                    theme={theme}
                    themeMode={themeMode}
                />
            )}

            <div key={`graphs-${forceRenderKey}`}
                className={`live-graphs-container 
                    ${isMobileLandscape ? 'mobile-landscape' : ' '} 
                    ${isMobilePortrait ? 'mobile-portrait' : ' '} 
                    ${isMobileLandscape ? 'series-hidden' : ' '}`}
                style={{
                    backgroundColor: theme.bgColor,
                    color: theme.fontColor,
                    '--btn-bg-color': themeMode === 'light' ? '#e8e8e8' : '#2a2a2a',
                    '--card-bg-color': themeMode === 'light' ? '#f5f5f5' : '#0a0a0a',
                    width: !mobile && grillaIzqVisible && dataLoaded ? 'calc(100% - 158px)' : '100%', // Ajustar ancho SOLO en desktop
                    minWidth: 0,
                    overflow: 'hidden' // Evitar que este contenedor sobrepase
                }}
            >

                {isMobilePortrait && (
                    <>
                        {/* Flecha Izquierda */}
                        {currentTrackIndex > 0 && (
                            <button
                                className="mobile-track-arrow mobile-track-arrow-left"
                                onClick={() => setCurrentTrackIndex(prev => Math.max(0, prev - 1))}
                                title={`Track ${currentTrackIndex}`}
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>
                        )}

                        {/* Flecha Derecha */}
                        {/* En móvil vertical: excluir el último track (comentarios) */}
                        {currentTrackIndex < TRACKS_CONFIG.length - 2 && (
                            <button
                                className="mobile-track-arrow mobile-track-arrow-right"
                                onClick={() => setCurrentTrackIndex(prev => Math.min(TRACKS_CONFIG.length - 2, prev + 1))}
                                title={`Track ${currentTrackIndex + 2}`}
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        )}
                    </>
                )}

                <div className="live-graphs-info-panel" style={{ backgroundColor: theme.bgColor }}>
                    <div className="live-graphs-stats">
                        {/* Botones de Navegación Temporal */}
                        <button
                            onClick={shiftTimeBackward}
                            disabled={loadingMore}
                            className="live-graphs-btn live-graphs-btn-nav-back active"
                            title="Retroceder en el tiempo manteniendo el rango actual"
                        >
                            <i className="fa-solid fa-arrow-up-long"></i>
                        </button>

                        <button
                            onClick={shiftTimeForward}
                            disabled={loadingMore}
                            className="live-graphs-btn live-graphs-btn-nav-forward active"
                            title="Avanzar en el tiempo manteniendo el rango actual"
                        >
                            <i className="fa-solid fa-arrow-down-long"></i>
                        </button>

                        {/* Bot�n Cargar M�s Datos */}
                        <button
                            onClick={loadMoreData}
                            disabled={!oldestDate || loadingMore}
                            className={`live-graphs-btn live-graphs-btn-load-more ${loadingMore ? 'loading' : ''}`}
                            title={oldestDate ? `Cargar ${hoursToLoad} horas adicionales desde ${oldestDate.toLocaleString()}` : 'No hay datos cargados'}
                        >
                            {/* {loadingMore ? '🔄 Cargando...' : `🔽 Cargar ${hoursToLoad}h más`} */}
                            {loadingMore ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-angles-up"></i>}
                        </button>

                        {/* Botón para Activar/Desactivar Tooltip */}
                        <button
                            onClick={() => {
                                const newState = !tooltipEnabled;
                                setTooltipEnabled(newState);
                                // Si se activa, ponerlo en modo compartido por defecto
                                if (newState) setTooltipShared(true);
                            }}
                            className={`live-graphs-btn live-graphs-btn-tooltip ${tooltipEnabled ? 'active' : ''}`}
                            title={tooltipEnabled ? 'Tooltip activo' : 'Tooltip desactivado'}
                        >
                            {tooltipEnabled
                                ? <><i className="fa-solid fa-lightbulb"></i> {!mobile && 'ON'}</>
                                : <><i className="fa-regular fa-lightbulb"></i> {!mobile && 'OFF'}</>}
                        </button>

                        {/* Botón Individual/Compartido (solo visible si tooltip está activo) */}
                        {tooltipEnabled && !mobile && (
                            <button
                                onClick={() => setTooltipShared(!tooltipShared)}
                                className={`live-graphs-btn live-graphs-btn-tooltip ${tooltipShared ? 'active' : ''}`}
                                title={tooltipShared ? 'Tooltip compartido (todos los tracks)' : 'Tooltip individual (por track)'}
                            >
                                {tooltipShared ? '🔗 Compartido' : '🔗 Individual'}
                            </button>
                        )}

                        {/* Botón de Ejes Fijos/Automáticos */}
                        <button
                            onClick={() => {
                                // Cambiar el estado - el useEffect se encargará de reprocesar
                                setUseFixedAxes(!useFixedAxes);
                            }}
                            className={`live-graphs-btn live-graphs-btn-axes ${useFixedAxes ? 'fixed' : ''}`}
                        >
                            {useFixedAxes ? '📏 BD' : '📊 Auto'}
                        </button>

                        {/* Botón de Asignación de Plantillas */}
                        <button
                            onClick={handleOpenModalPlantilla}
                            className={`live-graphs-btn live-graphs-btn-plantilla ${isLoadingPlantilla ? 'disabled' : ''}`}
                            disabled={isLoadingPlantilla || internalLoading}
                            title="Asignación de Plantillas"
                        >
                            {isLoadingPlantilla ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-regular fa-object-group"></i>}
                        </button>

                        {/* 📅 Botón de Exploración Histórica */}
                        <button
                            onClick={() => {
                                if (viewMode === 'historical') {
                                    // Si está en modo histórico, volver a live
                                    resumePolling();
                                } else {
                                    // Abrir modal para seleccionar fecha
                                    setShowHistoricalModal(true);
                                    // Cargar comentarios históricos
                                    loadHistoricalComments();
                                }
                            }}
                            className={`live-graphs-btn live-graphs-btn-historical ${viewMode === 'historical' ? 'active' : ''}`}
                            title={viewMode === 'historical' ? 'Volver a EN VIVO' : 'Explorar Historial'}
                        >
                            {viewMode === 'historical'
                                ? <><i className="fa-solid fa-play"></i> {!mobile && 'VIVO'}</>
                                : <><i className="fa-solid fa-calendar-days"></i> {!mobile && 'Explorar'}</>
                            }
                        </button>

                    </div>
                </div>

                {/* 📅 MODAL DE SELECCIÓN DE FECHA HISTÓRICA */}
                {showHistoricalModal && (
                    <div className="modal-overlay " onClick={closeHistoricalModal}>
                        <div className="modal-content-historical" onClick={(e) => e.stopPropagation()}
                            style={{
                                backgroundColor: theme.bgColor,
                                color: theme.fontColor,
                                borderColor: theme.gridColor
                            }}>
                            <div className="modal-header-historical">
                                <h3>📅 Explorar Datos Históricos</h3>
                                <button
                                    onClick={closeHistoricalModal}
                                    className="modal-close-btn"
                                >
                                    <i className="fa-solid fa-times"></i>
                                </button>
                            </div>

                            <div className="modal-body-historical">
                                {/* Información del rango disponible */}
                                {rigTransmissionStartDate && (
                                    <div className="info-box" style={{
                                        backgroundColor: theme.cardBg,
                                        borderColor: theme.gridColor
                                    }}>
                                        <i className="fa-solid fa-info-circle"></i>
                                        <span>
                                            Datos disponibles desde: <strong>{rigTransmissionStartDate.toLocaleString()}</strong>
                                        </span>
                                    </div>
                                )}

                                {/* 💬 BUSCADOR DE COMENTARIOS */}
                                <div className="form-group-historical">
                                    <label>💬 Buscar en Comentarios: {historicalComments.length > 0 && `(${historicalComments.length} disponibles)`}</label>
                                    <input
                                        type="text"
                                        className="comment-search-input"
                                        placeholder="Buscar texto en comentarios..."
                                        value={commentSearchText}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            setCommentSearchText(newValue);
                                            if (newValue && historicalComments.length > 0) {
                                                const filtered = historicalComments.filter(c =>
                                                    c.nb_com_val?.toLowerCase().includes(newValue.toLowerCase())
                                                    // || c.vr_adt_usureg?.toLowerCase().includes(newValue.toLowerCase()) // Buscar por usuario (comentado)
                                                );
                                            }
                                        }}
                                        style={{
                                            backgroundColor: theme.cardBg,
                                            color: theme.fontColor,
                                            borderColor: theme.gridColor,
                                            padding: '8px 12px',
                                            borderRadius: '4px',
                                            width: '100%',
                                            border: '1px solid'
                                        }}
                                    />
                                </div>

                                {/* Lista de comentarios filtrados */}
                                {commentSearchText && historicalComments.length > 0 && (
                                    <div className="comments-results" style={{
                                        maxHeight: '350px',
                                        overflowY: 'auto',
                                        backgroundColor: theme.cardBg,
                                        borderRadius: '4px',
                                        border: `1px solid ${theme.gridColor}`,
                                        marginBottom: '15px'
                                    }}>
                                        {historicalComments
                                            .filter(comment => {
                                                const searchLower = commentSearchText.toLowerCase();
                                                return comment.nb_com_val?.toLowerCase().includes(searchLower);
                                                // || comment.vr_adt_usureg?.toLowerCase().includes(searchLower); // Buscar por usuario (comentado)
                                            })
                                            .slice(0, 10) // Máximo 10 resultados
                                            .map((comment, index) => (
                                                <div
                                                    key={index}
                                                    className="comment-result-item"
                                                    onClick={() => centerOnComment(comment)}
                                                    style={{
                                                        padding: '10px',
                                                        borderBottom: `1px solid ${theme.gridColor}`,
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedHistoricalComment === comment ? 'rgba(78, 205, 196, 0.2)' : 'transparent',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(78, 205, 196, 0.1)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedHistoricalComment === comment ? 'rgba(78, 205, 196, 0.2)' : 'transparent'}
                                                >
                                                    <div style={{ fontSize: '0.85em', color: theme.accentColor || '#4ECDC4', marginBottom: '4px' }}>
                                                        📅 {(() => {
                                                            const dateUtc = new Date(comment.dt_com_fch);
                                                            const dateLocal = new Date(dateUtc.getTime() + (5 * 60 * 60 * 1000));
                                                            return dateLocal.toLocaleString('es-ES', {
                                                                year: 'numeric',
                                                                month: '2-digit',
                                                                day: '2-digit',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                second: '2-digit',
                                                                hour12: false
                                                            });
                                                        })()}
                                                        {/* - <strong>{comment.vr_adt_usureg}</strong> */}
                                                    </div>
                                                    <div style={{ fontSize: '0.9em', color: theme.fontColor }}>
                                                        {comment.nb_com_val}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}

                                {commentSearchText && historicalComments.length === 0 && (
                                    <div style={{
                                        padding: '10px',
                                        textAlign: 'center',
                                        color: theme.fontColor,
                                        opacity: 0.6,
                                        fontSize: '0.9em'
                                    }}>
                                        ⏳ Cargando comentarios...
                                    </div>
                                )}

                                {commentSearchText && historicalComments.length > 0 &&
                                    historicalComments.filter(c =>
                                        c.nb_com_val?.toLowerCase().includes(commentSearchText.toLowerCase())
                                        // || c.vr_adt_usureg?.toLowerCase().includes(commentSearchText.toLowerCase()) // Buscar por usuario (comentado)
                                    ).length === 0 && (
                                        <div style={{
                                            padding: '10px',
                                            textAlign: 'center',
                                            color: theme.fontColor,
                                            opacity: 0.6,
                                            fontSize: '0.9em'
                                        }}>
                                            🔍 No se encontraron comentarios con "{commentSearchText}" (Buscados: {historicalComments.length})
                                        </div>
                                    )}

                                {/* Selector de fecha y hora */}
                                <div className="form-group-historical">
                                    <label>📆 Fecha y Hora:</label>
                                    <input
                                        type="datetime-local"
                                        className="datetime-input"
                                        max={new Date().toISOString().slice(0, 16)}
                                        min={rigTransmissionStartDate?.toISOString().slice(0, 16)}
                                        value={tempHistoricalDate || ''}
                                        onChange={(e) => {
                                            setTempHistoricalDate(e.target.value);
                                            setSelectedHistoricalComment(null); // Limpiar selección de comentario
                                        }}
                                        style={{
                                            backgroundColor: theme.cardBg,
                                            color: theme.fontColor,
                                            borderColor: theme.gridColor
                                        }}
                                    />
                                </div>

                                {/* Selector de horas a cargar */}
                                <div className="form-group-historical">
                                    <label>⏱️ Horas a cargar:</label>
                                    <div className="slider-container">
                                        <input
                                            type="range"
                                            min="4"
                                            max="24"
                                            step="1"
                                            value={historicalHours}
                                            onChange={(e) => setHistoricalHours(parseInt(e.target.value))}
                                            className="hours-slider"
                                        />
                                        <span className="slider-value">{historicalHours} horas</span>
                                    </div>
                                </div>

                                {/* Vista previa del rango */}
                                {tempHistoricalDate && (
                                    <div className="preview-box" style={{
                                        backgroundColor: theme.cardBg,
                                        borderColor: theme.gridColor
                                    }}>
                                        <strong style={{ color: theme.accentColor || '#4ECDC4' }}>🔍 Vista previa:</strong>
                                        <div className="preview-dates" style={{ color: theme.fontColor }}>
                                            <div>
                                                <i className="fa-solid fa-calendar-check" style={{ color: theme.accentColor || '#4ECDC4' }}></i>
                                                Inicio: {new Date(tempHistoricalDate).toLocaleString()}
                                            </div>
                                            <div>
                                                <i className="fa-solid fa-calendar-xmark" style={{ color: theme.accentColor || '#4ECDC4' }}></i>
                                                Fin: {new Date(new Date(tempHistoricalDate).getTime() + historicalHours * 60 * 60 * 1000).toLocaleString()}
                                            </div>
                                        </div>
                                        {selectedHistoricalComment && (
                                            <div style={{
                                                marginTop: '10px',
                                                padding: '8px',
                                                backgroundColor: 'rgba(78, 205, 196, 0.1)',
                                                borderRadius: '4px',
                                                fontSize: '0.9em'
                                            }}>
                                                📍 <strong>Centrado en comentario:</strong><br />
                                                "{selectedHistoricalComment.nb_com_val?.substring(0, 50)}{selectedHistoricalComment.nb_com_val?.length > 50 ? '...' : ''}"
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer-historical">
                                <button
                                    onClick={closeHistoricalModal}
                                    className="btn-cancel"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        if (tempHistoricalDate) {
                                            const selectedDate = new Date(tempHistoricalDate);
                                            loadHistoricalRange(selectedDate, historicalHours);
                                            closeHistoricalModal();
                                        }
                                    }}
                                    disabled={!tempHistoricalDate}
                                    className="btn-load"
                                >
                                    <i className="fa-solid fa-download"></i> Cargar Datos
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loading Message - Footer Fijo */}
                <div className={`live-graphs-loading-footer 
                    ${showFooter ? 'visible' : ''} 
                    ${loading ? 'processing' : 'info'}`}>
                    {loadingMessage}
                </div>

                {/* HighchartsReact Container */}
                <HighchartsReact
                    key={forceRenderKey}
                    highcharts={Highcharts}
                    constructorType={'stockChart'}
                    options={chartOptions}
                    ref={chartRef}
                    containerProps={{
                        className: 'live-graphs-chart-container'
                    }}
                />

                {/* 📊 GRILLA COMPACTA PARA MÓVIL VERTICAL */}
                {isMobilePortrait && (
                    <div className="mobile-compact-grid" style={{ backgroundColor: theme.bgColor }}>
                        {seriesInfo
                            .filter(serie => serie.trackIndex === currentTrackIndex)
                            .map((serie, idx) => {
                                // Encontrar el índice original de esta serie en seriesInfo
                                const originalIndex = seriesInfo.findIndex(s => s.name === serie.name);
                                return (
                                    <div
                                        key={`compact-${serie.varId}-${idx}`}
                                        className="mobile-compact-variable"
                                        style={{
                                            borderLeftColor: serie.color,
                                            backgroundColor: theme.bgColor,
                                            color: theme.fontColor,
                                            cursor: themeMode === 'custom' ? 'pointer' : 'default'
                                        }}
                                        onClick={(e) => {
                                            // Si es tema custom, el click en el borde abre el picker
                                            if (themeMode === 'custom' && e.target === e.currentTarget) {
                                                handleBorderClick(e, originalIndex);
                                            }
                                        }}
                                        title={themeMode === 'custom' ? 'Click en el borde para cambiar color' : ''}
                                    >

                                        {/* Nombre de la variable con el color de la gráfica */}
                                        <div
                                            className="mobile-compact-name"
                                            onClick={() => handleOpenVarSelector(originalIndex, varsDown[originalIndex])}
                                            title="Click para cambiar variable"
                                            style={{ color: serie.color }}
                                        >
                                            {serie.variable || serie.name}
                                        </div>

                                        {/* Línea de valores: Min + Valor+Unidad + Max (como desktop) */}
                                        <div
                                            className="mobile-compact-values-line"
                                            onClick={() => handleOpenAlarmModal(originalIndex)}
                                            title="Click para configurar alarma"
                                        >
                                            <span
                                                className="mobile-compact-min"
                                                onClick={(e) => { e.stopPropagation(); handleOpenVarSelector(originalIndex, varsDown[originalIndex], 'axes'); }}
                                                title="Min"
                                            >
                                                {serie.configMin != null ? serie.configMin.toFixed(0) : (serie.dataMin != null ? serie.dataMin.toFixed(0) : '0')}
                                            </span>

                                            <span className="mobile-compact-value-container">
                                                <span
                                                    className="mobile-compact-current"
                                                    data-var-id={varsDown[originalIndex]}
                                                    data-alarm-min={serie.alarmMin}
                                                    data-alarm-max={serie.alarmMax}
                                                    style={{
                                                        color: serie.alarmMin !== null && serie.alarmMax !== null && serie.lastValue != null &&
                                                            (serie.lastValue < serie.alarmMin || serie.lastValue > serie.alarmMax)
                                                            ? '#ff6b6b'
                                                            : theme.measureValueColor
                                                    }}
                                                >
                                                    {serie.lastValue !== null && serie.lastValue !== undefined
                                                        ? Number(serie.lastValue).toFixed(1)
                                                        : 'N/A'}
                                                </span>
                                                <span className="mobile-compact-unit">
                                                    {serie.unit || ''}
                                                </span>
                                            </span>

                                            <span
                                                className="mobile-compact-max"
                                                onClick={(e) => { e.stopPropagation(); handleOpenVarSelector(originalIndex, varsDown[originalIndex], 'axes'); }}
                                                title="Max"
                                            >
                                                {serie.configMax != null ? serie.configMax.toFixed(0) : (serie.dataMax != null ? serie.dataMax.toFixed(0) : 'N/A')}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                )}

                {!isMobilePortrait && !isMobileLandscape && (
                    /* 📊 INFORMACIÓN DE SERIES POR TRACK - SOLO DESKTOP */
                    <div className="live-graphs-series-panel"
                        style={{ backgroundColor: theme.bgColor, color: theme.fontColor }}>
                        {/* Contenedor interno con los mismos m�rgenes que el chart */}
                        <div className="live-graphs-series-inner">
                            {TRACKS_CONFIG.map((track, trackIndex) => {

                                // 🔑 Usar valores exactos de TRACKS_CONFIG para alineación perfecta
                                const trackConfig = TRACKS_CONFIG[trackIndex];
                                // Obtener ancho real del contenedor del chart para cálculo preciso
                                const chartContainer = document.querySelector('.highcharts-container');
                                const containerWidth = chartContainer ? chartContainer.offsetWidth : window.innerWidth;
                                const trackLeft = parseFloat(trackConfig.left); // Extraer número del porcentaje
                                const trackWidth = parseFloat(trackConfig.width); // Usar mismo ancho que track

                                // Obtener las 2 posiciones de variables para este track
                                const varIndex1 = trackIndex * 2;
                                const varIndex2 = trackIndex * 2 + 1;

                                // Buscar las series correspondientes
                                const serie1 = seriesInfo.find(s => s.trackIndex === trackIndex && s.name.includes(`T${trackIndex + 1}.1`));
                                const serie2 = seriesInfo.find(s => s.trackIndex === trackIndex && s.name.includes(`T${trackIndex + 1}.2`));

                                // Verificar si hay variables configuradas en estas posiciones
                                const hasVar1 = varsDown[varIndex1] && varsDown[varIndex1] !== '0';
                                const hasVar2 = varsDown[varIndex2] && varsDown[varIndex2] !== '0';

                                // Si no hay ninguna variable en este track, no mostrar nada
                                if (!hasVar1 && !hasVar2 && !serie1 && !serie2) return null;

                                // 🎯 USAR POSICIONES REALES DE LOS YAXIS (almacenadas por renderTrackLegends)
                                const trackPosition = trackPositionsRef.current[trackIndex];
                                const useRealPosition = trackPosition && trackPosition.chartWidth;

                                return (
                                    <div
                                        key={trackIndex}
                                        style={{
                                            position: 'absolute',
                                            left: useRealPosition
                                                ? `${(trackPosition.left / trackPosition.chartWidth) * 100}%`
                                                : `${2 + trackLeft + trackIndex * 0.2}%`,
                                            width: useRealPosition
                                                ? `${(trackPosition.width / trackPosition.chartWidth) * 100}%`
                                                : `${trackWidth}%`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {/* Variable 1 del track */}
                                        {hasVar1 && serie1
                                            ? (
                                                <div
                                                    style={{
                                                        marginBottom: '4px',
                                                        padding: '3px',
                                                        backgroundColor: theme.bgColor,
                                                        borderRadius: '4px',
                                                        borderLeft: `4px solid ${serie1.color}`
                                                    }}
                                                    onClick={(e) => {
                                                        // Si es tema custom y click en el borde (primeros 8px), abrir picker
                                                        if (themeMode === 'custom' && e.nativeEvent.offsetX <= 8) {
                                                            handleBorderClick(e, varIndex1);
                                                        }
                                                    }}
                                                    title={themeMode === 'custom' ? 'Click en el borde para cambiar color' : ''}
                                                >

                                                    <div className="variable-title live-graphs-serie-name"
                                                        style={{ color: serie1.color }}
                                                        onClick={() => handleOpenVarSelector(varIndex1, varsDown[varIndex1])}
                                                        title="Click para cambiar variable"
                                                    >
                                                        {serie1.variable}
                                                    </div>

                                                    <div className="variable-stats-mobile desktop-size">
                                                        <div
                                                            className="mobile-value-line"
                                                            onClick={() => handleOpenAlarmModal(varIndex1)}
                                                            title="Click para configurar alarma"
                                                        >
                                                            <span className="mobile-min"
                                                                onClick={(e) => { e.stopPropagation(); handleOpenVarSelector(varIndex1, varsDown[varIndex1], 'axes'); }}
                                                                title="Min">
                                                                {serie1.configMin != null ? serie1.configMin.toFixed(0) : (serie1.dataMin != null ? serie1.dataMin.toFixed(0) : 0)}
                                                            </span>
                                                            <span
                                                                className="mobile-value"
                                                                data-var-id={varsDown[varIndex1]}
                                                                data-alarm-min={serie1.alarmMin}
                                                                data-alarm-max={serie1.alarmMax}
                                                                style={{
                                                                    color: serie1.alarmMin !== null && serie1.alarmMax !== null && serie1.lastValue != null &&
                                                                        (serie1.lastValue < serie1.alarmMin || serie1.lastValue > serie1.alarmMax)
                                                                        ? '#ff6b6b'
                                                                        : '#4ECDC4'
                                                                }}
                                                            >
                                                                {serie1.lastValue != null ? serie1.lastValue.toFixed(1) : 'N/A'}
                                                            </span>
                                                            <span className='mobile-unit'> {serie1.unit || ''}</span>
                                                            <span className="mobile-max" onClick={(e) => { e.stopPropagation(); handleOpenVarSelector(varIndex1, varsDown[varIndex1], 'axes'); }} title="Max">
                                                                {serie1.configMax != null ? serie1.configMax.toFixed(0) : (serie1.dataMax != null ? serie1.dataMax.toFixed(0) : 'N/A')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                            : hasVar1
                                                ? (<div style={{
                                                    marginBottom: '4px',
                                                    padding: '3px',
                                                    backgroundColor: theme.bgColor,
                                                    borderRadius: '4px',
                                                    borderLeft: `4px solid #666666`
                                                }}
                                                >
                                                    <div className="variable-title live-graphs-serie-name"
                                                        style={{ color: '#666666' }}
                                                        onClick={() => handleOpenVarSelector(varIndex1, varsDown[varIndex1])}
                                                        title="Click para cambiar variable"
                                                    >
                                                        Variable sin datos (ID: {varsDown[varIndex1]})
                                                    </div>
                                                </div>
                                                )
                                                : null
                                        }

                                        {/* Variable 2 del track */}
                                        {hasVar2 && serie2 ? (
                                            <div
                                                style={{
                                                    marginBottom: '4px',
                                                    padding: '3px',
                                                    backgroundColor: theme.bgColor,
                                                    borderRadius: '4px',
                                                    borderLeft: `4px solid ${serie2.color}`
                                                }}
                                                onClick={(e) => {
                                                    // Si es tema custom y click en el borde (primeros 8px), abrir picker
                                                    if (themeMode === 'custom' && e.nativeEvent.offsetX <= 8) {
                                                        handleBorderClick(e, varIndex2);
                                                    }
                                                }}
                                                title={themeMode === 'custom' ? 'Click en el borde para cambiar color' : ''}
                                            >

                                                <div
                                                    className="variable-title live-graphs-serie-name"
                                                    style={{ color: serie2.color }}
                                                    onClick={() => handleOpenVarSelector(varIndex2, varsDown[varIndex2])}
                                                    title="Click para cambiar variable"
                                                >
                                                    {serie2.variable}
                                                </div>

                                                <div className="variable-stats-mobile desktop-size">
                                                    <div
                                                        className="mobile-value-line"
                                                        onClick={() => handleOpenAlarmModal(varIndex2)}
                                                        title="Click para configurar alarma"
                                                    >
                                                        <span className="mobile-min" onClick={(e) => { e.stopPropagation(); handleOpenVarSelector(varIndex2, varsDown[varIndex2], 'axes'); }} title="Min">
                                                            {serie2.configMin != null ? serie2.configMin.toFixed(0) : (serie2.dataMin != null ? serie2.dataMin.toFixed(0) : 0)}
                                                        </span>
                                                        <span
                                                            className="mobile-value"
                                                            data-var-id={varsDown[varIndex2]}
                                                            data-alarm-min={serie2.alarmMin}
                                                            data-alarm-max={serie2.alarmMax}
                                                            style={{
                                                                color: serie2.alarmMin !== null && serie2.alarmMax !== null && serie2.lastValue != null &&
                                                                    (serie2.lastValue < serie2.alarmMin || serie2.lastValue > serie2.alarmMax)
                                                                    ? '#ff6b6b'
                                                                    : '#4ECDC4'
                                                            }}
                                                        >
                                                            {serie2.lastValue != null ? serie2.lastValue.toFixed(1) : 'N/A'}
                                                        </span>
                                                        <span className='mobile-unit'> {serie2.unit || ''}</span>
                                                        <span className="mobile-max" onClick={(e) => { e.stopPropagation(); handleOpenVarSelector(varIndex2, varsDown[varIndex2], 'axes'); }} title="Max">
                                                            {serie2.configMax != null ? serie2.configMax.toFixed(0) : (serie2.dataMax != null ? serie2.dataMax.toFixed(0) : 'N/A')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : hasVar2 ? (
                                            <div
                                                style={{
                                                    padding: '5px',
                                                    backgroundColor: theme.bgColor,
                                                    borderRadius: '4px',
                                                    borderLeft: `4px solid #666666`
                                                }}
                                            >
                                                <div
                                                    className="variable-title live-graphs-serie-name"
                                                    style={{ color: '#666666' }}
                                                    onClick={() => handleOpenVarSelector(varIndex2, varsDown[varIndex2])}
                                                    title="Click para cambiar variable"
                                                >
                                                    Variable sin datos (ID: {varsDown[varIndex2]})
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ?? MODAL DE COMENTARIOS */}
                {showModalComments && (
                    <ModalCommentsOpt
                        show={showModalComments}
                        handleClose={() => setShowModalComments(false)}
                        xComentario={xComentario}
                        rig={rig}
                        user={user}
                        theme={theme}
                        recargarComentarios={async () => {
                            // Recargar comentarios del per�odo visible actual
                            if (chartRef.current && chartRef.current.chart) {
                                const chart = chartRef.current.chart;
                                const xAxis = chart.xAxis[0];
                                const extremes = xAxis.getExtremes();

                                // 🔍 Usar el máximo entre extremes.max y latestDateRef.current
                                // para asegurar que incluimos los datos más recientes
                                const maxDate = latestDateRef.current ?
                                    new Date(Math.max(extremes.max, latestDateRef.current.getTime())) :
                                    new Date(extremes.max);

                                const minDate = new Date(extremes.min);

                                await cargarComentarios(minDate, maxDate);
                            }
                        }}
                    />
                )}

                {/* 🔄 MODAL SELECTOR DE VARIABLES */}
                {showModalVarSelector && (
                    <ModalVarSelectorOpt
                        show={showModalVarSelector}
                        handleClose={() => setShowModalVarSelector(false)}
                        selectedVarIndex={selectedVarIndex}
                        availableVariables={availableVariables}
                        currentVarId={selectedVarIndex !== null ? varsDown[selectedVarIndex] : null}
                        theme={theme}
                        currentVariableName={selectedVarIndex !== null && datosInferiores[selectedVarIndex] ? datosInferiores[selectedVarIndex].vr_var_nom : null}
                        currentMin={currentVarMin}
                        currentMax={currentVarMax}
                        currentAlarmMin={currentAlarmMin}
                        currentAlarmMax={currentAlarmMax}
                        currentAlarmId={currentAlarmId}
                        rig={rig}
                        uid={uid}
                        user={user}
                        initialTab={modalInitialTab}
                        onAlarmUpdated={handleAlarmUpdated}
                        onSelectVariable={async (varIndex, newVarId, newMin, newMax) => {
                            setShowModalVarSelector(false);
                            setLoadingMessage('🔄 Actualizando variable en BD...');

                            try {
                                // PASO 1: Actualizar en BD con nuevos min/max
                                const result = await updateVariableInMatriz(varIndex, newVarId, newMin, newMax);

                                if (!result.success) {
                                    setLoadingMessage('');
                                    return;
                                }

                                setLoadingMessage('📊 Cargando datos de la nueva variable...');

                                // PASO 2: Obtener datos de la nueva variable en el rango actual
                                const newVarData = await getDataForVariable(newVarId);

                                if (!newVarData || newVarData.length === 0) {
                                    setLoadingMessage(' No hay datos disponibles'); setTimeout(() => setLoadingMessage(''), 2000);
                                }

                                setLoadingMessage('🔧 Actualizando configuración...');

                                // PASO 3: Actualizar varsDown
                                // 🔄 IMPORTANTE: Al actualizar varsDown, el useEffect en línea 809 sincroniza
                                // automáticamente varsDownRef.current, lo que hace que updateLiveData()
                                // empiece a consultar la nueva variable en el siguiente polling (10s)
                                const updatedVarsDown = [...varsDown];
                                updatedVarsDown[varIndex] = newVarId.toString();
                                setVarsDown(updatedVarsDown);

                                // PASO 4: Obtener la info completa de la nueva variable de las props
                                const variableCompleta = variables?.find(v => v.nb_var_idn == newVarId);

                                // PASO 5: Actualizar datosInferiores con TODA la info (merge BD + props) + nuevos min/max
                                const updatedDatosInferiores = [...datosInferiores];
                                updatedDatosInferiores[varIndex] = {
                                    ...updatedDatosInferiores[varIndex],
                                    ...result.updatedData,
                                    ...variableCompleta,
                                    nb_var_idn: newVarId,
                                    nb_tldvrb_min: newMin,
                                    nb_tldvrb_max: newMax,
                                    vr_var_nom: variableCompleta?.vr_var_nom || result.updatedData?.vr_var_nom,
                                    vr_var_umd: variableCompleta?.vr_var_umd || result.updatedData?.vr_var_umd,
                                    tx_var_nme: variableCompleta?.tx_var_nme || result.updatedData?.tx_var_nme,
                                    tx_und_mda: variableCompleta?.tx_und_mda || result.updatedData?.tx_und_mda
                                };
                                setDatosInferiores(updatedDatosInferiores);
                                setLoadingMessage('? Reemplazando serie...');

                                // PASO 6: Actualizar medidasGraficas (remover datos viejos, agregar nuevos)
                                const oldVarId = varsDown[varIndex];
                                const updatedMedidasGraficas = [
                                    ...medidasGraficas.filter(m => parseInt(m.nb_var_idn) !== parseInt(oldVarId)),
                                    ...newVarData
                                ];
                                setMedidasGraficas(updatedMedidasGraficas);

                                // PASO 7: Reemplazar SOLO la serie afectada (sin reprocesar todo) + nuevos ejes
                                setTimeout(() => {
                                    replaceSerieData(varIndex, newVarId, newVarData, updatedDatosInferiores, newMin, newMax);
                                    setLoadingMessage('✅ Variable actualizada correctamente');
                                    setTimeout(() => setLoadingMessage(''), 2000);
                                }, 100);

                            } catch (error) {
                                console.error('Error en onSelectVariable:', error);
                                setLoadingMessage(' Error al actualizar variable'); setTimeout(() => setLoadingMessage(''), 3000);
                                setLoadingMessage('');
                            }
                        }}
                    />
                )}

                {/* 📋 MODAL DE PLANTILLAS */}
                {showModalPlantilla && (
                    <ModalPlantillaOpt
                        rig={rig}
                        setRig={setRig}
                        datosInferiores={datosInferiores}
                        setDatosInferiores={setDatosInferiores}
                        setIsLoading={setIsLoadingPlantilla}
                        tipo={'graph'}
                        show={showModalPlantilla}
                        theme={theme}
                        handleClose={handleCloseModalPlantilla}
                        uid={uid}
                        user={user}
                        variables={variables}
                        onPlantillaAplicada={handlePlantillaAplicada}
                    />
                )}

                {/* 💬 MODAL DE DETALLE DE COMENTARIO */}
                <ModalCommentDetail
                    show={showCommentDetail}
                    comment={selectedComment}
                    user={user}
                    tipo={tipo}
                    onClose={() => setShowCommentDetail(false)}
                    onCommentUpdated={handleCommentUpdated}
                    theme={theme}
                />

                {/* ✅ DIALOG DE CONFIRMACIÓN */}
                <ConfirmDialog />

                {/* 🎨 COLOR PICKER FLOTANTE */}
                {showColorPicker && selectedSeriesIndex !== null && (
                    <div
                        style={{
                            position: 'fixed',
                            top: pickerPosition.top,
                            left: pickerPosition.left,
                            zIndex: 10000,
                            backgroundColor: theme.bgColor,
                            border: `2px solid ${theme.gridColor}`,
                            borderRadius: '8px',
                            padding: '15px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                    >
                        <div style={{ marginBottom: '10px', color: theme.fontColor, fontWeight: 'bold' }}>
                            Seleccionar Color - {varsDown[selectedSeriesIndex]}
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5, 1fr)',
                            gap: '8px',
                            width: '220px'
                        }}>
                            {[
                                '#FF6B6B', // Rojo
                                '#4ECDC4', // Turquesa
                                '#45B7D1', // Azul
                                '#FFA07A', // Salmon
                                '#98D8C8', // Verde menta
                                '#FFD93D', // Amarillo
                                '#C7CEEA', // Lavanda
                                '#FF9FF3', // Rosa
                                '#95E1D3', // Aqua
                                '#F38181'  // Coral
                            ].map((color) => {
                                const currentColor = seriesInfo.find(s => s.varId == varsDown[selectedSeriesIndex])?.color || '#FF6B6B';
                                const isSelected = currentColor.toLowerCase() === color.toLowerCase();
                                return (
                                    <div
                                        key={color}
                                        onClick={() => handleColorChange(selectedSeriesIndex, color)}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            backgroundColor: color,
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            border: isSelected ? '3px solid #fff' : '2px solid transparent',
                                            boxShadow: isSelected ? '0 0 8px rgba(255,255,255,0.5)' : '0 2px 4px rgba(0,0,0,0.2)',
                                            transition: 'all 0.2s',
                                            transform: isSelected ? 'scale(1.1)' : 'scale(1)'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.transform = 'scale(1.15)';
                                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                                            }
                                        }}
                                        title={color}
                                    />
                                );
                            })}
                        </div>
                        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => setShowColorPicker(false)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#4ECDC4',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                )}

                {/* Overlay para cerrar el color picker al hacer clic fuera */}
                {showColorPicker && (
                    <div
                        onClick={() => setShowColorPicker(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 9999,
                            backgroundColor: 'transparent'
                        }}
                    />
                )}

                {/* 🗂️ MODAL DE SELECCIÓN DE RIGS (solo desktop) */}
                {!mobile && finalShowRigSelector && (
                    <>
                        <div style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10001,
                            backgroundColor: theme.bgColor,
                            padding: '20px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                            border: `2px solid ${theme.gridColor}`,
                            minWidth: '350px',
                            maxWidth: '500px'
                        }}>
                            <div style={{
                                marginBottom: '15px',
                                color: theme.fontColor,
                                fontWeight: 'bold',
                                fontSize: '16px',
                                textAlign: 'center'
                            }}>
                                <i className="fa-solid fa-plus-circle me-2"></i>
                                Seleccionar Rig para nueva pestaña
                            </div>

                            <div style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                marginBottom: '15px'
                            }}>
                                {/* Lista real de rigs filtrada */}
                                {rigs && rigs.length > 0 ? (
                                    rigs
                                        .filter(rigItem => {
                                            // Filtrar rigs que ya están abiertos
                                            const isAlreadyOpen = finalOpenTabs.some(tab =>
                                                parseInt(tab.rigId) === parseInt(rigItem.nb_tldro_idn)
                                            );
                                            return !isAlreadyOpen;
                                        })
                                        .sort((a, b) => {
                                            // Primero los que transmiten, luego los que no
                                            if (a.tx && !b.tx) return -1;
                                            if (!a.tx && b.tx) return 1;
                                            return 0;
                                        })
                                        .map(rigItem => (
                                            <div
                                                key={rigItem.nb_tldro_idn}
                                                onClick={() => handleSelectRigFromModal(rigItem.nb_tldro_idn)}
                                                style={{
                                                    padding: '12px',
                                                    marginBottom: '8px',
                                                    backgroundColor: theme.chartBg,
                                                    border: `1px solid ${theme.gridColor}`,
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = theme.gridColor;
                                                    e.currentTarget.style.transform = 'translateX(4px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = theme.chartBg;
                                                    e.currentTarget.style.transform = 'translateX(0)';
                                                }}
                                            >
                                                {/* Indicador de transmisión */}
                                                <i className="fa-solid fa-circle"
                                                    style={{
                                                        color: rigItem.tx ? '#28a745' : '#dc3545',
                                                        fontSize: '10px'
                                                    }}></i>

                                                {/* Info del rig */}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontWeight: 'bold',
                                                        color: theme.fontColor,
                                                        marginBottom: '4px'
                                                    }}>
                                                        {rigItem.rig || rigItem.vr_tldro_nom || `Rig ${rigItem.nb_tldro_idn}`}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: theme.fontColor,
                                                        opacity: 0.7
                                                    }}>
                                                        {rigItem.pozo || 'Sin pozo'}
                                                    </div>
                                                </div>

                                                {/* Estado de transmisión */}
                                                <div style={{
                                                    fontSize: '11px',
                                                    color: rigItem.tx ? '#28a745' : '#dc3545',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {rigItem.tx ? 'Transmitiendo' : 'Sin transmisión'}
                                                </div>
                                            </div>
                                        ))
                                ) : (
                                    <div style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: theme.fontColor,
                                        opacity: 0.7
                                    }}>
                                        No hay rigs disponibles para agregar
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    onClick={() => finalSetShowRigSelector(false)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: theme.chartBg,
                                        color: theme.fontColor,
                                        border: `1px solid ${theme.gridColor}`,
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>

                        {/* Overlay para cerrar el modal al hacer clic fuera */}
                        <div
                            onClick={() => finalSetShowRigSelector(false)}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 10000,
                                backgroundColor: 'rgba(0,0,0,0.5)'
                            }}
                        />
                    </>
                )}

            </div>
        </div >
    );
};

// 🔒 MEMOIZAR COMPONENTE PARA EVITAR RE-RENDERS INNECESARIOS
// ⚠️ COMPARACIÓN: Solo re-renderizar cuando cambie el RIG
// 🔒 MEMOIZACIÓN ESTRICTA: Los cambios internos (forceRenderKey) se manejan con key= en el div principal
const LiveGraphsOptimized = React.memo(LiveGraphsOptimizedComponent, (prevProps, nextProps) => {
    // React.memo retorna TRUE para BLOQUEAR re-render, FALSE para PERMITIR re-render
    // Solo bloquear si el rig NO cambió
    return prevProps.rig === nextProps.rig;
});

// Exportar también RigTabs para que Menu.jsx pueda usarlo
export { RigTabs };

export default LiveGraphsOptimized;